import { StepperSelectionEvent } from '@angular/cdk/stepper';
import { AfterViewInit, Component, ElementRef, Inject, ViewChild } from '@angular/core';
import { FormControl, FormGroupDirective, NgForm, AbstractControl, ValidationErrors, UntypedFormGroup, UntypedFormControl, Validators, UntypedFormArray, FormGroup } from '@angular/forms';
import { MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { ErrorStateMatcher } from '@angular/material/core';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatStepper } from '@angular/material/stepper';
import { MatTableDataSource } from '@angular/material/table';
import { ACCEPTED_FILE_TYPES, LEAD_BASED_PAINT, APPLICATION_STATUS } from '@shared/utils/app-static-data';
import { DOCUMENT_CATEGORIES } from 'app/models/document/document-categories';
import { DocumentService } from 'app/services/document/document.service';
import { IUser } from 'app/auth/auth.models';
import { IAtp } from 'app/models/atp/atp';
import { IAtpApplication } from 'app/models/atp/atpApplication';
import { AtpService } from 'app/services/atp/atp.service';
import { SharedService } from 'app/services/core/shared.service';
import { ToastrService } from 'ngx-toastr';
import { forkJoin, of, Subject } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, map, switchMap } from 'rxjs/operators';
import { ConfirmationDialogueComponent } from '../../common/confirmation-dialogue/confirmation-dialogue.component';
import { AtpApplicationApprovalDialogComponent, AtpApplicationApprovalDialogResult } from './atp-application-approval-dialog/atp-application-approval-dialog.component';
import { CommonDataService } from 'app/services/common/common-data.service';
import { AddEditAtpComponent } from 'app/components/atp/add-edit-atp/add-edit-atp.component';
import { ILinkApprovalEmailStatus } from 'app/models/common/account-link-approval';

export class TouchedErrorStateMatcher implements ErrorStateMatcher {
  isErrorState(control: FormControl | null, form: FormGroupDirective | NgForm | null): boolean {
    const isSubmitted = form && form.submitted;
    return !!(control && control.invalid && (control.dirty || control.touched || isSubmitted));
  }
}

interface AtpApplicationTargetOption {
  accreditation: any;
  sourceApplication: IAtpApplication | null;
  label: string;
}

interface AtpApplicationTargetContext {
  atpId: number;
  accreditations: any[];
  applications: IAtpApplication[];
}

interface FacilityAddressOption {
  key: string;
  label: string;
  street: string;
  city: string;
  state: string;
  zip: string;
}

@Component({
  selector: 'app-add-atp-application',
  templateUrl: './add-atp-application.component.html',
  styleUrls: ['./add-atp-application.component.scss']
})

export class AddAtpApplicationComponent implements AfterViewInit{
  public loading: boolean = false;
  public verificationLoading: boolean = true;
  public selectedApplicationType: string = '';
  public uiData = LEAD_BASED_PAINT;
  public APPLICATION_STATUS = APPLICATION_STATUS;
  public atpApplicationForm = null;
  public atpApplication: IAtpApplication;
  public applicationUserId: string = '';
  public step: number = 0;
  public allAtpNames: IAtp[] = [];
  public filteredAtpNames: IAtp[] = [];
  public linkAtpIdDisplayControl: FormControl = new FormControl('');
  public filteredLinkUsers: IUser[] = [];
  public linkUserDisplayControl: FormControl = new FormControl('');
  public showAtpWarning: boolean = false;
  public atpWarningMessage: string = '';
  public sendingLinkApprovalEmails: boolean = false;
  public loadingLinkApprovalEmailStatus: boolean = false;
  public linkApprovalEmailStatus: ILinkApprovalEmailStatus | null = null;
  public ammendmentTargetOptions: AtpApplicationTargetOption[] = [];
  public reaccreditationTargetOptions: AtpApplicationTargetOption[] = [];
  public ammendmentTargetWarning: string = '';
  public reaccreditationTargetWarning: string = '';
  private wasSaved: boolean = false;
  private atpTargetContextCache: AtpApplicationTargetContext | null = null;
  private readonly atpLinkSearchInput$ = new Subject<string>();
  private readonly linkUserSearchInput$ = new Subject<string>();
  public exampleDocumentsByType: { [key: string]: { documentId: number, downloadFileName: string } } = {};
  private pendingDocuments: any[] = [];
  public exampleDocumentTypes = [
    { key: 'material', label: 'Material Documents' },
    { key: 'test', label: 'Test Documents' },
    { key: 'assessment', label: 'Assessment Documents' },
    { key: 'qualityControl', label: 'Quality Control Documents' },
    { key: 'certification', label: 'Certification Documents' }
  ];

  public applicationTypes = [
    { value: null, label: '--Select--' },
    { value: 'firstaccreditation', label: 'First-time accreditation in Oregon' },
    { value: 'addaccreditation', label: 'Accreditation of a new training course' },
    { value: 'reaccreditation', label: 'Reaccreditation' },
    { value: 'ammendment', label: 'Ammendment to current accreditation' }
  ];
  public ammendmentType = [
    { value: null, label: '--Select--' },
    { value: 'manager', label: 'New training manager' },
    { value: 'instructor', label: 'New instructor' },
    { value: 'location', label: 'New training location' },
    { value: 'other', label: 'Other' }
  ];

  public programTypes = [
    { value: 'rrp', label: 'Renovation, Repair and Painting (RRP)' },
    { value: 'lbpa', label: 'Lead-Based Paint Activities (LBPA)' }
  ];

  public courseTypes = [
    { value: 'initial', label: 'Initial' },
    { value: 'refresher', label: 'Refresher' }
  ];
  public courseAccreditations = [
    { value: 'accreditation', label: 'Initial Accreditation' },
    { value: 'reaccreditation', label: 'Reaccreditation' }
  ];
  public courseFormats = [
    { value: 'inperson', label: 'In Person' },
    { value: 'online', label: 'Online' }
  ];
  public courseLanguages = [
    { value: 'english', label: 'English' },
    { value: 'spanish', label: 'Spanish' }    
  ];
  public lbpaCourseDisciplines = [
    'Abatement Worker',
    'Inspector',
    'Supervisor',
    'Risk Assessor',
    'Project Designer'    
  ];
  public rrpCourseDisciplines = [
    'Renovator',
    'Dust Sampling Technician'
  ];
  private readonly lbpaHigherFeeDisciplines = ['Inspector', 'Supervisor'];
  private readonly lbpaStandardFeeDisciplines = ['Risk Assessor', 'Abatement Worker', 'Project Designer'];
  private readonly lbpaBundledReaccreditationRefresherPairs: Array<[string, string]> = [
    ['Abatement Worker', 'Supervisor'],
    ['Inspector', 'Risk Assessor']
  ];
  public courseDataSource = new MatTableDataSource<any>([]);
  public facilityDataSource = new MatTableDataSource<any>([]);
  public readonly facilityAllCoursesValue = '__ALL__';
  public facilityCourseOptionsCache: Array<{ id: string, label: string }> = [];
  public atpCourseAddressOptions: FacilityAddressOption[] = [];
  private filteredFacilityAddressOptions: { [key: string]: FacilityAddressOption[] } = {};

  private startedFromAtpContext(): boolean {
    return !!(
      this.data?.atpId ||
      this.data?.atp?.id ||
      this.data?.renewalAccreditation ||
      this.data?.ammendmentAccreditation ||
      this.data?.sourceApplicationId
    );
  }

  private hasAtpContext(): boolean {
    return this.startedFromAtpContext() || !!this.atpApplication?.atpId;
  }

  private isStandaloneNewApplicationContext(): boolean {
    return !this.data?.atpApplicationId && !this.startedFromAtpContext();
  }

  private getContextualApplicationTypeValues(): string[] {
    if (this.data?.renewalAccreditation) {
      return ['reaccreditation'];
    }

    if (this.isStandaloneNewApplicationContext()) {
      return ['firstaccreditation'];
    }

    if (this.hasAtpContext()) {
      return ['addaccreditation', 'ammendment', 'reaccreditation'];
    }

    return ['firstaccreditation', 'addaccreditation', 'ammendment'];
  }

  getAvailableApplicationTypes() {
    const currentValue = (this.atpApplicationForm as any)?.get('applicationType')?.value;
    const allowedValues = new Set(this.getContextualApplicationTypeValues());

    if (currentValue) {
      allowedValues.add(currentValue);
    }

    return this.applicationTypes.filter(option => {
      if (option.value === null) {
        return allowedValues.size > 1 || !currentValue;
      }

      return allowedValues.has(option.value);
    });
  }

  compareAccreditationOptions(left: any, right: any): boolean {
    return (left?.id || null) === (right?.id || null);
  }

  startNewApplicationFlow(): void {
    this.selectedApplicationType = 'new';
    this.linkApprovalEmailStatus = null;
    this.loadingLinkApprovalEmailStatus = false;
    this.applyContextDefaultApplicationType();
  }

  startLinkApplicationFlow(): void {
    this.selectedApplicationType = 'link';
    this.refreshLinkApprovalEmailStatus();
  }

  private applyContextDefaultApplicationType(): void {
    const form = this.atpApplicationForm as UntypedFormGroup | null;
    if (!form) {
      return;
    }

    if (this.data?.renewalAccreditation) {
      form.patchValue({ applicationType: 'reaccreditation' });
      return;
    }

    if (this.isStandaloneNewApplicationContext()) {
      form.patchValue({ applicationType: 'firstaccreditation' });
      return;
    }

    if (this.hasAtpContext() && form.get('applicationType')?.value === 'firstaccreditation') {
      form.patchValue({ applicationType: '' });
    }
  }

  isTargetSelectionLocked(applicationType: 'ammendment' | 'reaccreditation'): boolean {
    return applicationType === 'ammendment'
      ? !!this.data?.ammendmentAccreditation
      : !!this.data?.renewalAccreditation;
  }

  isProgramTypeLocked(): boolean {
    const form = this.atpApplicationForm as UntypedFormGroup | null;
    if (!form) {
      return false;
    }

    const applicationType = (form.get('applicationType')?.value || '').toString().trim().toLowerCase();
    const programType = (form.get('programType')?.value || '').toString().trim().toLowerCase();
    return applicationType === 'reaccreditation' && (programType === 'rrp' || programType === 'lbpa');
  }

  // Helper method to get course disciplines based on program type
  getCourseDisciplines(): string[] {
    const programType = this.atpApplicationForm?.get('programType')?.value;
    if (!programType) {
      // If programType is not set yet, return all disciplines (both LBPA and RRP)
      return this.getAllCourseDisciplines();
    }
    return programType === 'rrp' ? this.rrpCourseDisciplines : this.lbpaCourseDisciplines;
  }

  // Helper method to get all course disciplines (both LBPA and RRP)
  getAllCourseDisciplines(): string[] {
    return [...this.lbpaCourseDisciplines, ...this.rrpCourseDisciplines];
  }

  public displayedColumns: string[] = ['discipline', 'type', 'language', 'format', 'accreditation', 'fee', 'actions'];    

  public trainingManagerTrainingDropdownOptions = [
    { value: 'experience', label: 'Experience' },
    { value: 'training', label: 'Training' },
    { value: 'highereducation', label: 'Higher Education' }
  ];  
  public trainingManagerConstructionDropdownOptions = [
    { value: 'leadasbestosabatement', label: 'Lead or Asbestos Abatement' },
    { value: 'painting', label: 'Painting' },
    { value: 'carpentry', label: 'Carpentry' },
    { value: 'renovation', label: 'Renovation' },
    { value: 'remodeling', label: 'Remodeling' },
    { value: 'occupationalsafetyhealth', label: 'Occupational Safety and Health' },
    { value: 'industrialhygiene', label: 'Industrial Hygiene' }
  ];
  
  // Filtered options for construction dropdowns with autocomplete
  filteredTrainingManagerConstructionOptions: { [key: string]: any[] } = {};
  filteredPrimaryInstructorConstructionOptions: { [key: string]: any[] } = {};
  
  private trainingManagerIds: string[] = [];
  trainingManagerTrainingDataSources: { [key: string]: MatTableDataSource<any> } = {};
  trainingManagerConstructionDataSources: { [key: string]: MatTableDataSource<any> } = {};

  private primaryInstructorIds: string[] = [];
  primaryInstructorTrainingDataSources: { [key: string]: MatTableDataSource<any> } = {};
  primaryInstructorConstructionDataSources: { [key: string]: MatTableDataSource<any> } = {};

  primaryInstructorTrainingDropdownOptions = [
    { value: 'experience', label: 'Experience' },
    { value: 'training', label: 'Training' },
    { value: 'education', label: 'Education' }
  ];
  primaryInstructorConstructionDropdownOptions = this.trainingManagerConstructionDropdownOptions;

  public stateOptions = [
    { value: null, label: '--Select--' },
    { value: 'AL', label: 'Alabama' },
    { value: 'AK', label: 'Alaska' },
    { value: 'AZ', label: 'Arizona' },
    { value: 'AR', label: 'Arkansas' },
    { value: 'CA', label: 'California' },
    { value: 'CO', label: 'Colorado' },
    { value: 'CT', label: 'Connecticut' },
    { value: 'DE', label: 'Delaware' },
    { value: 'FL', label: 'Florida' },
    { value: 'GA', label: 'Georgia' },
    { value: 'HI', label: 'Hawaii' },
    { value: 'ID', label: 'Idaho' },
    { value: 'IL', label: 'Illinois' },
    { value: 'IN', label: 'Indiana' },
    { value: 'IA', label: 'Iowa' },
    { value: 'KS', label: 'Kansas' },
    { value: 'KY', label: 'Kentucky' },
    { value: 'LA', label: 'Louisiana' },
    { value: 'ME', label: 'Maine' },
    { value: 'MD', label: 'Maryland' },
    { value: 'MA', label: 'Massachusetts' },
    { value: 'MI', label: 'Michigan' },
    { value: 'MN', label: 'Minnesota' },
    { value: 'MS', label: 'Mississippi' },
    { value: 'MO', label: 'Missouri' },
    { value: 'MT', label: 'Montana' },
    { value: 'NE', label: 'Nebraska' },
    { value: 'NV', label: 'Nevada' },
    { value: 'NH', label: 'New Hampshire' },
    { value: 'NJ', label: 'New Jersey' },
    { value: 'NM', label: 'New Mexico' },
    { value: 'NY', label: 'New York' },
    { value: 'NC', label: 'North Carolina' },
    { value: 'ND', label: 'North Dakota' },
    { value: 'OH', label: 'Ohio' },
    { value: 'OK', label: 'Oklahoma' },
    { value: 'OR', label: 'Oregon' },
    { value: 'PA', label: 'Pennsylvania' },
    { value: 'RI', label: 'Rhode Island' },
    { value: 'SC', label: 'South Carolina' },
    { value: 'SD', label: 'South Dakota' },
    { value: 'TN', label: 'Tennessee' },
    { value: 'TX', label: 'Texas' },
    { value: 'UT', label: 'Utah' },
    { value: 'VT', label: 'Vermont' },
    { value: 'VA', label: 'Virginia' },
    { value: 'WA', label: 'Washington' },
    { value: 'WV', label: 'West Virginia' },
    { value: 'WI', label: 'Wisconsin' },
    { value: 'WY', label: 'Wyoming' }
  ];

  private guestInstructorIds: string[] = [];
  @ViewChild('atpStepper') atpStepper: MatStepper;  
  @ViewChild('deficiencyCard') deficiencyCard?: ElementRef<HTMLTextAreaElement>;
  private validationSummaryRequested = false;

  courseMaterialTypes = [
    { value: 'EPA', label: 'EPA' },
    { value: 'other', label: 'Other' }
  ];

  courseTestTypes = [
    { value: 'EPA', label: 'EPA' },
    { value: 'other', label: 'Other' }
  ];

  facilityTypes = [
    { value: 'permanent', label: 'Permanent' },
    { value: 'nonpermanent', label: 'Non-Permanent' }
  ];

  // Add to your component properties
  otherAccreditationsDataSource = new MatTableDataSource<any>([]);
  otherAccreditationsDisplayedColumns: string[] = ['jurisdiction', 'accreditationNumber', 'courseDiscipline', 'courseType', 'approvalDate', 'approvingAuthority', 'documents', 'actions'];

  // Reaccreditation tables
  reaccredInstructorDataSource = new MatTableDataSource<any>([]);
  reaccredInstructorDisplayedColumns: string[] = ['firstName', 'lastName', 'actions'];

  reaccredCourseDataSource = new MatTableDataSource<any>([]);
  reaccredCourseDisplayedColumns: string[] = ['discipline', 'date', 'courseType', 'location', 'preTrainingNotification', 'postTrainingNotification', 'actions'];

  // ATP Staff for contact person autocomplete
  atpStaffMembers: any[] = [];
  filteredStaffMembers: any[] = [];

  // True once ATP staff have been loaded as prefill context for a new/renewal/amendment application
  private atpContactContextLoaded: boolean = false;
  
  // Filtered staff for each staff section
  filteredTrainingManagers: { [key: string]: any[] } = {};
  filteredPrimaryInstructors: { [key: string]: any[] } = {};
  filteredGuestInstructors: { [key: string]: any[] } = {};

  // Error state matcher for form fields - only show errors after field is touched
  matcher = new TouchedErrorStateMatcher();

  // Custom validator for document arrays
  private requiredDocumentValidator(control: AbstractControl): ValidationErrors | null {
    const value = control.value;
    if (!value || !Array.isArray(value) || value.length === 0) {
      return { requiredDocument: true };
    }
    return null;
  }

  private toDateOnly(value: Date): Date {
    const dateOnly = new Date(value);
    dateOnly.setHours(0, 0, 0, 0);
    return dateOnly;
  }

  private hasText(value: any): boolean {
    return (value ?? '').toString().trim().length > 0;
  }

  constructor(
    public sharedService: SharedService,
    public commonService: CommonDataService,
    private readonly documentService: DocumentService,
    private dialog: MatDialog,
    private selfDialogRef: MatDialogRef<AddAtpApplicationComponent>,
    private atpService: AtpService,
    private readonly toastr: ToastrService,
    @Inject(MAT_DIALOG_DATA) public data: { 
      atpApplicationId?: number, 
      atpId?: number, 
      atp?: IAtp, 
      atpStaff?: any[],
      renewalAccreditation?: any,
      ammendmentAccreditation?: any,
      sourceApplicationId?: number,
      targetContext?: AtpApplicationTargetContext
    }
  ) {     
    this.setApplicationDataFormFields();
    this.setupApprovedLocking();

    this.atpLinkSearchInput$
      .pipe(debounceTime(300), distinctUntilChanged())
      .subscribe((searchValue) => this.filterAtpLinkOptions(searchValue));

    this.linkUserSearchInput$
      .pipe(debounceTime(300), distinctUntilChanged())
      .subscribe((searchValue) => this.searchLinkUsers(searchValue));
  }

  private setupApprovedLocking(): void {
    const statusControl = this.atpApplicationForm?.get('applicationStatus');
    statusControl?.valueChanges?.subscribe(() => {
      this.applyApprovedLockState();
    });
    this.applyApprovedLockState();
  }

  private applyApprovedLockState(): void {
    if (!this.atpApplicationForm) return;

    if (this.isApplicationApproved()) {
      this.atpApplicationForm.disable({ emitEvent: false });
      return;
    }

    this.atpApplicationForm.enable({ emitEvent: false });
  }

  ngAfterViewInit() {
    // Load all ATP names for autocomplete
    this.loadAllAtpNames();
    this.loadAtpExampleDocuments();

    if (this.data?.targetContext?.atpId) {
      this.atpTargetContextCache = this.data.targetContext;
    }
    
    // Check if we have an existing application to load
    if (this.data?.atpApplicationId) {
      this.loadAtpApplication(this.data.atpApplicationId);
    } else {
      // Initialize with default data for new application
      this.initializeDefaultData();

      const atpContextId = this.getAtpContextId();
      if (atpContextId) {
        this.preloadAtpTargetContext(atpContextId);
      }

      if (this.data?.atpId || this.data?.atp?.id) {
        this.startNewApplicationFlow();
      }

      if (this.data?.renewalAccreditation) {
        this.initializeRenewalApplication(this.data.renewalAccreditation);
        return;
      }

      if (this.data?.ammendmentAccreditation || this.data?.sourceApplicationId) {
        this.initializeAmmendmentApplication(this.data.ammendmentAccreditation || null, this.data.sourceApplicationId);
        return;
      }
      
      // If atpId is provided (from embedded context), set it in the form and load ATP data
      if (this.data?.atpId) {
        this.atpApplicationForm.patchValue({
          atpId: this.data.atpId
        });
        // Load ATP data to pre-populate form fields
        this.loadAtpDataForApplication(this.data.atpId);
      }
      
      // If atpStaff is provided, populate staff members from ATP
      if (this.data?.atpStaff && Array.isArray(this.data.atpStaff) && this.data.atpStaff.length > 0) {
        this.populateStaffFromAtp(this.data.atpStaff);
      }
    }
  }

  private initializeDefaultData() {
    this.addCourseSelection();
    this.addTrainingManager();
    this.addPrimaryInstructor();
    this.addOtherAccreditation();
    this.addReaccredInstructor();
    this.addReaccredCourse();
  }

  private loadAtpApplicationContext(atpId: number) {
    return forkJoin({
      atp: this.data?.atp ? of(this.data.atp) : this.atpService.getATPById(atpId),
      addresses: this.atpService.getATPAddresses(atpId),
      contactInfo: this.atpService.getATPContactInformation(atpId),
      staff: Array.isArray(this.data?.atpStaff) && this.data.atpStaff.length > 0
        ? of(this.data.atpStaff)
        : this.atpService.getATPPeople(atpId),
      courseAddresses: this.atpService.getATPCourseAddressesByAtpId(atpId).pipe(
        catchError(error => {
          console.error('Error loading ATP course addresses for autocomplete:', error);
          return of([]);
        })
      )
    });
  }

  private getAtpContextId(): number | null {
    return this.data?.atpId || this.data?.atp?.id || this.atpApplication?.atpId || this.atpApplicationForm?.get('atpId')?.value || null;
  }

  private preloadAtpTargetContext(atpId: number): void {
    this.loadAtpTargetContext(atpId).subscribe({
      next: () => {},
      error: () => {}
    });
  }

  private loadAtpTargetContext(atpId: number) {
    if (this.atpTargetContextCache?.atpId === atpId) {
      return of(this.atpTargetContextCache);
    }

    return forkJoin({
      accreditations: this.atpService.getATPAccreditations(atpId).pipe(
        catchError(error => {
          console.error('Error loading ATP accreditations for target selection:', error);
          return of([]);
        })
      ),
      applicationListings: this.atpService.getAllAtpAccreditationApplications(atpId).pipe(
        catchError(error => {
          console.error('Error loading ATP applications for target selection:', error);
          return of([]);
        })
      )
    }).pipe(
      switchMap(({ accreditations, applicationListings }) => {
        const applicationIds = (applicationListings || [])
          .map((application: any) => application.id)
          .filter((id: any) => !!id);

        if (applicationIds.length === 0) {
          return of({
            atpId,
            accreditations: accreditations || [],
            applications: []
          } as AtpApplicationTargetContext);
        }

        return forkJoin(
          applicationIds.map(id =>
            this.atpService.getAtpApplication(id).pipe(
              catchError(error => {
                console.error('Error loading ATP application for target selection:', error);
                return of(null);
              })
            )
          )
        ).pipe(
          map((applications: Array<IAtpApplication | null>) => ({
            atpId,
            accreditations: accreditations || [],
            applications: applications.filter((application): application is IAtpApplication => !!application)
          } as AtpApplicationTargetContext))
        );
      }),
      map(result => {
        this.atpTargetContextCache = result;
        return result;
      })
    );
  }

  private hasApplicationStaffData(application: IAtpApplication | null | undefined): boolean {
    return !!application && (
      !!application.contactPerson ||
      this.hasText(application.atpContactFirstName) ||
      this.hasText(application.atpContactLastName) ||
      this.hasText(application.atpContactTitle) ||
      (application.trainingManagers?.length || 0) > 0 ||
      (application.primaryInstructors?.length || 0) > 0 ||
      (application.guestInstructors?.length || 0) > 0
    );
  }

  private getLatestStaffDataApplication(applications: IAtpApplication[] | null | undefined): IAtpApplication | null {
    const matches = (applications || [])
      .filter(application => application.applicationStatus !== 'Cancelled')
      .filter(application => this.hasApplicationStaffData(application))
      .sort((left, right) => this.getApplicationSortTimestamp(right) - this.getApplicationSortTimestamp(left));

    return matches[0] ?? null;
  }

  private buildAtpApplicationPatch(result: any): any {
    const patchData: any = {};
    const setIfPresent = (fieldName: string, value: any) => {
      if (value !== undefined && value !== null && value !== '') {
        patchData[fieldName] = value;
      }
    };

    if (result?.atp) {
      setIfPresent('atpName', (result.atp as any).name || (result.atp as any).atpName || '');
      setIfPresent('atpOrId', (result.atp as any).atporId || (result.atp as any).atpOrId || (result.atp as any).atpNameAka || '');
    }

    const mailingAddress = result?.addresses?.find((addr: any) => addr.isCurrentMailing);
    if (mailingAddress?.address) {
      setIfPresent('atpMailingStreet', mailingAddress.address.street || '');
      setIfPresent('atpMailingCity', mailingAddress.address.city || '');
      setIfPresent('atpMailingState', mailingAddress.address.state || '');
      setIfPresent('atpMailingZip', mailingAddress.address.zip || '');
      setIfPresent('atpMailingCounty', mailingAddress.address.county || '');
    }

    const physicalAddress = result?.addresses?.find((addr: any) => addr.isCurrentPhysical);
    if (physicalAddress?.address) {
      setIfPresent('atpPhysicalStreet', physicalAddress.address.street || '');
      setIfPresent('atpPhysicalCity', physicalAddress.address.city || '');
      setIfPresent('atpPhysicalState', physicalAddress.address.state || '');
      setIfPresent('atpPhysicalZip', physicalAddress.address.zip || '');
      setIfPresent('atpPhysicalCounty', physicalAddress.address.county || '');

      if (mailingAddress?.address) {
        patchData.mailingIsPhysical =
          physicalAddress.address.street === mailingAddress.address.street &&
          physicalAddress.address.city === mailingAddress.address.city &&
          physicalAddress.address.state === mailingAddress.address.state &&
          physicalAddress.address.zip === mailingAddress.address.zip;
      }
    }

    if (result?.contactInfo?.contactInformation) {
      const contact = result.contactInfo.contactInformation;
      setIfPresent('atpPhone', contact.phone || '');
      setIfPresent('atpAlternatePhone', contact.cell || '');
      setIfPresent('atpFaxPhone', contact.fax || '');
      setIfPresent('atpEmail', contact.email || '');
      setIfPresent('atpConfirmEmail', contact.email || '');
      setIfPresent('atpWebsite', contact.website || '');
    }

    const contactPerson = this.getAtpContactPerson(result?.staff);
    if (contactPerson?.person) {
      patchData.contactPerson = contactPerson.person;
      setIfPresent('atpContactFirstName', contactPerson.person.firstName || '');
      setIfPresent('atpContactLastName', contactPerson.person.lastName || '');
      setIfPresent('atpContactTitle', contactPerson.person.title || '');
    }

    return patchData;
  }

  // The ATP's first staff member flagged IsContact is the contact for prefill purposes
  private getAtpContactPerson(staff: any[] | null | undefined): any {
    return (staff || []).find((member: any) => member?.isContact && member?.person) || null;
  }

  /**
   * Applies the ATP's IsContact staff member as the authoritative default contact. Runs after every
   * other prefill step so contact data carried over from a previous application can never override
   * it. When the ATP has no IsContact staff member the contact is left blank for the user to fill in
   * or assign. This only affects the initial prefill - the user can still clear the contact or pick
   * a different staff member afterwards.
   */
  private applyAtpContactPrefill(): void {
    const form = this.atpApplicationForm as UntypedFormGroup | null;
    if (!form || !this.atpContactContextLoaded) {
      return;
    }

    const contactPerson = this.getAtpContactPerson(this.atpStaffMembers);
    form.patchValue({
      contactPerson: contactPerson?.person || null,
      atpContactFirstName: contactPerson?.person?.firstName || '',
      atpContactLastName: contactPerson?.person?.lastName || '',
      atpContactTitle: contactPerson?.person?.title || ''
    }, { emitEvent: false });

    this.setContactFieldsDisabled(!!contactPerson?.person?.id && contactPerson.person.id !== 0);
  }

  private setContactFieldsDisabled(disabled: boolean): void {
    ['atpContactFirstName', 'atpContactLastName', 'atpContactTitle'].forEach(controlName => {
      const control = this.atpApplicationForm?.get(controlName);
      if (!control) {
        return;
      }

      if (disabled) {
        control.disable({ emitEvent: false });
      } else {
        control.enable({ emitEvent: false });
      }
    });
  }

  private applyAtpApplicationContext(result: any, populateStaffForms: boolean): void {
    if (!result) {
      this.clearAtpCourseAddressOptions();
      return;
    }

    const form = this.atpApplicationForm as UntypedFormGroup | null;
    if (!form) {
      return;
    }

    form.patchValue(this.buildAtpApplicationPatch(result));
    this.atpStaffMembers = result.staff || [];
    this.atpContactContextLoaded = Array.isArray(result.staff);
    this.setAtpCourseAddressOptions(result.courseAddresses || []);

    if (populateStaffForms && result.staff?.length > 0) {
      this.populateStaffFromAtp(result.staff);
      return;
    }

    const contactPerson = this.getAtpContactPerson(result.staff);
    if (contactPerson?.person?.id) {
      form.get('atpContactFirstName')?.disable();
      form.get('atpContactLastName')?.disable();
      form.get('atpContactTitle')?.disable();
    }
  }

  private isSourceApplicationForAccreditation(application: IAtpApplication | null | undefined, accreditationId: number): boolean {
    return !!application && (
      application.createdAtpAccreditation?.id === accreditationId ||
      application.renewalAtpAccreditation?.id === accreditationId
    );
  }

  private getApplicationSortTimestamp(application: IAtpApplication): number {
    return new Date(
      application.approvalDate ||
      application.applicationViableDate ||
      application.applicationDate ||
      0
    ).getTime();
  }

  private isApprovedApplication(application: IAtpApplication | null | undefined): boolean {
    return (application?.applicationStatus || '').toString().trim().toLowerCase() === 'approved';
  }

  private getNormalizedEndorsementKey(accreditation: any): string {
    return (accreditation?.accreditationEndorsementType || '').toString().trim().toLowerCase();
  }

  private getAccreditationExpirationTimestamp(accreditation: any): number {
    return new Date(accreditation?.expirationDate || 0).getTime();
  }

  private isAccreditationEligibleForAmmendment(accreditation: any): boolean {
    if (!accreditation?.expirationDate) {
      return false;
    }

    const expirationDate = this.toDateOnly(new Date(accreditation.expirationDate));
    const sixMonthsFromNow = this.toDateOnly(new Date());
    sixMonthsFromNow.setMonth(sixMonthsFromNow.getMonth() + 6);
    return expirationDate > sixMonthsFromNow;
  }

  private isAccreditationEligibleForReaccreditation(accreditation: any): boolean {
    if (!accreditation?.expirationDate) {
      return false;
    }

    const expirationDate = this.toDateOnly(new Date(accreditation.expirationDate));
    const sixMonthsFromNow = this.toDateOnly(new Date());
    sixMonthsFromNow.setMonth(sixMonthsFromNow.getMonth() + 6);
    return expirationDate <= sixMonthsFromNow;
  }

  private matchesSelectedProgramType(accreditation: any): boolean {
    const selectedProgramType = (this.atpApplicationForm?.get('programType')?.value || '').toString().trim().toLowerCase();
    if (!selectedProgramType) {
      return true;
    }

    const parsedProgramType = this.parseAccreditationEndorsementType(accreditation?.accreditationEndorsementType).programType;
    return parsedProgramType === selectedProgramType;
  }

  private getTargetSourceApplication(applications: IAtpApplication[], accreditationId: number, requireApproved: boolean): IAtpApplication | null {
    const matches = (applications || [])
      .filter(application => application.applicationStatus !== 'Cancelled')
      .filter(application => !requireApproved || this.isApprovedApplication(application))
      .filter(application => this.isSourceApplicationForAccreditation(application, accreditationId))
      .sort((left, right) => this.getApplicationSortTimestamp(right) - this.getApplicationSortTimestamp(left));

    return matches[0] ?? null;
  }

  private formatTargetOptionLabel(accreditation: any, sourceApplication: IAtpApplication | null, useApplicationLabel: boolean): string {
    const expirationDate = accreditation?.expirationDate
      ? new Date(accreditation.expirationDate).toLocaleDateString()
      : 'Unknown';
    const prefix = useApplicationLabel && sourceApplication?.id
      ? `Application #${sourceApplication.id}`
      : `Accreditation #${accreditation?.id || ''}`;
    return `${prefix} - ${accreditation?.accreditationEndorsementType || 'Unknown'} (expires ${expirationDate})`;
  }

  private buildTargetOptions(context: AtpApplicationTargetContext, targetType: 'ammendment' | 'reaccreditation'): AtpApplicationTargetOption[] {
    const eligibleAccreditations = (context.accreditations || [])
      .filter(accreditation => !!accreditation?.id)
      .filter(accreditation => this.matchesSelectedProgramType(accreditation))
      .filter(accreditation => targetType === 'ammendment'
        ? this.isAccreditationEligibleForAmmendment(accreditation)
        : this.isAccreditationEligibleForReaccreditation(accreditation)
      )
      .sort((left, right) => this.getAccreditationExpirationTimestamp(right) - this.getAccreditationExpirationTimestamp(left));

    const dedupedAccreditations = new Map<string, any>();
    eligibleAccreditations.forEach(accreditation => {
      const key = this.getNormalizedEndorsementKey(accreditation);
      if (!dedupedAccreditations.has(key)) {
        dedupedAccreditations.set(key, accreditation);
      }
    });

    return Array.from(dedupedAccreditations.values())
      .map(accreditation => {
        const sourceApplication = this.getTargetSourceApplication(
          context.applications,
          accreditation.id,
          targetType === 'ammendment'
        );

        return {
          accreditation,
          sourceApplication,
          label: this.formatTargetOptionLabel(accreditation, sourceApplication, targetType === 'ammendment')
        };
      })
      .sort((left, right) => left.label.localeCompare(right.label));
  }

  private ensureSelectedTargetOptionPresent(targetType: 'ammendment' | 'reaccreditation', context: AtpApplicationTargetContext): void {
    const controlName = targetType === 'ammendment' ? 'ammendmentAtpAccreditation' : 'renewalAtpAccreditation';
    const currentSelection = this.atpApplicationForm?.get(controlName)?.value;
    if (!currentSelection?.id) {
      return;
    }

    const sourceApplication = this.getTargetSourceApplication(
      context.applications,
      currentSelection.id,
      targetType === 'ammendment'
    );
    const option: AtpApplicationTargetOption = {
      accreditation: currentSelection,
      sourceApplication,
      label: this.formatTargetOptionLabel(currentSelection, sourceApplication, targetType === 'ammendment')
    };

    if (targetType === 'ammendment') {
      if (!this.ammendmentTargetOptions.some(target => target.accreditation?.id === currentSelection.id)) {
        this.ammendmentTargetOptions = [option, ...this.ammendmentTargetOptions];
      }
      return;
    }

    if (!this.reaccreditationTargetOptions.some(target => target.accreditation?.id === currentSelection.id)) {
      this.reaccreditationTargetOptions = [option, ...this.reaccreditationTargetOptions];
    }
  }

  private refreshContextualTargetOptions(): void {
    const applicationType = this.atpApplicationForm?.get('applicationType')?.value;
    const atpId = this.getAtpContextId();

    if (!atpId || (applicationType !== 'ammendment' && applicationType !== 'reaccreditation')) {
      this.ammendmentTargetOptions = [];
      this.reaccreditationTargetOptions = [];
      this.ammendmentTargetWarning = '';
      this.reaccreditationTargetWarning = '';
      return;
    }

    this.loadAtpTargetContext(atpId).subscribe({
      next: (context) => {
        if (applicationType === 'ammendment') {
          this.ammendmentTargetOptions = this.buildTargetOptions(context, 'ammendment');
          this.ensureSelectedTargetOptionPresent('ammendment', context);
          this.ammendmentTargetWarning = this.ammendmentTargetOptions.length === 0
            ? 'No valid accreditations or approved applications are currently eligible for amendment.'
            : '';
          if (this.ammendmentTargetWarning) {
            this.toastr.warning(this.ammendmentTargetWarning, 'No Valid Amendment Options');
          }
          return;
        }

        this.reaccreditationTargetOptions = this.buildTargetOptions(context, 'reaccreditation');
        this.ensureSelectedTargetOptionPresent('reaccreditation', context);
        this.reaccreditationTargetWarning = this.reaccreditationTargetOptions.length === 0
          ? 'No valid accreditations are currently eligible for reaccreditation.'
          : '';
        if (this.reaccreditationTargetWarning) {
          this.toastr.warning(this.reaccreditationTargetWarning, 'No Valid Reaccreditation Options');
        }
      },
      error: () => {
        if (applicationType === 'ammendment') {
          this.ammendmentTargetOptions = [];
          this.ammendmentTargetWarning = 'Could not load amendment options. Please try again.';
          return;
        }

        this.reaccreditationTargetOptions = [];
        this.reaccreditationTargetWarning = 'Could not load reaccreditation options. Please try again.';
      }
    });
  }

  private findSourceApplicationForRenewal(atpId: number, accreditationId: number) {
    return this.atpService.getAllAtpAccreditationApplications(atpId).pipe(
      switchMap(applications => {
        const applicationIds = (applications || [])
          .map(application => application.id)
          .filter(id => !!id);

        if (applicationIds.length === 0) {
          return of([]);
        }

        return forkJoin(
          applicationIds.map(id =>
            this.atpService.getAtpApplication(id).pipe(
              catchError(error => {
                console.error('Error loading ATP application for renewal source matching:', error);
                return of(null);
              })
            )
          )
        );
      }),
      map((applications: Array<IAtpApplication | null>) => {
        const matches = applications
          .filter((application): application is IAtpApplication => !!application)
          .filter(application => application.applicationStatus !== 'Cancelled')
          .filter(application => this.isSourceApplicationForAccreditation(application, accreditationId))
          .sort((left, right) => this.getApplicationSortTimestamp(right) - this.getApplicationSortTimestamp(left));

        return matches[0] ?? null;
      }),
      catchError(error => {
        console.error('Error finding source ATP application for renewal:', error);
        return of(null);
      })
    );
  }

  private findMatchingSourceCourse(sourceApplication: IAtpApplication | null, parsedData: {
    programType: string;
    discipline: string;
    courseType: string;
    language: string;
    format: string;
  }): any | null {
    const normalize = (value: any) => (value ?? '').toString().trim().toLowerCase();
    const sourceCourses = sourceApplication?.courses || [];

    const exactMatch = sourceCourses.find(course =>
      normalize(course.discipline) === normalize(parsedData.discipline) &&
      normalize(course.type) === normalize(parsedData.courseType) &&
      normalize(course.language) === normalize(parsedData.language) &&
      normalize(course.format) === normalize(parsedData.format)
    );

    return exactMatch || sourceCourses[0] || null;
  }

  private buildCourseFromParsedAccreditation(parsedData: {
    programType: string;
    discipline: string;
    courseType: string;
    language: string;
    format: string;
  }, sourceApplication: IAtpApplication | null): any {
    const sourceCourse = this.findMatchingSourceCourse(sourceApplication, parsedData);

    return {
      ...(sourceCourse || {}),
      discipline: parsedData.discipline || sourceCourse?.discipline || '',
      type: parsedData.courseType || sourceCourse?.type || 'initial',
      language: parsedData.language || sourceCourse?.language || 'english',
      format: parsedData.format || sourceCourse?.format || 'inperson'
    };
  }

  private findBundledReaccreditationPartnerOption(
    accreditation: any,
    parsedData: {
      programType: string;
      discipline: string;
      courseType: string;
      language: string;
      format: string;
    },
    context: AtpApplicationTargetContext | null = this.atpTargetContextCache
  ): AtpApplicationTargetOption | null {
    if (parsedData.programType !== 'lbpa' || parsedData.courseType !== 'refresher') {
      return null;
    }

    const partnerDiscipline = this.getBundledLbpaReaccreditationPartnerDiscipline(parsedData.discipline);
    if (!partnerDiscipline) {
      return null;
    }

    const targetOptions = this.reaccreditationTargetOptions.length > 0
      ? this.reaccreditationTargetOptions
      : context
        ? this.buildTargetOptions(context, 'reaccreditation')
        : [];

    return targetOptions.find(option => {
      if (!option?.accreditation?.id || option.accreditation.id === accreditation?.id) {
        return false;
      }

      const optionParsedData = this.parseAccreditationEndorsementType(option.accreditation.accreditationEndorsementType);
      return optionParsedData.programType === 'lbpa'
        && optionParsedData.courseType === 'refresher'
        && this.normalizeDiscipline(optionParsedData.discipline) === this.normalizeDiscipline(partnerDiscipline);
    }) || null;
  }

  private loadRenewalCoursesForAccreditation(
    accreditation: any,
    sourceApplication: IAtpApplication | null,
    parsedData: {
      programType: string;
      discipline: string;
      courseType: string;
      language: string;
      format: string;
    },
    context: AtpApplicationTargetContext | null = this.atpTargetContextCache
  ): void {
    const primaryCourse = this.buildCourseFromParsedAccreditation(parsedData, sourceApplication);
    const partnerOption = this.findBundledReaccreditationPartnerOption(accreditation, parsedData, context);

    if (!partnerOption) {
      this.loadCourses([primaryCourse], false);
      return;
    }

    const partnerParsedData = this.parseAccreditationEndorsementType(partnerOption.accreditation?.accreditationEndorsementType || '');
    const partnerCourse = this.buildCourseFromParsedAccreditation(partnerParsedData, partnerOption.sourceApplication);
    const selectedDiscipline = parsedData.discipline || 'the selected course';
    const partnerDiscipline = partnerParsedData.discipline || 'the paired course';
    const partnerAccreditationLabel = partnerOption.accreditation?.accreditationEndorsementType || partnerDiscipline;
    const partnerExpirationDate = partnerOption.accreditation?.expirationDate
      ? new Date(partnerOption.accreditation.expirationDate).toLocaleDateString()
      : 'Unknown';
    const dialogRef = this.dialog.open(ConfirmationDialogueComponent, {
      width: '440px',
      data: {
        title: 'Add Bundled Course?',
        message: `A refresher reaccreditation for <strong>${selectedDiscipline}</strong> can be bundled with <strong>${partnerDiscipline}</strong> for $100 total.\n\nThis will add your current <strong>${partnerAccreditationLabel}</strong> accreditation, which expires on <strong>${partnerExpirationDate}</strong>.\n\nWould you like to add ${partnerDiscipline} to this application?`
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      const form = this.atpApplicationForm as UntypedFormGroup | null;
      const selectedAccreditation = form?.get('renewalAtpAccreditation')?.value;
      if (selectedAccreditation?.id !== accreditation?.id) {
        return;
      }

      this.loadCourses(result === true ? [primaryCourse, partnerCourse] : [primaryCourse], false);
    });
  }

  private initializeRenewalApplication(accreditation: any): void {
    const atpId = this.data?.atpId || this.data?.atp?.id;
    if (!atpId) {
      this.setupRenewalApplication(accreditation);
      return;
    }

    this.loading = true;
    forkJoin({
      atpContext: this.loadAtpApplicationContext(atpId).pipe(
        catchError(error => {
          console.error('Error loading ATP context for renewal:', error);
          return of(null);
        })
      ),
      targetContext: this.loadAtpTargetContext(atpId).pipe(
        catchError(error => {
          console.error('Error loading ATP target context for renewal:', error);
          return of(null);
        })
      )
    }).subscribe({
      next: ({ atpContext, targetContext }) => {
        const sourceApplication = this.getTargetSourceApplication(targetContext?.applications || [], accreditation.id, false);
        const latestStaffDataApplication = this.getLatestStaffDataApplication(targetContext?.applications);

        if (sourceApplication) {
          this.populateFormWithData(sourceApplication, true);
        }

        if (atpContext) {
          this.applyAtpApplicationContext(atpContext, !sourceApplication && !latestStaffDataApplication);
        }

        if (latestStaffDataApplication) {
          this.populateStaffDataFromApplication(latestStaffDataApplication);
        }

        this.applyAtpContactPrefill();
        this.setupRenewalApplication(accreditation, sourceApplication, targetContext);
        this.loading = false;
      },
      error: (error) => {
        console.error('Error initializing ATP renewal application:', error);
        this.toastr.warning('Could not prefill all renewal information. Please verify the application before submitting.', 'Warning');
        this.setupRenewalApplication(accreditation);
        this.loading = false;
      }
    });
  }

  private initializeAmmendmentApplication(accreditation: any | null, sourceApplicationId?: number): void {
    const atpId = this.data?.atpId || this.data?.atp?.id || this.atpApplication?.atpId;
    const sourceApplication$ = sourceApplicationId
      ? this.atpService.getAtpApplication(sourceApplicationId).pipe(
          catchError(error => {
            console.error('Error loading source ATP application for amendment:', error);
            return of(null);
          })
        )
      : of(null);

    if (!atpId) {
      sourceApplication$.subscribe(sourceApplication => {
        const targetAccreditation = accreditation || sourceApplication?.renewalAtpAccreditation || sourceApplication?.createdAtpAccreditation || (sourceApplication as any)?.ammendmentAtpAccreditation || null;
        if (!targetAccreditation) {
          this.toastr.warning('Could not determine the accreditation to amend from the selected application.', 'Warning');
          return;
        }

        if (sourceApplication) {
          this.populateFormWithData(sourceApplication, true);
        }

        this.setupAmmendmentApplication(targetAccreditation, sourceApplication);
      });
      return;
    }

    this.loading = true;
    forkJoin({
      atpContext: this.loadAtpApplicationContext(atpId).pipe(
        catchError(error => {
          console.error('Error loading ATP context for amendment:', error);
          return of(null);
        })
      ),
      targetContext: this.loadAtpTargetContext(atpId).pipe(
        catchError(error => {
          console.error('Error loading ATP target context for amendment:', error);
          return of(null);
        })
      ),
      sourceApplication: sourceApplication$
    }).subscribe({
      next: ({ atpContext, targetContext, sourceApplication }) => {
        const targetAccreditation = accreditation || sourceApplication?.renewalAtpAccreditation || sourceApplication?.createdAtpAccreditation || (sourceApplication as any)?.ammendmentAtpAccreditation || null;
        if (!targetAccreditation) {
          this.toastr.warning('Could not determine the accreditation to amend from the selected application.', 'Warning');
          this.loading = false;
          return;
        }

        const resolvedSourceApplication = sourceApplication || this.getTargetSourceApplication(targetContext?.applications || [], targetAccreditation.id, true);
        const latestStaffDataApplication = this.getLatestStaffDataApplication(targetContext?.applications);

        if (resolvedSourceApplication) {
          this.populateFormWithData(resolvedSourceApplication, true);
        }

        if (atpContext) {
          this.applyAtpApplicationContext(atpContext, !resolvedSourceApplication && !latestStaffDataApplication);
        }

        if (latestStaffDataApplication) {
          this.populateStaffDataFromApplication(latestStaffDataApplication);
        }

        this.applyAtpContactPrefill();
        this.setupAmmendmentApplication(targetAccreditation, resolvedSourceApplication);
        this.loading = false;
      },
      error: (error) => {
        console.error('Error initializing ATP amendment application:', error);
        this.toastr.warning('Could not prefill all amendment information. Please verify the application before submitting.', 'Warning');
        this.setupAmmendmentApplication(accreditation);
        this.loading = false;
      }
    });
  }

  private loadAllAtpNames() {
    this.atpService.getAllAtpNames().subscribe({
      next: (result) => {
        this.allAtpNames = result || [];
      },
      error: (error) => {
        console.error('Error loading ATP names:', error);
        this.toastr.error('Failed to load ATP names', 'Error');
      }
    });
  }

  private loadAtpExampleDocuments(): void {
    this.atpService.getATPExampleDocuments().subscribe({
      next: (documents) => {
        this.exampleDocumentsByType = {};
        (documents || []).forEach((doc: any) => {
          if (doc?.documentType && doc?.documentId) {
            this.exampleDocumentsByType[doc.documentType] = {
              documentId: doc.documentId,
              downloadFileName: doc.downloadFileName || doc.fileName
            };
          }
        });
      },
      error: () => {
        this.exampleDocumentsByType = {};
      }
    });
  }

  hasExampleDocument(documentType: string): boolean {
    return !!this.exampleDocumentsByType[documentType];
  }

  downloadExampleDocument(documentType: string): void {
    const doc = this.exampleDocumentsByType[documentType];
    if (!doc?.documentId) {
      this.toastr.warning('No example document is currently configured for this type.', 'No Document');
      return;
    }

    this.documentService.getDocumentContent(doc.documentId).subscribe({
      next: (blob) => this.documentService.saveBlob(blob, doc.downloadFileName),
      error: () => {
        this.toastr.error('Unable to download the example document.', 'Download Error');
      }
    });
  }

  onAtpNameChange(value: string | Event) {
    // Handle both direct string values and input events
    const searchValue = typeof value === 'string' ? value : (value.target as HTMLInputElement).value;
    
    if (!searchValue || searchValue.trim() === '') {
      this.filteredAtpNames = [];
      this.showAtpWarning = false;
      this.atpWarningMessage = '';
      return;
    }

    const normalizedSearchValue = searchValue.toLowerCase().trim();
    
    // Filter ATPs that match the search value in name or nameAKA
    const matchingAtps = this.allAtpNames.filter(atp => {
      const nameMatch = atp.name?.toLowerCase().includes(normalizedSearchValue);
      const akaMatch = atp.nameAKA?.toLowerCase().includes(normalizedSearchValue);
      return nameMatch || akaMatch;
    });

    // Separate ATPs with Id != 0 (for dropdown) and Id == 0 (for warning)
    const atpsWithId = matchingAtps.filter(atp => atp.id !== 0);
    const atpsWithoutId = matchingAtps.filter(atp => atp.id === 0);

    // Set filtered ATPs for dropdown (only those with Id != 0)
    this.filteredAtpNames = atpsWithId;

    // Only show warning for exact matches with Id == 0 when NOT in link mode
    // (The warning tells users to use link mode, so it doesn't make sense in link mode)
    if (this.selectedApplicationType !== 'link') {
      const exactMatch = atpsWithoutId.find(atp => {
        const nameExactMatch = atp.name?.toLowerCase() === normalizedSearchValue;
        const akaExactMatch = atp.nameAKA?.toLowerCase() === normalizedSearchValue;
        return nameExactMatch || akaExactMatch;
      });

      if (exactMatch) {
        this.showAtpWarning = true;
        this.atpWarningMessage = 'This ATP already exists. Please go back and select "Link to existing ATP" option.';
      } else {
        this.showAtpWarning = false;
        this.atpWarningMessage = '';
      }
    } else {
      // Always hide warning in link mode
      this.showAtpWarning = false;
      this.atpWarningMessage = '';
    }
  }

  // ---------------------------------------------------------------------------
  // Reaccreditation branching
  // ---------------------------------------------------------------------------

  isReaccreditationApplication(): boolean {
    return this.atpApplicationForm?.get('applicationType')?.value === 'reaccreditation';
  }

  showReaccreditStep(): boolean {
    return this.isReaccreditationApplication();
  }

  hasReaccreditationChanges(): boolean {
    return this.isReaccreditationApplication()
      && this.atpApplicationForm?.get('reaccreditationHasChanges')?.value === true;
  }

  showReaccreditationChangeCheckboxes(): boolean {
    return this.hasReaccreditationChanges()
      && this.atpApplicationForm?.get('reaccreditationMaterialsOrLocationChanged')?.value === true;
  }

  isReaccreditationTrainingMaterialsChanged(): boolean {
    return this.showReaccreditationChangeCheckboxes()
      && this.atpApplicationForm?.get('reaccreditationTrainingMaterialsChanged')?.value === true;
  }

  isReaccreditationTrainingLocationChanged(): boolean {
    return this.showReaccreditationChangeCheckboxes()
      && this.atpApplicationForm?.get('reaccreditationTrainingLocationChanged')?.value === true;
  }

  hasNewReaccreditationStaff(): boolean {
    return this.hasReaccreditationChanges()
      && this.atpApplicationForm?.get('reaccreditationHasNewStaff')?.value === true;
  }

  /** Course Training Materials + Course Additional Documents sections. */
  showCourseMaterialsSection(): boolean {
    return !this.isReaccreditationApplication() || this.isReaccreditationTrainingMaterialsChanged();
  }

  showFacilitySection(): boolean {
    return !this.isReaccreditationApplication() || this.isReaccreditationTrainingLocationChanged();
  }

  showCourseDetailsStep(): boolean {
    return this.showCourseMaterialsSection() || this.showFacilitySection();
  }

  showStaffStep(): boolean {
    return !this.isReaccreditationApplication() || this.hasNewReaccreditationStaff();
  }

  /** Reapplies every validator whose requirement depends on the reaccreditation answers. */
  private applyReaccreditationValidators(): void {
    const isReaccreditation = this.isReaccreditationApplication();

    const changesControl = this.atpApplicationForm?.get('reaccreditationChangesSinceLastApproval');
    if (changesControl) {
      if (this.hasReaccreditationChanges()) {
        changesControl.setValidators([Validators.required]);
      } else {
        changesControl.clearValidators();
      }
      changesControl.updateValueAndValidity({ emitEvent: false });
    }

    ['reaccreditationInstructorListDocuments', 'reaccreditationCourseHistoryDocuments'].forEach(key => {
      const control = this.atpApplicationForm?.get(key);
      if (!control) {
        return;
      }
      if (isReaccreditation) {
        control.setValidators([this.requiredDocumentValidator.bind(this)]);
      } else {
        control.clearValidators();
      }
      control.updateValueAndValidity({ emitEvent: false });
    });

    this.coursesFormArray?.controls.forEach(control => {
      this.applyCourseDocumentValidators(control as UntypedFormGroup);
    });

    this.facilityLocationsFormArray?.controls.forEach(control => {
      this.applyFacilityLocationValidators(control as UntypedFormGroup);
    });

    this.updateReaccreditationChangeSelectionError();
  }

  /**
   * When the applicant says materials or location changed, at least one of the two checkboxes must be set.
   */
  private updateReaccreditationChangeSelectionError(): void {
    const control = this.atpApplicationForm?.get('reaccreditationMaterialsOrLocationChanged');
    if (!control) {
      return;
    }

    const needsSelection = this.showReaccreditationChangeCheckboxes()
      && !this.isReaccreditationTrainingMaterialsChanged()
      && !this.isReaccreditationTrainingLocationChanged();

    const currentErrors = { ...(control.errors || {}) };
    delete currentErrors.requiredSelection;

    if (needsSelection) {
      currentErrors.requiredSelection = true;
    }

    control.setErrors(Object.keys(currentErrors).length > 0 ? currentErrors : null, { emitEvent: false });
  }

  private applyFacilityLocationValidators(facilityGroup: UntypedFormGroup): void {
    const required = this.showFacilitySection();

    const validatorMap: { [key: string]: any[] } = {
      selectedCourseIds: [this.atLeastOneCourseSelectedValidator.bind(this)],
      street: [Validators.required],
      city: [Validators.required],
      state: [Validators.required],
      zip: [Validators.required],
      facilityType: [Validators.required],
      facilityDocuments: [this.requiredDocumentValidator.bind(this)]
    };

    Object.keys(validatorMap).forEach(key => {
      const control = facilityGroup.get(key);
      if (!control) {
        return;
      }
      if (required) {
        control.setValidators(validatorMap[key]);
      } else {
        control.clearValidators();
      }
      control.updateValueAndValidity({ emitEvent: false });
    });
  }

  /**
   * Existing reaccreditation applications pre-date the Yes/No question, so infer the answer from
   * whatever the applicant already typed instead of blanket-defaulting everyone to "No".
   */
  private resolveReaccreditationHasChanges(application: any): boolean {
    if (application?.reaccreditationHasChanges === true || application?.reaccreditationHasChanges === false) {
      return application.reaccreditationHasChanges;
    }

    return (application?.reaccreditationChangesSinceLastApproval || '').toString().trim().length > 0;
  }

  /**
   * Reaccreditation applications never pre-seed a Training Manager and Principal Instructor row.
   * Drops the blank rows that the default initialization added before the type was known.
   */
  private removeEmptyPrepopulatedStaffRows(): void {
    const isBlankStaffRow = (control: AbstractControl): boolean => {
      const group = control as UntypedFormGroup;
      const hasName = ['firstName', 'lastName', 'middleName', 'name', 'email', 'phone']
        .some(key => (group.get(key)?.value || '').toString().trim().length > 0);
      const hasPerson = !!group.get('person')?.value || !!group.get('personId')?.value;
      return !hasName && !hasPerson;
    };

    [this.trainingManagersFormArray, this.primaryInstructorsFormArray, this.guestInstructorsFormArray]
      .forEach(formArray => {
        if (!formArray) {
          return;
        }
        for (let index = formArray.length - 1; index >= 0; index--) {
          if (isBlankStaffRow(formArray.at(index))) {
            formArray.removeAt(index);
          }
        }
      });

    this.trainingManagerIds = this.trainingManagersFormArray.controls.map(control => control.get('id')?.value);
  }

  /** Ordered labels of the steps currently rendered for a new/amended/reaccreditation application. */
  private getNewApplicationStepKeys(): string[] {
    const keys: string[] = ['App Info', 'Courses'];

    if (this.showReaccreditStep()) {
      keys.push('ReAccredit');
    }

    if (this.showCourseDetailsStep()) {
      keys.push('Course Details');
    }

    keys.push('Contact Info');

    if (this.showStaffStep()) {
      keys.push('Staff');
    }

    keys.push('Background');
    keys.push('Overview');

    return keys;
  }

  /** Index of a step label within the currently visible stepper, or -1 when it is hidden. */
  getStepIndexByKey(key: string): number {
    return this.getNewApplicationStepKeys().indexOf(key);
  }

  /** True when the step with the supplied label is the one currently selected. */
  isStepKeyActive(key: string): boolean {
    return this.getNewApplicationStepKeys()[this.step] === key;
  }

  private getActiveStepKey(): string {
    return this.getNewApplicationStepKeys()[this.step] || '';
  }

  changeStep(step: StepperSelectionEvent): void {      
    if (this.selectedApplicationType === 'link') {
      if(this.step == 0){
        this.getAtpMatchForLinking();
      }      
    }
    else{    
      // If leaving the Course Details step, update the course data source to reflect any changes
      const courseDetailsIndex = this.getStepIndexByKey('Course Details');
      if (courseDetailsIndex >= 0 && this.step === courseDetailsIndex && step.selectedIndex !== courseDetailsIndex) {
        this.updateCourseDataSource();
      }
    }

    this.step = step.selectedIndex;     
  }
  
  getAtpMatchForLinking()
  {
    const atpNameValue = (this.atpApplicationForm.get('atpName')?.value || '').toString().trim();
    const selectedAtpId = this.atpApplicationForm.get('atpId')?.value;
    
    // Check if atpName has valid content
    if (!selectedAtpId && !atpNameValue) {
      this.verificationLoading = false;
      return;
    }
    
    this.verificationLoading = true;
    this.atpService.getAtpForLinking(atpNameValue, selectedAtpId || undefined).subscribe({
      next: (result) => {
        this.atpApplicationForm.patchValue({
          atpId: result?.id ?? null,
          atpName: result?.atpName || atpNameValue,
          atpNameAka: result?.atpNameAka || ''
        });
        this.verificationLoading = false;
      },
      error: (error) => {
        console.error('Error looking up ATP for linking:', error);
        this.atpApplicationForm.patchValue({
          atpId: null,
          atpNameAka: ''
        });
        this.verificationLoading = false;
      }
    });
  }

  goToNextStep() {    
    if (this.atpStepper) {
      // Mark fields as touched to show validation errors when navigating
      if (this.step === 0 && this.selectedApplicationType === 'new') {
        this.atpApplicationForm.get('programType')?.markAsTouched();
        this.atpApplicationForm.get('applicationType')?.markAsTouched();
        
        if (this.atpApplicationForm.get('applicationType')?.value === 'ammendment') {
          this.atpApplicationForm.get('ammendmentType')?.markAsTouched();
          this.atpApplicationForm.get('ammendmentAtpAccreditation')?.markAsTouched();
          
          if (this.atpApplicationForm.get('ammendmentType')?.value === 'other') {
            this.atpApplicationForm.get('other')?.markAsTouched();
          }
        }

        if (this.atpApplicationForm.get('applicationType')?.value === 'reaccreditation') {
          this.atpApplicationForm.get('renewalAtpAccreditation')?.markAsTouched();
        }
      }
      
      // If leaving the Course Details step, update the course data source
      if (this.getActiveStepKey() === 'Course Details') {
        this.updateCourseDataSource();
      }
      this.atpStepper.next();      
    }
  }

  goToPreviousStep() {
    if (this.step === 0) {
      if (this.selectedApplicationType === 'new' && this.hasAtpContext()) {
        return;
      }

      if (this.hasPersistedApplicationId()) {
        return;
      }

      this.selectedApplicationType = '';
      this.step = 0;
    } else {      
      // If leaving the Course Details step, update the course data source
      if (this.getActiveStepKey() === 'Course Details') {
        this.updateCourseDataSource();
      }
      this.step--;
    }
  }

  showPreviousButton(): boolean {
    if (!this.selectedApplicationType) {
      return false;
    }

    if (this.step === 0 && this.hasPersistedApplicationId()) {
      return false;
    }

    return !(this.selectedApplicationType === 'new' && this.step === 0 && this.hasAtpContext());
  }

  navigateToConfirmationStep() {
    // Calculate the last step index dynamically based on application type
    this.step = this.getLastStepIndex();
    
    // Update the stepper to reflect the new step
    // Use setTimeout to ensure the stepper is fully initialized after form loading
    setTimeout(() => {
      if (this.atpStepper) {
        // Double-check with actual stepper steps if available (most reliable)
        if (this.atpStepper.steps && this.atpStepper.steps.length > 0) {
          this.step = this.atpStepper.steps.length - 1;
        }
        this.atpStepper.selectedIndex = this.step;
      }
    }, 100);
  }

  // Helper method to get the last step index dynamically
  getLastStepIndex(): number {
    if (this.selectedApplicationType === 'link') {
      return 0;
    } else if (this.selectedApplicationType === 'new') {
      // Steps are rendered conditionally, so derive the last index from the visible step list.
      return Math.max(this.getNewApplicationStepKeys().length - 1, 0);
    }
    
    return 0; // Default fallback
  }

  isLinkApplicationApproved(): boolean {
    return this.isApplicationApproved();
  }

  private hasPersistedApplicationId(): boolean {
    const applicationId = this.atpApplicationForm?.get('id')?.value;
    return !!applicationId && applicationId !== 0;
  }

  openLinkedAtp(): void {
    const atpId = this.atpApplicationForm?.get('atpId')?.value;
    if (!atpId) {
      return;
    }

    this.atpService.getATPById(atpId).subscribe({
      next: (result) => {
        const dialogRef = this.dialog.open(AddEditAtpComponent, {
          width: '90%',
          autoFocus: false,
          data: { atp: result },
        });

        dialogRef.afterClosed().subscribe(() => {
          this.atpService.getATPById(atpId).subscribe({
            next: (updatedAtp) => {
              this.atpApplicationForm.patchValue({
                atpId: updatedAtp?.id || atpId,
                atpName: updatedAtp?.name || this.atpApplicationForm.get('atpName')?.value,
                atpOrId: updatedAtp?.atporId || this.atpApplicationForm.get('atpOrId')?.value,
              });
            },
            error: () => undefined,
          });
        });
      },
      error: (error) => {
        this.toastr.error('Error loading ATP', error);
      }
    });
  }

  isNewApplicationApproved(): boolean {
    return this.isApplicationApproved();
  }

  isApplicationApproved(): boolean {
    return this.atpApplicationForm?.get('applicationStatus')?.value === APPLICATION_STATUS.approved;
  }

  private getDefaultLinkApprovalEmailStatus(disabledReason: string): ILinkApprovalEmailStatus {
    return {
      requestType: 'ATP',
      applicationId: this.atpApplicationForm?.get('id')?.value || 0,
      hasSelectedEntity: !!this.atpApplicationForm?.get('atpId')?.value,
      hasValidContactEmails: false,
      validContactEmails: [],
      lastApprovalEmailSentAt: null,
      disabledReason,
    };
  }

  private refreshLinkApprovalEmailStatus(): void {
    if (this.selectedApplicationType !== 'link') {
      this.linkApprovalEmailStatus = null;
      this.loadingLinkApprovalEmailStatus = false;
      return;
    }

    const applicationId = this.atpApplicationForm?.get('id')?.value || 0;
    if (!applicationId) {
      this.linkApprovalEmailStatus = this.getDefaultLinkApprovalEmailStatus('Save the link request before sending approval emails.');
      return;
    }

    this.loadingLinkApprovalEmailStatus = true;
    const statusPayload = this.prepareApplicationData();
    this.atpService.getAtpLinkApprovalEmailStatus(statusPayload).subscribe(status => {
      this.loadingLinkApprovalEmailStatus = false;
      this.linkApprovalEmailStatus = status ?? this.getDefaultLinkApprovalEmailStatus('Unable to evaluate approval email status.');
    }, () => {
      this.loadingLinkApprovalEmailStatus = false;
      this.linkApprovalEmailStatus = this.getDefaultLinkApprovalEmailStatus('Unable to evaluate approval email status.');
    });
  }

  canSendLinkApprovalEmails(): boolean {
    if (this.selectedApplicationType !== 'link' || this.isApplicationApproved() || this.sendingLinkApprovalEmails || this.loadingLinkApprovalEmailStatus) {
      return false;
    }

    if (!this.hasPersistedApplicationId()) {
      return false;
    }

    if (!this.atpApplicationForm?.get('atpId')?.value) {
      return false;
    }

    const applicationUserId = (this.applicationUserId || this.atpApplication?.userID || '').trim();
    if (!applicationUserId) {
      return false;
    }

    return this.linkApprovalEmailStatus?.hasValidContactEmails === true;
  }

  getSendLinkApprovalDisabledReason(): string {
    if (this.selectedApplicationType !== 'link') {
      return '';
    }

    if (this.isApplicationApproved()) {
      return 'Approved link requests cannot send new approval emails.';
    }

    if (this.sendingLinkApprovalEmails) {
      return 'Approval emails are currently being sent.';
    }

    if (this.loadingLinkApprovalEmailStatus) {
      return 'Checking link approval email status...';
    }

    if (!this.hasPersistedApplicationId()) {
      return 'Save the link request before sending approval emails.';
    }

    if (!this.atpApplicationForm?.get('atpId')?.value) {
      return 'Select a matching ATP before sending approval emails.';
    }

    const applicationUserId = (this.applicationUserId || this.atpApplication?.userID || '').trim();
    if (!applicationUserId) {
      return 'Assign a User ID email before sending approval emails.';
    }

    return this.linkApprovalEmailStatus?.disabledReason || 'No valid contact email is available for the selected ATP.';
  }

  getLinkApprovalEmailRecipientsSummary(): string {
    if (this.selectedApplicationType !== 'link') {
      return '';
    }

    if (this.loadingLinkApprovalEmailStatus) {
      return 'Checking contact emails...';
    }

    const contactEmails = this.linkApprovalEmailStatus?.validContactEmails || [];
    if (contactEmails.length === 0) {
      return this.getSendLinkApprovalDisabledReason();
    }

    return `${contactEmails.length} valid contact email${contactEmails.length === 1 ? '' : 's'}: ${contactEmails.join(', ')}`;
  }

  sendLinkApprovalEmails(): void {
    if (!this.canSendLinkApprovalEmails()) {
      this.toastr.error(this.getSendLinkApprovalDisabledReason());
      return;
    }

    const applicationData = this.prepareApplicationData();
    this.sendingLinkApprovalEmails = true;
    this.atpService.sendAtpLinkApprovalEmails(applicationData).subscribe(result => {
      this.sendingLinkApprovalEmails = false;

      if (result?.emailsSent > 0) {
        const label = result.emailsSent === 1 ? 'email' : 'emails';
        this.toastr.success(`Approval ${label} sent to ${result.emailsSent} contact recipient(s).`);
      } else {
        this.toastr.success('Approval emails queued.');
      }

      this.refreshLinkApprovalEmailStatus();
    }, error => {
      this.sendingLinkApprovalEmails = false;
      this.toastr.error('There was an error sending approval emails for this link request: ', error);
    });
  }

  canApproveApplication(): boolean {
    // Already approved - cannot approve again
    if (this.isApplicationApproved()) {
      return false;
    }
    
    // For link applications, only need atpId to be set
    if (this.selectedApplicationType === 'link') {
      const atpId = this.atpApplicationForm?.get('atpId')?.value;
      return atpId && atpId !== 0 && !!this.applicationUserId;
    }
    
    // For new applications, form must be valid
    return this.atpApplicationForm?.valid || false;
  }

  // Get available application status options based on current status
  getApplicationStatusOptions(): string[] {
    const currentStatus = this.atpApplicationForm?.get('applicationStatus')?.value;
    
    // If already approved, include approved in the options
    if (currentStatus === APPLICATION_STATUS.approved) {
      return Object.values(APPLICATION_STATUS);
    }
    
    // Otherwise, exclude approved from options
    return Object.values(APPLICATION_STATUS).filter(status => status !== APPLICATION_STATUS.approved);
  }

  disableFormForSubmittedApplication(): void {
    if (this.selectedApplicationType === 'new' && this.isApplicationApproved()) {
      this.atpApplicationForm.disable();
    }
  }
  
  onExitClick() {
    if(this.atpApplicationForm.get("applicationStatus").value !== APPLICATION_STATUS.approved){
      const dialogRef = this.dialog.open(ConfirmationDialogueComponent, {
        width: '400px',
        data: {
          title: 'Exit without saving?',
          message: 'Any unsaved data will be lost.\n\nAre you sure you want to exit?'
        }
      });

      dialogRef.afterClosed().subscribe(result => {
        if (result === true) {
          this.selfDialogRef.close({ saved: this.wasSaved });
        }      
      });
    }else{
      this.selfDialogRef.close({ saved: false });    
    }
  }

  setBackgroundColor(selection: string, match: string): string {
    if (selection === match) {
      return this.uiData.atp.color;
    }

    return this.sharedService.darkMode ? '#5c5c5c' :'#f5f5f5';
  }

  setApplicationDataFormFields(){       
    this.atpApplicationForm = new UntypedFormGroup({
      id: new UntypedFormControl(0), // Add ID field - 0 for new applications, database ID for existing
      atpId: new UntypedFormControl(null),

      approvalDate: new UntypedFormControl(null),
      applicationDate: new UntypedFormControl(null),
      applicationViableDate: new UntypedFormControl(null),   
      applicationStatus: new UntypedFormControl("In Progress"),
      applicationFeedback: new UntypedFormControl(""),

      // New fields for contact person and accreditations
      contactPerson: new UntypedFormControl(null),
      createdAtpAccreditation: new UntypedFormControl(null),
      renewalAtpAccreditation: new UntypedFormControl(null),
      ammendmentAtpAccreditation: new UntypedFormControl(null),

      submissionType: new UntypedFormControl(""), 
      programType: new UntypedFormControl("", [Validators.required]), 
      applicationType: new UntypedFormControl("", [Validators.required]),
      ammendmentType: new UntypedFormControl(""),
      other: new UntypedFormControl(""),
            
      atpName: new UntypedFormControl("", [Validators.required]),
      atpOrId: new UntypedFormControl(""),

      mailingIsPhysical: new UntypedFormControl(false),
      atpPhysicalState: new UntypedFormControl("", [Validators.required]),
      atpPhysicalStreet: new UntypedFormControl("", [Validators.required]),
      atpPhysicalCity: new UntypedFormControl("", [Validators.required]),
      atpPhysicalZip: new UntypedFormControl("", [Validators.required]),
      atpPhysicalCounty: new UntypedFormControl(""),        
      atpMailingStreet: new UntypedFormControl(""),
      atpMailingCity: new UntypedFormControl(""),
      atpMailingState: new UntypedFormControl(""),
      atpMailingZip: new UntypedFormControl(""),
      atpMailingCounty: new UntypedFormControl(""),
      atpContactFirstName: new UntypedFormControl("", [Validators.required]),
      atpContactLastName: new UntypedFormControl("", [Validators.required]),
      atpContactTitle: new UntypedFormControl(""),
      atpPhone: new UntypedFormControl("", [Validators.required]),
      atpAlternatePhone: new UntypedFormControl(""),
      atpFaxPhone: new UntypedFormControl(""),
      atpEmail: new UntypedFormControl("", [Validators.required, Validators.email]),
      atpConfirmEmail: new UntypedFormControl("", [Validators.required, Validators.email]),    
      atpWebsite: new UntypedFormControl(""),
      
      trainingManagers: new UntypedFormArray([]),
      primaryInstructors: new UntypedFormArray([]),
      guestInstructors: new UntypedFormArray([]),

      courses: new UntypedFormArray([]),
      facilityLocations: new UntypedFormArray([]),

      otherJurisdictionAccreditation: new UntypedFormControl(false),
      otherAccreditations: new UntypedFormArray([]),

      hasViolation: new UntypedFormControl(false),
      violationDescription: new UntypedFormControl(""),
      additionalInformation: new UntypedFormControl(""),

      reaccredInstructorTable: new UntypedFormArray([]),
      reaccredCourseTable: new UntypedFormArray([]),
      reaccreditationChangesSinceLastApproval: new UntypedFormControl(""),
      reaccreditationHasChanges: new UntypedFormControl(false),
      reaccreditationMaterialsOrLocationChanged: new UntypedFormControl(false),
      reaccreditationTrainingMaterialsChanged: new UntypedFormControl(false),
      reaccreditationTrainingLocationChanged: new UntypedFormControl(false),
      reaccreditationHasNewStaff: new UntypedFormControl(false),
      reaccreditationInstructorListDocuments: new UntypedFormControl([]),
      reaccreditationCourseHistoryDocuments: new UntypedFormControl([]),

      attestation: new UntypedFormControl(false, [Validators.requiredTrue]),
      esignature: new UntypedFormControl("", [Validators.required]),      
    }, {
      validators: [
        this.commonService.emailValidator('contactEmail', 'contactConfirmEmail'),
        this.commonService.emailValidator('firmEmail', 'firmConfirmEmail'), 
      ]
    });
    
    // Add listener for otherJurisdictionAccreditation to auto-populate table when Yes is selected
    this.atpApplicationForm.get('otherJurisdictionAccreditation')?.valueChanges.subscribe(value => {
      if (value === true && this.otherAccreditationsFormArray.length === 0) {
        this.addOtherAccreditation();
      }
    });

    // Add conditional validators for applicationType and ammendmentType
    this.atpApplicationForm.get('applicationType')?.valueChanges.subscribe(value => {
      const ammendmentTypeControl = this.atpApplicationForm.get('ammendmentType');
      const ammendmentAccreditationControl = this.atpApplicationForm.get('ammendmentAtpAccreditation');
      const renewalAccreditationControl = this.atpApplicationForm.get('renewalAtpAccreditation');
      const otherControl = this.atpApplicationForm.get('other');
      
      if (value === 'ammendment') {
        ammendmentTypeControl?.setValidators([Validators.required]);
        ammendmentAccreditationControl?.setValidators([Validators.required]);
        renewalAccreditationControl?.clearValidators();
      } else {
        ammendmentTypeControl?.clearValidators();
        ammendmentAccreditationControl?.clearValidators();
        otherControl?.clearValidators();
        if (!this.data?.ammendmentAccreditation) {
          ammendmentAccreditationControl?.setValue(null, { emitEvent: false });
        }
        this.ammendmentTargetOptions = [];
        this.ammendmentTargetWarning = '';
      }

      if (value === 'reaccreditation') {
        renewalAccreditationControl?.setValidators([Validators.required]);
        ammendmentAccreditationControl?.clearValidators();
      } else {
        renewalAccreditationControl?.clearValidators();
        if (!this.data?.renewalAccreditation) {
          renewalAccreditationControl?.setValue(null, { emitEvent: false });
        }
        this.reaccreditationTargetOptions = [];
        this.reaccreditationTargetWarning = '';
      }

      ammendmentTypeControl?.updateValueAndValidity();
      ammendmentAccreditationControl?.updateValueAndValidity();
      renewalAccreditationControl?.updateValueAndValidity();
      otherControl?.updateValueAndValidity();
      this.syncCourseAccreditations();
      this.recomputeCourseFees();
      this.refreshContextualTargetOptions();
      this.applyReaccreditationValidators();

      if (value === 'reaccreditation') {
        this.removeEmptyPrepopulatedStaffRows();
      }
    });

    // Reaccreditation branching questions drive which steps/sections are visible and required.
    this.atpApplicationForm.get('reaccreditationHasChanges')?.valueChanges.subscribe(value => {
      if (value !== true) {
        this.atpApplicationForm.patchValue({
          reaccreditationMaterialsOrLocationChanged: false,
          reaccreditationTrainingMaterialsChanged: false,
          reaccreditationTrainingLocationChanged: false,
          reaccreditationHasNewStaff: false
        }, { emitEvent: false });
      }
      this.applyReaccreditationValidators();
    });

    this.atpApplicationForm.get('reaccreditationMaterialsOrLocationChanged')?.valueChanges.subscribe(value => {
      if (value !== true) {
        this.atpApplicationForm.patchValue({
          reaccreditationTrainingMaterialsChanged: false,
          reaccreditationTrainingLocationChanged: false
        }, { emitEvent: false });
      }
      this.applyReaccreditationValidators();
    });

    this.atpApplicationForm.get('reaccreditationTrainingMaterialsChanged')?.valueChanges.subscribe(() => {
      this.applyReaccreditationValidators();
    });

    this.atpApplicationForm.get('reaccreditationTrainingLocationChanged')?.valueChanges.subscribe(() => {
      this.applyReaccreditationValidators();
    });

    this.atpApplicationForm.get('reaccreditationHasNewStaff')?.valueChanges.subscribe(() => {
      this.applyReaccreditationValidators();
    });

    this.atpApplicationForm.get('programType')?.valueChanges.subscribe(() => {
      this.refreshContextualTargetOptions();
    });

    this.atpApplicationForm.get('ammendmentType')?.valueChanges.subscribe(value => {
      const otherControl = this.atpApplicationForm.get('other');
      
      if (value === 'other') {
        otherControl?.setValidators([Validators.required]);
      } else {
        otherControl?.clearValidators();
      }
      otherControl?.updateValueAndValidity();
    });

    // Add conditional validator for hasViolation and violationDescription
    this.atpApplicationForm.get('hasViolation')?.valueChanges.subscribe(value => {
      const violationDescriptionControl = this.atpApplicationForm.get('violationDescription');
      
      if (value === true) {
        violationDescriptionControl?.setValidators([Validators.required]);
      } else {
        violationDescriptionControl?.clearValidators();
      }
      violationDescriptionControl?.updateValueAndValidity();
    });

    // Add conditional validator for otherJurisdictionAccreditation and otherAccreditations
    this.atpApplicationForm.get('otherJurisdictionAccreditation')?.valueChanges.subscribe(value => {
      const otherAccreditationsArray = this.otherAccreditationsFormArray;
      
      if (value === true) {
        // Add validators to all existing rows
        otherAccreditationsArray.controls.forEach((control: any) => {
          control.get('jurisdiction')?.setValidators([Validators.required]);
          control.get('accreditationNumber')?.setValidators([Validators.required]);
          control.get('courseDiscipline')?.setValidators([Validators.required]);
          control.get('courseType')?.setValidators([Validators.required]);
          control.get('approvalDate')?.setValidators([Validators.required]);
          control.get('approvingAuthority')?.setValidators([Validators.required]);
          control.get('documents')?.setValidators([this.requiredDocumentValidator.bind(this)]);
          
          control.get('jurisdiction')?.updateValueAndValidity();
          control.get('accreditationNumber')?.updateValueAndValidity();
          control.get('courseDiscipline')?.updateValueAndValidity();
          control.get('courseType')?.updateValueAndValidity();
          control.get('approvalDate')?.updateValueAndValidity();
          control.get('approvingAuthority')?.updateValueAndValidity();
          control.get('documents')?.updateValueAndValidity();
        });
      } else {
        // Clear validators from all rows
        otherAccreditationsArray.controls.forEach((control: any) => {
          control.get('jurisdiction')?.clearValidators();
          control.get('accreditationNumber')?.clearValidators();
          control.get('courseDiscipline')?.clearValidators();
          control.get('courseType')?.clearValidators();
          control.get('approvalDate')?.clearValidators();
          control.get('approvingAuthority')?.clearValidators();
          control.get('documents')?.clearValidators();
          
          control.get('jurisdiction')?.updateValueAndValidity();
          control.get('accreditationNumber')?.updateValueAndValidity();
          control.get('courseDiscipline')?.updateValueAndValidity();
          control.get('courseType')?.updateValueAndValidity();
          control.get('approvalDate')?.updateValueAndValidity();
          control.get('approvingAuthority')?.updateValueAndValidity();
          control.get('documents')?.updateValueAndValidity();
        });
      }
    });
  }

  addCourseSelection() {
    const newGroup = this.createCourseGroup();
    this.coursesFormArray.push(newGroup);
    this.recomputeCourseFees();
    this.updateCourseDataSource();
    this.updateFacilityCourseOptionsCache();

    if (this.facilityLocationsFormArray.length === 0) {
      this.addFacilityLocation();
    }
  }

  updateCourseDataSource() {
    // Create display objects that include both the FormControl and its values for easy access in templates
    this.courseDataSource.data = this.coursesFormArray.controls.map((control, index) => ({
      ...control.value,
      formControl: control,
      index: index
    }));

    // Keep facility course labels in sync with current course edits.
    this.updateFacilityCourseOptionsCache();
  }

  removeSelection(index: number) {
    const course = this.coursesFormArray.at(index);
    const disciplineValue = course.get('discipline')?.value;
    const typeValue = course.get('type')?.value;
    
    const disciplineLabel = this.getDisciplineLabel(disciplineValue);
    const typeLabel = this.getTypeLabel(typeValue);
    
    const courseDescription = typeLabel ? `${disciplineLabel} ${typeLabel}` : disciplineLabel;
    
    const dialogRef = this.dialog.open(ConfirmationDialogueComponent, {
      width: '400px',
      data: {
        title: 'Delete?',
        message: `Are you sure you want to delete <strong>${courseDescription}</strong>?`
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result === true) {
        this.coursesFormArray.removeAt(index);
        this.updateCourseDataSource();
        this.updateFacilityCourseOptionsCache();

        if (this.coursesFormArray.length === 0) {
          while (this.facilityLocationsFormArray.length > 0) {
            this.facilityLocationsFormArray.removeAt(0);
          }
          this.updateFacilityDataSource();
        }
      }      
    });
  }

  private getDisciplineLabel(disciplineValue: string): string {
    if (!disciplineValue) return '';
    
    // Check LBPA disciplines
    if (this.lbpaCourseDisciplines.includes(disciplineValue)) {
      return disciplineValue;
    }
    
    // Check RRP disciplines
    if (this.rrpCourseDisciplines.includes(disciplineValue)) {
      return disciplineValue;
    }
    
    return disciplineValue; // fallback to value if not found
  }

  getTypeLabel(typeValue: string): string {
    if (!typeValue) return '';
    
    const typeOption = this.courseTypes.find(option => option.value === typeValue);
    return typeOption ? typeOption.label : typeValue;
  }

  updateSelection(index: number, field: string, value: any) {
    const course = this.coursesFormArray.at(index);
    course.get(field)?.setValue(value);
    
    if (field === 'discipline' || field === 'type' || field === 'accreditation') {
      this.recomputeCourseFees();
      return;
    }
    
    // Update the data source to reflect changes
    this.updateCourseDataSource();
  }

  private recomputeCourseFees(): void {
    const applicationType = (this.atpApplicationForm as any)?.get('applicationType')?.value;
    const bundledRefresherCoursesByDiscipline = new Map<string, UntypedFormGroup[]>();

    this.coursesFormArray.controls.forEach(control => {
      const course = control as UntypedFormGroup;
      const discipline = course.get('discipline')?.value;
      const type = course.get('type')?.value;

      course.get('fee')?.setValue(this.calculateFee(discipline, type, applicationType), { emitEvent: false });

      if (this.isBundledLbpaReaccreditationRefresher(discipline, type, applicationType)) {
        const normalizedDiscipline = this.normalizeDiscipline(discipline);
        const courses = bundledRefresherCoursesByDiscipline.get(normalizedDiscipline) || [];
        courses.push(course);
        bundledRefresherCoursesByDiscipline.set(normalizedDiscipline, courses);
      }
    });

    this.lbpaBundledReaccreditationRefresherPairs.forEach(([leftDiscipline, rightDiscipline]) => {
      const leftCourses = [...(bundledRefresherCoursesByDiscipline.get(this.normalizeDiscipline(leftDiscipline)) || [])];
      const rightCourses = [...(bundledRefresherCoursesByDiscipline.get(this.normalizeDiscipline(rightDiscipline)) || [])];

      while (leftCourses.length > 0 && rightCourses.length > 0) {
        const primaryCourse = leftCourses.shift();
        const bundledCourse = rightCourses.shift();

        primaryCourse?.get('fee')?.setValue(100, { emitEvent: false });
        bundledCourse?.get('fee')?.setValue(0, { emitEvent: false });
      }
    });

    this.updateCourseDataSource();
  }

  private normalizeDiscipline(value: any): string {
    return (value ?? '').toString().trim().toLowerCase();
  }

  private getDerivedCourseAccreditation(): string {
    return this.atpApplicationForm?.get('applicationType')?.value === 'reaccreditation'
      ? 'reaccreditation'
      : 'accreditation';
  }

  private syncCourseAccreditations(): void {
    const accreditation = this.getDerivedCourseAccreditation();

    this.coursesFormArray.controls.forEach(control => {
      control.get('accreditation')?.setValue(accreditation, { emitEvent: false });
    });
  }

  private calculateFee(discipline: string, type: string, applicationType: string): number {
    if (!discipline || !type) {
      return 0;
    }

    const isReaccreditationApplication = applicationType === 'reaccreditation';

    if (this.rrpCourseDisciplines.includes(discipline)) {
      if (type === 'initial') {
        return isReaccreditationApplication ? 340 : 560;
      }

      return isReaccreditationApplication ? 310 : 400;
    }

    if (this.lbpaHigherFeeDisciplines.includes(discipline)) {
      if (type === 'initial') {
        return isReaccreditationApplication ? 300 : 750;
      }

      return isReaccreditationApplication ? 100 : 500;
    }

    if (this.lbpaStandardFeeDisciplines.includes(discipline)) {
      if (type === 'initial') {
        return isReaccreditationApplication ? 300 : 500;
      }

      return isReaccreditationApplication ? 100 : 350;
    }

    return 0;
  }

  private getBundledLbpaReaccreditationRefresherPair(discipline: string): [string, string] | null {
    const normalizedDiscipline = this.normalizeDiscipline(discipline);

    return this.lbpaBundledReaccreditationRefresherPairs.find(([leftDiscipline, rightDiscipline]) =>
      this.normalizeDiscipline(leftDiscipline) === normalizedDiscipline
      || this.normalizeDiscipline(rightDiscipline) === normalizedDiscipline
    ) || null;
  }

  private getBundledLbpaReaccreditationPartnerDiscipline(discipline: string): string | null {
    const pair = this.getBundledLbpaReaccreditationRefresherPair(discipline);
    if (!pair) {
      return null;
    }

    return this.normalizeDiscipline(pair[0]) === this.normalizeDiscipline(discipline)
      ? pair[1]
      : pair[0];
  }

  private isBundledLbpaReaccreditationRefresher(discipline: string, type: string, applicationType: string): boolean {
    return applicationType === 'reaccreditation'
      && type === 'refresher'
      && !!this.getBundledLbpaReaccreditationRefresherPair(discipline);
  }

  createCourseGroup(): UntypedFormGroup {
    const id = 'course_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
    
    const group = new UntypedFormGroup({
      id: new UntypedFormControl(id),
      discipline: new UntypedFormControl(null, [Validators.required]),
      type: new UntypedFormControl('initial'),
      language: new UntypedFormControl('english'),
      format: new UntypedFormControl('inperson'),
      accreditation: new UntypedFormControl(this.getDerivedCourseAccreditation()),
      fee: new UntypedFormControl(0),
      languageDocuments: new UntypedFormControl([]),
      courseWebsite: new UntypedFormControl(''),
      websiteAccessDescription: new UntypedFormControl(''),
      courseMaterialType: new UntypedFormControl(null, [Validators.required]),
      courseMaterialTypeCustom: new UntypedFormControl(''),
      courseMaterialDocuments: new UntypedFormControl([], [this.requiredDocumentValidator.bind(this)]),
      courseTest: new UntypedFormControl(null, [Validators.required]),
      courseTestCustom: new UntypedFormControl(''),
      courseTestDocuments: new UntypedFormControl([], [this.requiredDocumentValidator.bind(this)]),
      courseAssessmentDocuments: new UntypedFormControl([], [this.requiredDocumentValidator.bind(this)]),
      courseQualityControlDocuments: new UntypedFormControl([], [this.requiredDocumentValidator.bind(this)]),
      courseCertificationDocuments: new UntypedFormControl([], [this.requiredDocumentValidator.bind(this)]),
      street: new UntypedFormControl(''),
      city: new UntypedFormControl(''),
      state: new UntypedFormControl(''),
      zip: new UntypedFormControl(''),
      facilityType: new UntypedFormControl(''),
      facilityDocuments: new UntypedFormControl([])
    });

    // Material/Test document requirements depend on selected type.
    this.applyCourseDocumentValidators(group);
    const materialTypeControl = group.get('courseMaterialType');
    if (materialTypeControl) {
      materialTypeControl.valueChanges.subscribe(() => this.applyCourseDocumentValidators(group));
    }

    const testTypeControl = group.get('courseTest');
    if (testTypeControl) {
      testTypeControl.valueChanges.subscribe(() => this.applyCourseDocumentValidators(group));
    }
    
    return group;
  }

  private applyCourseDocumentValidators(courseGroup: UntypedFormGroup): void {
    const materialsSectionVisible = this.showCourseMaterialsSection();
    const materialType = courseGroup.get('courseMaterialType')?.value;
    const materialDocsControl = courseGroup.get('courseMaterialDocuments');

    // When the Course Training Materials section is hidden nothing inside it is required.
    const typeControls = ['courseMaterialType', 'courseTest'];
    typeControls.forEach(key => {
      const control = courseGroup.get(key);
      if (!control) {
        return;
      }
      if (materialsSectionVisible) {
        control.setValidators([Validators.required]);
      } else {
        control.clearValidators();
      }
      control.updateValueAndValidity({ emitEvent: false });
    });

    ['courseAssessmentDocuments', 'courseQualityControlDocuments', 'courseCertificationDocuments'].forEach(key => {
      const control = courseGroup.get(key);
      if (!control) {
        return;
      }
      if (materialsSectionVisible) {
        control.setValidators([this.requiredDocumentValidator.bind(this)]);
      } else {
        control.clearValidators();
      }
      control.updateValueAndValidity({ emitEvent: false });
    });

    if (materialDocsControl) {
      if (!materialsSectionVisible || materialType === 'EPA') {
        materialDocsControl.clearValidators();
      } else {
        materialDocsControl.setValidators([this.requiredDocumentValidator.bind(this)]);
      }
      materialDocsControl.updateValueAndValidity({ emitEvent: false });
    }

    const testType = courseGroup.get('courseTest')?.value;
    const testDocsControl = courseGroup.get('courseTestDocuments');

    if (testDocsControl) {
      if (!materialsSectionVisible || testType === 'EPA') {
        testDocsControl.clearValidators();
      } else {
        testDocsControl.setValidators([this.requiredDocumentValidator.bind(this)]);
      }
      testDocsControl.updateValueAndValidity({ emitEvent: false });
    }
  }

  // Helper function to check if a course field should have error styling
  hasFieldError(courseIndex: number, fieldName: string): boolean {
    const course = this.coursesFormArray.at(courseIndex) as UntypedFormGroup;
    const field = course.get(fieldName);
    if (!field) return false;
    
    // Check conditional requirements
    const language = course.get('language')?.value;
    const format = course.get('format')?.value;
    
    // Language documents only required when language is not English
    if (fieldName === 'languageDocuments' && language === 'english') {
      return false;
    }
    
    // Course website and instructions only required when format is online
    if ((fieldName === 'courseWebsite' || fieldName === 'websiteAccessDescription') && format !== 'online') {
      return false;
    }
    
    // Only show error if field is invalid AND (touched OR dirty) AND empty
    const isEmpty = field.value === null || field.value === '' || (Array.isArray(field.value) && field.value.length === 0);
    return isEmpty && (field.touched || field.dirty);
  }

  get coursesFormArray(): UntypedFormArray {
    return this.atpApplicationForm.get('courses') as UntypedFormArray;
  }

  get facilityLocationsFormArray(): UntypedFormArray {
    return this.atpApplicationForm.get('facilityLocations') as UntypedFormArray;
  }

  private atLeastOneCourseSelectedValidator(control: AbstractControl): ValidationErrors | null {
    const selected = control.value as string[];
    return Array.isArray(selected) && selected.length > 0 ? null : { requiredSelection: true };
  }

  private createFacilityLocationGroup(): UntypedFormGroup {
    const id = 'facility_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
    const group = new UntypedFormGroup({
      id: new UntypedFormControl(id),
      selectedCourseIds: new UntypedFormControl([], [this.atLeastOneCourseSelectedValidator.bind(this)]),
      street: new UntypedFormControl('', [Validators.required]),
      city: new UntypedFormControl('', [Validators.required]),
      state: new UntypedFormControl('', [Validators.required]),
      zip: new UntypedFormControl('', [Validators.required]),
      facilityType: new UntypedFormControl('', [Validators.required]),
      facilityDocuments: new UntypedFormControl([], [this.requiredDocumentValidator.bind(this)])
    });

    this.applyFacilityLocationValidators(group);

    return group;
  }

  addFacilityLocation(selectAllCourses: boolean = true): void {
    const group = this.createFacilityLocationGroup();
    if (selectAllCourses) {
      group.get('selectedCourseIds')?.setValue(this.getAllCourseIds());
    }
    this.facilityLocationsFormArray.push(group);
    this.updateFacilityAddressOptionsForIndex(this.facilityLocationsFormArray.length - 1);
    this.updateFacilityDataSource();
  }

  removeFacilityLocation(index: number): void {
    const dialogRef = this.dialog.open(ConfirmationDialogueComponent, {
      width: '400px',
      data: {
        title: 'Delete?',
        message: 'Are you sure you want to delete this facility location?'
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result === true) {
        const filterKey = this.getFacilityLocationFilterKey(index);
        this.facilityLocationsFormArray.removeAt(index);
        delete this.filteredFacilityAddressOptions[filterKey];
        this.updateFacilityDataSource();
      }
    });
  }

  updateFacilityDataSource(): void {
    this.facilityDataSource.data = this.facilityLocationsFormArray.controls.map((control, index) => ({
      ...control.value,
      formControl: control,
      index
    }));
  }

  getAllCourseIds(): string[] {
    return this.coursesFormArray.controls
      .map(control => control.get('id')?.value)
      .filter(id => !!id)
      .map(id => String(id));
  }

  getFacilityCourseOptions(): Array<{ id: string, label: string }> {
    return this.facilityCourseOptionsCache;
  }

  private updateFacilityCourseOptionsCache(): void {
    this.facilityCourseOptionsCache = this.coursesFormArray.controls
      .map((control, index) => {
        const course = control.value;
        const id = control.get('id')?.value;
        if (!id) {
          return null;
        }
        return {
          id: String(id),
          label: this.getCourseDisplayInfo({ ...course, index }) || `Course ${index + 1}`
        };
      })
      .filter(option => !!option) as Array<{ id: string, label: string }>;
  }

  private clearAtpCourseAddressOptions(): void {
    this.atpCourseAddressOptions = [];
    this.filteredFacilityAddressOptions = {};
  }

  private getFacilityAddressDedupeKey(street: string, city: string, state: string, zip: string): string {
    return [street, city, state, zip]
      .map(value => value.toLowerCase())
      .join('|');
  }

  private getFacilityAddressLabel(street: string, city: string, state: string, zip: string): string {
    const locality = [city, state].filter(value => !!value).join(', ');
    const localityLine = [locality, zip].filter(value => !!value).join(' ');
    return [street, localityLine].filter(value => !!value).join(' | ');
  }

  private setAtpCourseAddressOptions(courseAddresses: any[]): void {
    const uniqueOptions = new Map<string, FacilityAddressOption>();

    (courseAddresses || []).forEach((courseAddress: any) => {
      const address = courseAddress?.address;
      if (!address) {
        return;
      }

      const street = (address.street || '').toString().trim();
      const city = (address.city || '').toString().trim();
      const state = (address.state || '').toString().trim();
      const zip = (address.zip || '').toString().trim();

      if (!street && !city && !state && !zip) {
        return;
      }

      const key = this.getFacilityAddressDedupeKey(street, city, state, zip);
      if (uniqueOptions.has(key)) {
        return;
      }

      uniqueOptions.set(key, {
        key,
        street,
        city,
        state,
        zip,
        label: this.getFacilityAddressLabel(street, city, state, zip)
      });
    });

    this.atpCourseAddressOptions = Array.from(uniqueOptions.values())
      .sort((left, right) => left.label.localeCompare(right.label));

    this.filteredFacilityAddressOptions = {};
    this.facilityLocationsFormArray.controls.forEach((_, index) => {
      this.updateFacilityAddressOptionsForIndex(index);
    });
  }

  private getFacilityLocationFilterKey(index: number): string {
    const control = this.facilityLocationsFormArray.at(index);
    return control?.get('id')?.value || `facility_${index}`;
  }

  private filterFacilityAddressOptions(searchValue: string): FacilityAddressOption[] {
    const normalizedSearchValue = (searchValue || '').toString().toLowerCase().trim();
    if (!normalizedSearchValue) {
      return [...this.atpCourseAddressOptions];
    }

    return this.atpCourseAddressOptions.filter(option => {
      const searchable = `${option.street} ${option.city} ${option.state} ${option.zip}`.toLowerCase();
      return searchable.includes(normalizedSearchValue);
    });
  }

  private updateFacilityAddressOptionsForIndex(index: number, searchValue: string = ''): void {
    const key = this.getFacilityLocationFilterKey(index);
    this.filteredFacilityAddressOptions[key] = this.filterFacilityAddressOptions(searchValue);
  }

  onFacilityStreetInput(event: any, index: number): void {
    const searchValue = event?.target?.value || '';
    this.updateFacilityAddressOptionsForIndex(index, searchValue);
  }

  onFacilityStreetFocus(event: any, index: number, trigger: MatAutocompleteTrigger): void {
    const searchValue = event?.target?.value || '';
    this.updateFacilityAddressOptionsForIndex(index, searchValue);
    this.openAutocompletePanel(trigger);
  }

  getFilteredFacilityAddressOptions(index: number): FacilityAddressOption[] {
    const key = this.getFacilityLocationFilterKey(index);
    if (!this.filteredFacilityAddressOptions[key]) {
      const control = this.facilityLocationsFormArray.at(index);
      const currentStreetValue = control?.get('street')?.value || '';
      this.updateFacilityAddressOptionsForIndex(index, currentStreetValue);
    }

    return this.filteredFacilityAddressOptions[key] || [];
  }

  onFacilityAddressOptionSelected(index: number, option: FacilityAddressOption): void {
    if (!option) {
      return;
    }

    const control = this.facilityLocationsFormArray.at(index);
    if (!control) {
      return;
    }

    control.patchValue({
      street: option.street,
      city: option.city,
      state: option.state,
      zip: option.zip
    });

    control.get('street')?.markAsTouched();
    control.get('city')?.markAsTouched();
    control.get('state')?.markAsTouched();
    control.get('zip')?.markAsTouched();
    this.updateFacilityAddressOptionsForIndex(index, option.street);
    this.updateFacilityDataSource();
  }

  getFacilityAddressOptionLabel(option: FacilityAddressOption): string {
    return option?.label || '';
  }

  onFacilityCourseSelectionChange(index: number, selectedValues: string[]): void {
    const locationControl = this.facilityLocationsFormArray.at(index);
    if (!locationControl) {
      return;
    }

    const currentSelected = (locationControl.get('selectedCourseIds')?.value || []).map((value: any) => String(value));
    const selected = Array.isArray(selectedValues) ? selectedValues.map(value => String(value)) : [];
    const selectedWithoutAll = selected.filter(value => value !== this.facilityAllCoursesValue);
    const allCourseIds = this.getAllCourseIds();
    const hadAllSelected = allCourseIds.length > 0 && allCourseIds.every(id => currentSelected.includes(id));

    // If ALL is toggled on, select all. If individual items change while ALL was previously selected,
    // preserve the explicit individual selection so ALL can turn off correctly.
    if (selected.includes(this.facilityAllCoursesValue) && !hadAllSelected) {
      locationControl.get('selectedCourseIds')?.setValue(allCourseIds);
    } else if (!selected.includes(this.facilityAllCoursesValue) && hadAllSelected && selectedWithoutAll.length === allCourseIds.length) {
      // User explicitly unticked ALL while all individual courses were selected.
      locationControl.get('selectedCourseIds')?.setValue([]);
    } else {
      locationControl.get('selectedCourseIds')?.setValue(selectedWithoutAll);
    }
    locationControl.get('selectedCourseIds')?.markAsTouched();
    this.updateFacilityDataSource();
  }

  isAllCoursesSelected(index: number): boolean {
    const locationControl = this.facilityLocationsFormArray.at(index);
    const selected = (locationControl?.get('selectedCourseIds')?.value || []).map((value: any) => String(value));
    const allIds = this.getAllCourseIds();
    return allIds.length > 0 && allIds.every(id => selected.includes(id));
  }

  getFacilitySelectValues(index: number): string[] {
    const locationControl = this.facilityLocationsFormArray.at(index);
    const selected = (locationControl?.get('selectedCourseIds')?.value || []).map((value: any) => String(value));
    if (this.isAllCoursesSelected(index)) {
      return [this.facilityAllCoursesValue, ...selected];
    }
    return selected;
  }

  getFacilitySelectedCourseDisplay(index: number): string {
    const locationControl = this.facilityLocationsFormArray.at(index);
    const selected = (locationControl?.get('selectedCourseIds')?.value || []).map((value: any) => String(value));
    const selectedLabels = this.getSelectedFacilityCourseLabels(selected);

    if (selectedLabels.length === 0) {
      return '';
    }

    if (this.isAllCoursesSelected(index)) {
      return 'ALL';
    }

    if (selectedLabels.length === 1) {
      return selectedLabels[0];
    }

    return `${selectedLabels.length} courses selected`;
  }

  getFacilitySelectedCourseTooltip(index: number): string {
    const locationControl = this.facilityLocationsFormArray.at(index);
    const selected = (locationControl?.get('selectedCourseIds')?.value || []).map((value: any) => String(value));
    const selectedLabels = this.getSelectedFacilityCourseLabels(selected);
    return selectedLabels.join('\n');
  }

  private getSelectedFacilityCourseLabels(selectedIds: string[]): string[] {
    const selectedIdSet = new Set(selectedIds.map(id => String(id)));
    return this.getFacilityCourseOptions()
      .filter(option => selectedIdSet.has(String(option.id)))
      .map(option => option.label);
  }

  private syncFacilitySelectionsWithCourses(): void {
    const validIds = this.getAllCourseIds();

    this.facilityLocationsFormArray.controls.forEach((control: AbstractControl) => {
      const selectedIds = (control.get('selectedCourseIds')?.value || []).map((id: any) => String(id));
      const filteredSelectedIds = selectedIds.filter((id: string) => validIds.includes(id));
      control.get('selectedCourseIds')?.setValue(filteredSelectedIds, { emitEvent: false });
      control.get('selectedCourseIds')?.updateValueAndValidity({ emitEvent: false });
    });

    this.updateFacilityDataSource();
  }

  hasFacilityFieldError(facilityIndex: number, fieldName: string): boolean {
    const location = this.facilityLocationsFormArray.at(facilityIndex) as UntypedFormGroup;
    const field = location.get(fieldName);
    if (!field) {
      return false;
    }

    const isEmpty = field.value === null || field.value === '' || (Array.isArray(field.value) && field.value.length === 0);
    return isEmpty && (field.touched || field.dirty);
  }

  get trainingManagersFormArray(): UntypedFormArray {
    return this.atpApplicationForm.get('trainingManagers') as UntypedFormArray;
  }

  createTrainingManagerGroup(): UntypedFormGroup {
    const id = 'tm_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
    
    const group = new UntypedFormGroup({
      id: new UntypedFormControl(id), // Add ID to the form group
      firstName: new UntypedFormControl(''),
      lastName: new UntypedFormControl(''),
      middleName: new UntypedFormControl(''),
      title: new UntypedFormControl(''),
      phone: new UntypedFormControl(''),
      email: new UntypedFormControl(''),
      person: new UntypedFormControl(null), // IPerson reference
      trainingData: new UntypedFormArray([this.createTrainingManagerTrainingRow()]),
      constructionData: new UntypedFormArray([this.createTrainingManagerConstructionRow()])
    });
    
    return group;
  }

  createTrainingManagerTrainingRow(): UntypedFormGroup {
    return new UntypedFormGroup({
      type: new UntypedFormControl(null, [Validators.required]),
      description: new UntypedFormControl('', [Validators.required]),
      documents: new UntypedFormControl([], [this.requiredDocumentValidator.bind(this)])
    });
  }

  createTrainingManagerConstructionRow(): UntypedFormGroup {
    return new UntypedFormGroup({
      type: new UntypedFormControl(null, [Validators.required]),
      description: new UntypedFormControl('', [Validators.required]),
      documents: new UntypedFormControl([], [this.requiredDocumentValidator.bind(this)])
    });
  }

  addTrainingManager() {
    const newGroup = this.createTrainingManagerGroup();
    const id = newGroup.get('id')?.value;
    this.trainingManagerIds.push(id);
    this.trainingManagersFormArray.push(newGroup);
  }

  removeTrainingManager(index: number) {
    const trainingManager = this.trainingManagersFormArray.at(index);
    const firstName = trainingManager.get('firstName')?.value || '';
    const lastName = trainingManager.get('lastName')?.value || '';
    const displayName = firstName || lastName ? `${firstName} ${lastName}`.trim() : `Training Manager ${index + 1}`;
    
    const dialogRef = this.dialog.open(ConfirmationDialogueComponent, {
      width: '400px',
      data: {
        title: 'Delete?',
        message: `Are you sure you want to delete <strong>${displayName}</strong>?`
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result === true) {
        const id = trainingManager.get('id')?.value;
        
        // Remove from arrays and clean up data sources
        this.trainingManagersFormArray.removeAt(index);
        this.trainingManagerIds.splice(index, 1);
        
        // Clean up data sources using the ID
        if (this.trainingManagerTrainingDataSources[id]) {
          delete this.trainingManagerTrainingDataSources[id];
        }
        if (this.trainingManagerConstructionDataSources[id]) {
          delete this.trainingManagerConstructionDataSources[id];
        }
      }
    });
  }

  getTrainingManagerTrainingArray(staffIndex: number): UntypedFormArray {
    return this.trainingManagersFormArray.at(staffIndex).get('trainingData') as UntypedFormArray;
  }

  getTrainingManagerConstructionArray(staffIndex: number): UntypedFormArray {
    return this.trainingManagersFormArray.at(staffIndex).get('constructionData') as UntypedFormArray;
  }

  getTrainingDataSource(staffIndex: number): MatTableDataSource<any> {
    const trainingManager = this.trainingManagersFormArray.at(staffIndex);
    const id = trainingManager.get('id')?.value;
    
    if (!this.trainingManagerTrainingDataSources[id]) {
      this.trainingManagerTrainingDataSources[id] = new MatTableDataSource(
        this.getTrainingManagerTrainingArray(staffIndex).controls
      );
    }
    return this.trainingManagerTrainingDataSources[id];
  }

  getConstructionDataSource(staffIndex: number): MatTableDataSource<any> {
    const trainingManager = this.trainingManagersFormArray.at(staffIndex);
    const id = trainingManager.get('id')?.value;
    
    if (!this.trainingManagerConstructionDataSources[id]) {
      this.trainingManagerConstructionDataSources[id] = new MatTableDataSource(
        this.getTrainingManagerConstructionArray(staffIndex).controls
      );
    }
    return this.trainingManagerConstructionDataSources[id];
  }

  addTrainingRow(staffIndex: number) {
    this.getTrainingManagerTrainingArray(staffIndex).push(this.createTrainingManagerTrainingRow());
    const trainingManager = this.trainingManagersFormArray.at(staffIndex);
    const id = trainingManager.get('id')?.value;
    this.trainingManagerTrainingDataSources[id].data = this.getTrainingManagerTrainingArray(staffIndex).controls;
  }

  addConstructionRow(staffIndex: number) {
    this.getTrainingManagerConstructionArray(staffIndex).push(this.createTrainingManagerConstructionRow());
    const trainingManager = this.trainingManagersFormArray.at(staffIndex);
    const id = trainingManager.get('id')?.value;
    this.trainingManagerConstructionDataSources[id].data = this.getTrainingManagerConstructionArray(staffIndex).controls;
  }

  removeTrainingRow(staffIndex: number, rowIndex: number) {
    const trainingArray = this.getTrainingManagerTrainingArray(staffIndex);
    if (trainingArray.length <= 1) {
      return;
    }
    const trainingRow = trainingArray.at(rowIndex);
    const type = trainingRow.get('type')?.value;
    const description = trainingRow.get('description')?.value;
    
    // Build description for confirmation using labels
    const typeLabel = type ? this.getTrainingTypeLabel(type) : 'this training';
    const rowDescription = description ? `"${description}" (${typeLabel})` : typeLabel;
    
    const dialogRef = this.dialog.open(ConfirmationDialogueComponent, {
      width: '400px',
      data: {
        title: 'Delete?',
        message: `Are you sure you want to delete <strong>${rowDescription}</strong> training row?`
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result === true) {
        trainingArray.removeAt(rowIndex);
        const trainingManager = this.trainingManagersFormArray.at(staffIndex);
        const id = trainingManager.get('id')?.value;
        this.trainingManagerTrainingDataSources[id].data = trainingArray.controls;
      }
    });
  }

  removeConstructionRow(staffIndex: number, rowIndex: number) {
    const constructionArray = this.getTrainingManagerConstructionArray(staffIndex);
    if (constructionArray.length <= 1) {
      return;
    }
    const constructionRow = constructionArray.at(rowIndex);
    const type = constructionRow.get('type')?.value;
    const description = constructionRow.get('description')?.value;
    
    // Build description for confirmation using labels
    const typeLabel = type ? this.getConstructionTypeLabel(type) : 'this construction';
    const rowDescription = description ? `"${description}" (${typeLabel})` : typeLabel;
    
    const dialogRef = this.dialog.open(ConfirmationDialogueComponent, {
      width: '400px',
      data: {
        title: 'Delete?',
        message: `Are you sure you want to delete <strong>${rowDescription}</strong> construction row?`
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result === true) {
        constructionArray.removeAt(rowIndex);
        const trainingManager = this.trainingManagersFormArray.at(staffIndex);
        const id = trainingManager.get('id')?.value;
        this.trainingManagerConstructionDataSources[id].data = constructionArray.controls;
      }
    });
  }

  async onTrainingManagerDocumentSelected(event: any, staffIndex: number, tableType: 'training' | 'construction', rowIndex: number) {
    const file = event.target.files[0];
    if (file) {
      const array = tableType === 'training' 
        ? this.getTrainingManagerTrainingArray(staffIndex) 
        : this.getTrainingManagerConstructionArray(staffIndex);
      
      const currentDocuments = array.at(rowIndex).get('documents')?.value || [];
      const category = tableType === 'training' ? DOCUMENT_CATEGORIES.trainingDataDocument : DOCUMENT_CATEGORIES.constructionDataDocument;
      await this.handleDocumentSelection(file, currentDocuments, category);
      array.at(rowIndex).get('documents')?.setValue([...currentDocuments]);
    }
    // Clear the input
    event.target.value = '';
  }

  getTrainingManagerDisplayName(index: number): string {
    const trainingManager = this.trainingManagersFormArray.at(index);
    const firstName = trainingManager.get('firstName')?.value || '';
    const lastName = trainingManager.get('lastName')?.value || '';
    
    if (firstName || lastName) {
      return `${firstName} ${lastName}`.trim();
    }
    return '';
  }

  getTotalDocuments(index: number): number {
    const trainingManager = this.trainingManagersFormArray.at(index);
    let count = 0;
    
    // Count training documents
    const trainingArray = trainingManager.get('trainingData') as UntypedFormArray;
    if (trainingArray) {
      trainingArray.controls.forEach(control => {
        const documents = control.get('documents')?.value || [];
        count += documents.length;
      });
    }
    
    // Count construction documents
    const constructionArray = trainingManager.get('constructionData') as UntypedFormArray;
    if (constructionArray) {
      constructionArray.controls.forEach(control => {
        const documents = control.get('documents')?.value || [];
        count += documents.length;
      });
    }
    
    return count;
  }

  removeTrainingManagerDocument(trainingManagerIndex: number, type: 'training' | 'construction', rowIndex: number, docIndex: number) {
    const array = type === 'training' 
      ? this.getTrainingManagerTrainingArray(trainingManagerIndex) 
      : this.getTrainingManagerConstructionArray(trainingManagerIndex);
    
    const documents = array.at(rowIndex).get('documents')?.value || [];
    const fileName = documents[docIndex]?.name || 'this document';
    
    const dialogRef = this.dialog.open(ConfirmationDialogueComponent, {
      width: '400px',
      data: {
        title: 'Remove Document?',
        message: `Are you sure you want to remove <strong>"${fileName}"</strong>?`
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result === true) {
        documents.splice(docIndex, 1);
        array.at(rowIndex).get('documents')?.setValue([...documents]);
      }
    });
  }

  downloadTrainingManagerDocument(trainingManagerIndex: number, type: 'training' | 'construction', rowIndex: number, docIndex: number) {
    const array = type === 'training' 
      ? this.getTrainingManagerTrainingArray(trainingManagerIndex) 
      : this.getTrainingManagerConstructionArray(trainingManagerIndex);
    
    const documents = array.at(rowIndex).get('documents')?.value || [];
    const documentFile = documents[docIndex];

    this.downloadDocument(documentFile);
  }

  private getTrainingTypeLabel(typeValue: string): string {
    const option = this.trainingManagerTrainingDropdownOptions.find(opt => opt.value === typeValue);
    return option ? option.label : typeValue;
  }

  private getConstructionTypeLabel(typeValue: string): string {
    const option = this.trainingManagerConstructionDropdownOptions.find(opt => opt.value === typeValue);
    return option ? option.label : typeValue;
  }

  displayConstructionTypeLabel = (value: string | null): string => {
    if (!value) {
      return '';
    }
    return this.getConstructionTypeLabel(value);
  };

  // Filter construction dropdown options for Training Manager
  filterTrainingManagerConstructionOptions(staffIndex: number, rowIndex: number, searchValue: string): void {
    const key = `tm_${staffIndex}_${rowIndex}`;
    
    if (!searchValue || searchValue.trim() === '') {
      this.filteredTrainingManagerConstructionOptions[key] = [...this.trainingManagerConstructionDropdownOptions];
      return;
    }
    
    const search = searchValue.toLowerCase();
    this.filteredTrainingManagerConstructionOptions[key] = this.trainingManagerConstructionDropdownOptions.filter(option =>
      option.label.toLowerCase().includes(search)
    );
  }

  // Get filtered construction options for Training Manager
  getFilteredTrainingManagerConstructionOptions(staffIndex: number, rowIndex: number): any[] {
    const key = `tm_${staffIndex}_${rowIndex}`;
    
    if (!this.filteredTrainingManagerConstructionOptions[key]) {
      this.filteredTrainingManagerConstructionOptions[key] = [...this.trainingManagerConstructionDropdownOptions];
    }
    
    return this.filteredTrainingManagerConstructionOptions[key];
  }

  // Update construction type for Training Manager
  updateTrainingManagerConstructionType(staffIndex: number, rowIndex: number, value: string): void {
    const constructionArray = this.getTrainingManagerConstructionArray(staffIndex);
    const row = constructionArray.at(rowIndex);
    row.get('type')?.setValue(value);
  }

  // Get display value for Training Manager construction type (for showing selected value or custom text)
  getTrainingManagerConstructionDisplayValue(staffIndex: number, rowIndex: number): string {
    const constructionArray = this.getTrainingManagerConstructionArray(staffIndex);
    const row = constructionArray.at(rowIndex);
    const type = row.get('type')?.value;
    
    if (!type) return '';
    
    // Check if it's a predefined option
    const option = this.trainingManagerConstructionDropdownOptions.find(opt => opt.value === type);
    if (option) {
      return option.label;
    }
    
    // If not found in options, it's custom text - return as is
    return type;
  }

  // Filter construction dropdown options for Primary Instructor
  filterPrimaryInstructorConstructionOptions(staffIndex: number, rowIndex: number, searchValue: string): void {
    const key = `pi_${staffIndex}_${rowIndex}`;
    
    if (!searchValue || searchValue.trim() === '') {
      this.filteredPrimaryInstructorConstructionOptions[key] = [...this.primaryInstructorConstructionDropdownOptions];
      return;
    }
    
    const search = searchValue.toLowerCase();
    this.filteredPrimaryInstructorConstructionOptions[key] = this.primaryInstructorConstructionDropdownOptions.filter(option =>
      option.label.toLowerCase().includes(search)
    );
  }

  // Get filtered construction options for Primary Instructor
  getFilteredPrimaryInstructorConstructionOptions(staffIndex: number, rowIndex: number): any[] {
    const key = `pi_${staffIndex}_${rowIndex}`;
    
    if (!this.filteredPrimaryInstructorConstructionOptions[key]) {
      this.filteredPrimaryInstructorConstructionOptions[key] = [...this.primaryInstructorConstructionDropdownOptions];
    }
    
    return this.filteredPrimaryInstructorConstructionOptions[key];
  }

  // Update construction type for Primary Instructor
  updatePrimaryInstructorConstructionType(staffIndex: number, rowIndex: number, value: string): void {
    const constructionArray = this.getPrimaryInstructorConstructionArray(staffIndex);
    const row = constructionArray.at(rowIndex);
    row.get('type')?.setValue(value);
  }

  // Get display value for Primary Instructor construction type (for showing selected value or custom text)
  getPrimaryInstructorConstructionDisplayValue(staffIndex: number, rowIndex: number): string {
    const constructionArray = this.getPrimaryInstructorConstructionArray(staffIndex);
    const row = constructionArray.at(rowIndex);
    const type = row.get('type')?.value;
    
    if (!type) return '';
    
    // Check if it's a predefined option
    const option = this.primaryInstructorConstructionDropdownOptions.find(opt => opt.value === type);
    if (option) {
      return option.label;
    }
    
    // If not found in options, it's custom text - return as is
    return type;
  }

  // Add this getter (same pattern as Training Managers)
  get primaryInstructorsFormArray(): UntypedFormArray {
    return this.atpApplicationForm.get('primaryInstructors') as UntypedFormArray;
  }

  // Replace all the Primary Instructor methods with these (exact copies of Training Manager methods):

  createPrimaryInstructorGroup(): UntypedFormGroup {
    const id = 'pi_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
    
    const group = new UntypedFormGroup({
      id: new UntypedFormControl(id), // Add ID to the form group
      firstName: new UntypedFormControl(''),
      lastName: new UntypedFormControl(''),
      middleName: new UntypedFormControl(''),
      title: new UntypedFormControl(''),
      phone: new UntypedFormControl(''),
      email: new UntypedFormControl(''),
      person: new UntypedFormControl(null), // IPerson reference
      // Principal Instructor Information fields - all required
      trainingDiscipline: new UntypedFormControl(null, [Validators.required]),
      nameOfTrainer: new UntypedFormControl('', [Validators.required]),
      nameOfTrainingProgram: new UntypedFormControl('', [Validators.required]),
      trainingStreet: new UntypedFormControl('', [Validators.required]),
      trainingCity: new UntypedFormControl('', [Validators.required]),
      trainingState: new UntypedFormControl(null, [Validators.required]),
      trainingZip: new UntypedFormControl('', [Validators.required]),
      trainingProgramPhone: new UntypedFormControl('', [Validators.required]),
      trainingCompleteDate: new UntypedFormControl(null, [Validators.required]),
      trainingCertificateIdentificationNumber: new UntypedFormControl('', [Validators.required]),
      principalInstructorDocuments: new UntypedFormControl([], [this.requiredDocumentValidator.bind(this)]),
      trainingData: new UntypedFormArray([this.createPrimaryInstructorTrainingRow()]),
      constructionData: new UntypedFormArray([this.createPrimaryInstructorConstructionRow()])
    });
    
    return group;
  }

  createPrimaryInstructorTrainingRow(): UntypedFormGroup {
    return new UntypedFormGroup({
      type: new UntypedFormControl(null, [Validators.required]),
      description: new UntypedFormControl('', [Validators.required]),
      documents: new UntypedFormControl([], [this.requiredDocumentValidator.bind(this)])
    });
  }

  createPrimaryInstructorConstructionRow(): UntypedFormGroup {
    return new UntypedFormGroup({
      type: new UntypedFormControl(null, [Validators.required]),
      description: new UntypedFormControl('', [Validators.required]),
      documents: new UntypedFormControl([], [this.requiredDocumentValidator.bind(this)])
    });
  }

  addPrimaryInstructor() {
    const newGroup = this.createPrimaryInstructorGroup();
    const id = newGroup.get('id')?.value;
    this.primaryInstructorIds.push(id);
    this.primaryInstructorsFormArray.push(newGroup);
  }

  removePrimaryInstructor(index: number) {
    const primaryInstructor = this.primaryInstructorsFormArray.at(index);
    const firstName = primaryInstructor.get('firstName')?.value || '';
    const lastName = primaryInstructor.get('lastName')?.value || '';
    const displayName = firstName || lastName ? `${firstName} ${lastName}`.trim() : `Primary Instructor ${index + 1}`;
    
    const dialogRef = this.dialog.open(ConfirmationDialogueComponent, {
      width: '400px',
      data: {
        title: 'Delete?',
        message: `Are you sure you want to delete <strong>${displayName}</strong>?`
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result === true) {
        const id = primaryInstructor.get('id')?.value;
        
        // Remove from arrays and clean up data sources
        this.primaryInstructorsFormArray.removeAt(index);
        this.primaryInstructorIds.splice(index, 1);
        
        // Clean up data sources using the ID
        if (this.primaryInstructorTrainingDataSources[id]) {
          delete this.primaryInstructorTrainingDataSources[id];
        }
        if (this.primaryInstructorConstructionDataSources[id]) {
          delete this.primaryInstructorConstructionDataSources[id];
        }
      }
    });
  }

  getPrimaryInstructorTrainingArray(staffIndex: number): UntypedFormArray {
    return this.primaryInstructorsFormArray.at(staffIndex).get('trainingData') as UntypedFormArray;
  }

  getPrimaryInstructorConstructionArray(staffIndex: number): UntypedFormArray {
    return this.primaryInstructorsFormArray.at(staffIndex).get('constructionData') as UntypedFormArray;
  }

  getPrimaryInstructorTrainingDataSource(staffIndex: number): MatTableDataSource<any> {
    const primaryInstructor = this.primaryInstructorsFormArray.at(staffIndex);
    const id = primaryInstructor.get('id')?.value;
    
    if (!this.primaryInstructorTrainingDataSources[id]) {
      this.primaryInstructorTrainingDataSources[id] = new MatTableDataSource(
        this.getPrimaryInstructorTrainingArray(staffIndex).controls
      );
    }
    return this.primaryInstructorTrainingDataSources[id];
  }

  getPrimaryInstructorConstructionDataSource(staffIndex: number): MatTableDataSource<any> {
    const primaryInstructor = this.primaryInstructorsFormArray.at(staffIndex);
    const id = primaryInstructor.get('id')?.value;
    
    if (!this.primaryInstructorConstructionDataSources[id]) {
      this.primaryInstructorConstructionDataSources[id] = new MatTableDataSource(
        this.getPrimaryInstructorConstructionArray(staffIndex).controls
      );
    }
    return this.primaryInstructorConstructionDataSources[id];
  }

  addPrimaryInstructorTrainingRow(staffIndex: number) {
    this.getPrimaryInstructorTrainingArray(staffIndex).push(this.createPrimaryInstructorTrainingRow());
    const primaryInstructor = this.primaryInstructorsFormArray.at(staffIndex);
    const id = primaryInstructor.get('id')?.value;
    this.primaryInstructorTrainingDataSources[id].data = this.getPrimaryInstructorTrainingArray(staffIndex).controls;
  }

  addPrimaryInstructorConstructionRow(staffIndex: number) {
    this.getPrimaryInstructorConstructionArray(staffIndex).push(this.createPrimaryInstructorConstructionRow());
    const primaryInstructor = this.primaryInstructorsFormArray.at(staffIndex);
    const id = primaryInstructor.get('id')?.value;
    this.primaryInstructorConstructionDataSources[id].data = this.getPrimaryInstructorConstructionArray(staffIndex).controls;
  }

  removePrimaryInstructorTrainingRow(staffIndex: number, rowIndex: number) {
    const trainingArray = this.getPrimaryInstructorTrainingArray(staffIndex);
    if (trainingArray.length <= 1) {
      return;
    }
    const trainingRow = trainingArray.at(rowIndex);
    const type = trainingRow.get('type')?.value;
    const description = trainingRow.get('description')?.value;
    
    // Build description for confirmation using labels
    const typeLabel = type ? this.getPrimaryInstructorTrainingTypeLabel(type) : 'this training';
    const rowDescription = description ? `"${description}" (${typeLabel})` : typeLabel;
    
    const dialogRef = this.dialog.open(ConfirmationDialogueComponent, {
      width: '400px',
      data: {
        title: 'Delete?',
        message: `Are you sure you want to delete <strong>${rowDescription}</strong> training row?`
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result === true) {
        trainingArray.removeAt(rowIndex);
        const primaryInstructor = this.primaryInstructorsFormArray.at(staffIndex);
        const id = primaryInstructor.get('id')?.value;
        this.primaryInstructorTrainingDataSources[id].data = trainingArray.controls;
      }
    });
  }

  removePrimaryInstructorConstructionRow(staffIndex: number, rowIndex: number) {
    const constructionArray = this.getPrimaryInstructorConstructionArray(staffIndex);
    if (constructionArray.length <= 1) {
      return;
    }
    const constructionRow = constructionArray.at(rowIndex);
    const type = constructionRow.get('type')?.value;
    const description = constructionRow.get('description')?.value;
    
    // Build description for confirmation using labels
    const typeLabel = type ? this.getPrimaryInstructorConstructionTypeLabel(type) : 'this construction';
    const rowDescription = description ? `"${description}" (${typeLabel})` : typeLabel;
    
    const dialogRef = this.dialog.open(ConfirmationDialogueComponent, {
      width: '400px',
      data: {
        title: 'Delete?',
        message: `Are you sure you want to delete <strong>${rowDescription}</strong> construction row?`
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result === true) {
        constructionArray.removeAt(rowIndex);
        const primaryInstructor = this.primaryInstructorsFormArray.at(staffIndex);
        const id = primaryInstructor.get('id')?.value;
        this.primaryInstructorConstructionDataSources[id].data = constructionArray.controls;
      }
    });
  }

  async onPrimaryInstructorDocumentSelected(event: any, staffIndex: number, tableType: 'training' | 'construction', rowIndex: number) {
    const file = event.target.files[0];
    if (file) {
      const array = tableType === 'training' 
        ? this.getPrimaryInstructorTrainingArray(staffIndex) 
        : this.getPrimaryInstructorConstructionArray(staffIndex);
      
      const currentDocuments = array.at(rowIndex).get('documents')?.value || [];
      const category = tableType === 'training' ? DOCUMENT_CATEGORIES.trainingDataDocument : DOCUMENT_CATEGORIES.constructionDataDocument;
      
      await this.handleDocumentSelection(file, currentDocuments, category, (updatedDocuments) => {
        array.at(rowIndex).get('documents')?.setValue([...updatedDocuments]);
      });
    }
    // Clear the input
    event.target.value = '';
  }

  getPrimaryInstructorDisplayName(index: number): string {
    const primaryInstructor = this.primaryInstructorsFormArray.at(index);
    const firstName = primaryInstructor.get('firstName')?.value || '';
    const lastName = primaryInstructor.get('lastName')?.value || '';
    
    if (firstName || lastName) {
      return `${firstName} ${lastName}`.trim();
    }
    return '';
  }

  getTotalPrimaryInstructorDocuments(index: number): number {
    const primaryInstructor = this.primaryInstructorsFormArray.at(index);
    let count = 0;
    
    // Count principal instructor documents
    const principalDocuments = primaryInstructor.get('principalInstructorDocuments')?.value || [];
    count += principalDocuments.length;
    
    // Count training documents
    const trainingArray = primaryInstructor.get('trainingData') as UntypedFormArray;
    if (trainingArray) {
      trainingArray.controls.forEach(control => {
        const documents = control.get('documents')?.value || [];
        count += documents.length;
      });
    }
    
    // Count construction documents
    const constructionArray = primaryInstructor.get('constructionData') as UntypedFormArray;
    if (constructionArray) {
      constructionArray.controls.forEach(control => {
        const documents = control.get('documents')?.value || [];
        count += documents.length;
      });
    }
    
    return count;
  }

  removePrimaryInstructorDocument(primaryInstructorIndex: number, type: 'training' | 'construction', rowIndex: number, docIndex: number) {
    const array = type === 'training' 
      ? this.getPrimaryInstructorTrainingArray(primaryInstructorIndex) 
      : this.getPrimaryInstructorConstructionArray(primaryInstructorIndex);
    
    const documents = array.at(rowIndex).get('documents')?.value || [];
    const fileName = documents[docIndex]?.name || 'this document';
    
    const dialogRef = this.dialog.open(ConfirmationDialogueComponent, {
      width: '400px',
      data: {
        title: 'Remove Document?',
        message: `Are you sure you want to remove <strong>"${fileName}"</strong>?`
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result === true) {
        documents.splice(docIndex, 1);
        array.at(rowIndex).get('documents')?.setValue([...documents]);
      }
    });
  }

  downloadPrimaryInstructorDocument(primaryInstructorIndex: number, type: 'training' | 'construction', rowIndex: number, docIndex: number) {
    const array = type === 'training' 
      ? this.getPrimaryInstructorTrainingArray(primaryInstructorIndex) 
      : this.getPrimaryInstructorConstructionArray(primaryInstructorIndex);
    
    const documents = array.at(rowIndex).get('documents')?.value || [];
    const documentFile = documents[docIndex];

    this.downloadDocument(documentFile);
  }

  private getPrimaryInstructorTrainingTypeLabel(typeValue: string): string {
    const option = this.primaryInstructorTrainingDropdownOptions.find(opt => opt.value === typeValue);
    return option ? option.label : typeValue;
  }

  private getPrimaryInstructorConstructionTypeLabel(typeValue: string): string {
    const option = this.primaryInstructorConstructionDropdownOptions.find(opt => opt.value === typeValue);
    return option ? option.label : typeValue;
  }

  // Helper method to get Primary Instructor FormGroup
  getPrimaryInstructorFormGroup(index: number): UntypedFormGroup {
    return this.primaryInstructorsFormArray.at(index) as UntypedFormGroup;
  }

  // Principal Instructor document methods
  async onPrincipalInstructorDocumentSelected(event: any, primaryInstructorIndex: number) {
    const files = event.target.files;
    if (files && files.length > 0) {
      const primaryInstructorGroup = this.primaryInstructorsFormArray.at(primaryInstructorIndex) as UntypedFormGroup;
      const currentDocuments = primaryInstructorGroup.get('principalInstructorDocuments')?.value || [];
      
      // Add all selected files to the documents array
      for (let i = 0; i < files.length; i++) {
        await this.handleDocumentSelection(files[i], currentDocuments, DOCUMENT_CATEGORIES.primaryInstructorDocument, (updatedDocuments) => {
          primaryInstructorGroup.get('principalInstructorDocuments')?.setValue([...updatedDocuments]);
        });
      }
      
      // Clear the file input
      event.target.value = '';
    }
  }

  downloadPrincipalInstructorDocument(primaryInstructorIndex: number, docIndex: number) {
    const primaryInstructorGroup = this.primaryInstructorsFormArray.at(primaryInstructorIndex) as UntypedFormGroup;
    const documents = primaryInstructorGroup.get('principalInstructorDocuments')?.value || [];
    const documentFile = documents[docIndex];

    this.downloadDocument(documentFile);
  }

  removePrincipalInstructorDocument(primaryInstructorIndex: number, docIndex: number) {
    const primaryInstructorGroup = this.primaryInstructorsFormArray.at(primaryInstructorIndex) as UntypedFormGroup;
    const currentDocuments = primaryInstructorGroup.get('principalInstructorDocuments')?.value || [];
    const fileName = currentDocuments[docIndex]?.name || 'this document';
    
    const dialogRef = this.dialog.open(ConfirmationDialogueComponent, {
      width: '400px',
      data: {
        title: 'Remove Document?',
        message: `Are you sure you want to remove <strong>"${fileName}"</strong>?`
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result === true) {
        // Remove the document at the specified index
        currentDocuments.splice(docIndex, 1);
        primaryInstructorGroup.get('principalInstructorDocuments')?.setValue([...currentDocuments]);
      }
    });
  }

  get guestInstructorsFormArray(): UntypedFormArray {
    return this.atpApplicationForm.get('guestInstructors') as UntypedFormArray;
  }

  createGuestInstructorGroup(): UntypedFormGroup {
    const id = 'gi_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
    
    const group = new UntypedFormGroup({
      id: new UntypedFormControl(id),
      name: new UntypedFormControl(''),
      topics: new UntypedFormControl(''),
      handsOn: new UntypedFormControl(false),
      person: new UntypedFormControl(null) // IPerson reference
    });
    
    return group;
  }

  addGuestInstructor() {
    const newGroup = this.createGuestInstructorGroup();
    const id = newGroup.get('id')?.value;
    this.guestInstructorIds.push(id);
    this.guestInstructorsFormArray.push(newGroup);
  }

  removeGuestInstructor(index: number) {
    const guestInstructor = this.guestInstructorsFormArray.at(index);
    const name = guestInstructor.get('name')?.value || '';
    const displayName = name.trim() || `Guest Instructor ${index + 1}`;
    
    const dialogRef = this.dialog.open(ConfirmationDialogueComponent, {
      width: '400px',
      data: {
        title: 'Delete?',
        message: `Are you sure you want to delete <strong>${displayName}</strong>?`
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result === true) {
        const id = guestInstructor.get('id')?.value;
        
        // Remove from arrays
        this.guestInstructorsFormArray.removeAt(index);
        this.guestInstructorIds.splice(index, 1);
      }
    });
  }

  getGuestInstructorDisplayName(index: number): string {
    const guestInstructor = this.guestInstructorsFormArray.at(index);
    const name = guestInstructor.get('name')?.value || '';
    return name.trim();
  }  
  
  async onLanguageDocumentSelected(event: any, filteredIndex: number) {
    const file = event.target.files[0];
    if (file) {
      const filteredCourses = this.getFilteredCourseDataSource();
      const course = filteredCourses[filteredIndex];
      
      // Use the index from the course object
      const originalIndex = course.index;
      
      if (originalIndex >= 0) {
        const courseControl = this.coursesFormArray.at(originalIndex);
        const currentDocuments = courseControl.get('languageDocuments')?.value || [];
        
        await this.handleDocumentSelection(file, currentDocuments, DOCUMENT_CATEGORIES.courseLanguageDocument, (updatedDocuments) => {
          const languageDocsField = courseControl.get('languageDocuments');
          languageDocsField?.setValue([...updatedDocuments]);
          languageDocsField?.markAsTouched();
          this.updateCourseDataSource();
        });
      }
      
      // Clear the file input
      event.target.value = '';
    }
  }

  removeLanguageDocument(filteredIndex: number, docIndex: number) {
    const filteredCourses = this.getFilteredCourseDataSource();
    const course = filteredCourses[filteredIndex];
    
    // Use the index from the course object
    const originalIndex = course.index;
    
    if (originalIndex >= 0) {
      const courseControl = this.coursesFormArray.at(originalIndex);
      const documents = courseControl.get('languageDocuments')?.value || [];
      const fileName = documents[docIndex]?.name || 'this document';
      
      const dialogRef = this.dialog.open(ConfirmationDialogueComponent, {
        width: '400px',
        data: {
          title: 'Remove Document?',
          message: `Are you sure you want to remove <strong>"${fileName}"</strong>?`
        }
      });

      dialogRef.afterClosed().subscribe(result => {
        if (result === true) {
          documents.splice(docIndex, 1);
          const languageDocsField = courseControl.get('languageDocuments');
          languageDocsField?.setValue([...documents]);
          languageDocsField?.markAsTouched();
          this.updateCourseDataSource();
        }
      });
    }
  }

  updateCourseWebsite(filteredIndex: number, value: string) {
    const filteredCourses = this.getFilteredCourseDataSource();
    const course = filteredCourses[filteredIndex];
    
    // Use the index from the course object
    const originalIndex = course.index;
    
    if (originalIndex >= 0) {
      const courseControl = this.coursesFormArray.at(originalIndex);
      const websiteField = courseControl.get('courseWebsite');
      websiteField?.setValue(value);
      websiteField?.markAsTouched();
      this.updateCourseDataSource();
    }
  }

  updateWebsiteAccessDescription(filteredIndex: number, value: string) {
    const filteredCourses = this.getFilteredCourseDataSource();
    const course = filteredCourses[filteredIndex];
    
    // Use the index from the course object
    const originalIndex = course.index;
    
    if (originalIndex >= 0) {
      const courseControl = this.coursesFormArray.at(originalIndex);
      const instructionsField = courseControl.get('websiteAccessDescription');
      instructionsField?.setValue(value);
      instructionsField?.markAsTouched();
      this.updateCourseDataSource();
    }
  }

  getCourseDisplayInfo(course: any): string {
    const parts = [];
    if (course.discipline) parts.push(course.discipline);
    if (course.type) {
      const typeOption = this.courseTypes.find(t => t.value === course.type);
      parts.push(typeOption ? typeOption.label : course.type);
    }
    if (course.language) {
      const langOption = this.courseLanguages.find(l => l.value === course.language);
      parts.push(langOption ? langOption.label : course.language);
    }
    if (course.format) {
      const formatOption = this.courseFormats.find(f => f.value === course.format);
      parts.push(formatOption ? formatOption.label : course.format);
    }
    return parts.join(' - ') || 'Course Info';
  }

  getFilteredCourseDataSource(): any[] {
    return this.courseDataSource.data.filter(course => {
      const isOnline = course.format === 'online';
      const isNonEnglish = course.language && course.language !== 'english';
            
      return isOnline || isNonEnglish;
    });
  }

  // Add helper methods to check conditions
  shouldShowLanguageDocuments(course: any): boolean {    
    const isEnglish = course.language === 'english';        
    return !isEnglish;
  }

  shouldShowWebsiteFields(course: any): boolean {
    const isOnline = course.format === 'online';    
    return isOnline;
  }

  async onCourseMaterialDocumentSelected(event: any, courseIndex: number) {
    const file = event.target.files[0];
    if (file) {
      const courseControl = this.coursesFormArray.at(courseIndex);
      const currentDocuments = courseControl.get('courseMaterialDocuments')?.value || [];
      
      await this.handleDocumentSelection(file, currentDocuments, DOCUMENT_CATEGORIES.courseMaterialDocument, (updatedDocuments) => {
        courseControl.get('courseMaterialDocuments')?.setValue([...updatedDocuments]);
        this.updateCourseDataSource();
      });
      
      event.target.value = '';
    }
  }

  async onCourseTestDocumentSelected(event: any, courseIndex: number) {
    const file = event.target.files[0];
    if (file) {
      const courseControl = this.coursesFormArray.at(courseIndex);
      const currentDocuments = courseControl.get('courseTestDocuments')?.value || [];
      
      await this.handleDocumentSelection(file, currentDocuments, DOCUMENT_CATEGORIES.courseTestDocument, (updatedDocuments) => {
        courseControl.get('courseTestDocuments')?.setValue([...updatedDocuments]);
        this.updateCourseDataSource();
      });
      
      event.target.value = '';
    }
  }

  async onCourseAssessmentDocumentSelected(event: any, courseIndex: number) {
    const file = event.target.files[0];
    if (file) {
      const courseControl = this.coursesFormArray.at(courseIndex);
      const currentDocuments = courseControl.get('courseAssessmentDocuments')?.value || [];
      
      await this.handleDocumentSelection(file, currentDocuments, DOCUMENT_CATEGORIES.courseAssessmentDocument, (updatedDocuments) => {
        courseControl.get('courseAssessmentDocuments')?.setValue([...updatedDocuments]);
        this.updateCourseDataSource();
      });
      
      event.target.value = '';
    }
  }

  async onCourseQualityControlDocumentSelected(event: any, courseIndex: number) {
    const file = event.target.files[0];
    if (file) {
      const courseControl = this.coursesFormArray.at(courseIndex);
      const currentDocuments = courseControl.get('courseQualityControlDocuments')?.value || [];
      
      await this.handleDocumentSelection(file, currentDocuments, DOCUMENT_CATEGORIES.courseQualityControlDocument, (updatedDocuments) => {
        courseControl.get('courseQualityControlDocuments')?.setValue([...updatedDocuments]);
        this.updateCourseDataSource();
      });
      
      event.target.value = '';
    }
  }

  async onCourseCertificationDocumentSelected(event: any, courseIndex: number) {
    const file = event.target.files[0];
    if (file) {
      const courseControl = this.coursesFormArray.at(courseIndex);
      const currentDocuments = courseControl.get('courseCertificationDocuments')?.value || [];
      
      await this.handleDocumentSelection(file, currentDocuments, DOCUMENT_CATEGORIES.courseCertificationDocument, (updatedDocuments) => {
        courseControl.get('courseCertificationDocuments')?.setValue([...updatedDocuments]);
        this.updateCourseDataSource();
      });
      
      event.target.value = '';
    }
  }

  // Remove document methods
  removeCourseDocument(courseIndex: number, docIndex: number, documentType: string) {
    const courseControl = this.coursesFormArray.at(courseIndex);
    let documentArray: File[] = [];
    let fieldName = '';

    switch (documentType) {
      case 'material':
        documentArray = courseControl.get('courseMaterialDocuments')?.value || [];
        fieldName = 'courseMaterialDocuments';
        break;
      case 'test':
        documentArray = courseControl.get('courseTestDocuments')?.value || [];
        fieldName = 'courseTestDocuments';
        break;
      case 'assessment':
        documentArray = courseControl.get('courseAssessmentDocuments')?.value || [];
        fieldName = 'courseAssessmentDocuments';
        break;
      case 'qualityControl':
        documentArray = courseControl.get('courseQualityControlDocuments')?.value || [];
        fieldName = 'courseQualityControlDocuments';
        break;
      case 'certification':
        documentArray = courseControl.get('courseCertificationDocuments')?.value || [];
        fieldName = 'courseCertificationDocuments';
        break;
    }

    const fileName = documentArray[docIndex]?.name || 'this document';
    
    const dialogRef = this.dialog.open(ConfirmationDialogueComponent, {
      width: '400px',
      data: {
        title: 'Remove Document?',
        message: `Are you sure you want to remove <strong>"${fileName}"</strong>?`
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result === true) {
        documentArray.splice(docIndex, 1);
        courseControl.get(fieldName)?.setValue([...documentArray]);
        this.updateCourseDataSource();
      }
    });
  }

  // Update dropdown selection methods
  updateCourseMaterialType(courseIndex: number, value: string) {
    const courseControl = this.coursesFormArray.at(courseIndex);
    
    // If 'other' is selected, switch to text input mode
    if (value === 'other') {
      // Set a temporary marker to trigger text input display
      courseControl.get('courseMaterialType')?.setValue('other');
      courseControl.get('courseMaterialTypeCustom')?.setValue('');
    } else {
      // For 'EPA' or any other direct value, set it directly
      courseControl.get('courseMaterialType')?.setValue(value);
      courseControl.get('courseMaterialTypeCustom')?.setValue('');
    }
    
    this.updateCourseDataSource();
  }

  updateCourseMaterialTypeCustom(courseIndex: number, value: string) {
    const courseControl = this.coursesFormArray.at(courseIndex);
    // Store the custom text in both fields for proper saving and display
    courseControl.get('courseMaterialType')?.setValue(value || 'other');
    courseControl.get('courseMaterialTypeCustom')?.setValue(value);
    // Don't call updateCourseDataSource() here - it causes input to lose focus
  }

  clearCourseMaterialTypeCustom(courseIndex: number) {
    const courseControl = this.coursesFormArray.at(courseIndex);
    courseControl.get('courseMaterialType')?.setValue(null);
    courseControl.get('courseMaterialTypeCustom')?.setValue('');
    this.updateCourseDataSource();
  }

  // Helper method to determine if custom text input should be shown
  shouldShowCustomTextInput(courseElement: any): boolean {
    const materialType = courseElement.courseMaterialType;
    
    // Show text input if:
    // 1. 'other' is selected (regardless of courseMaterialTypeCustom value)
    // 2. Or if a custom value is loaded from DB (not EPA, not null/empty, not 'other')
    if (materialType === 'other') {
      return true;
    }
    
    if (materialType && materialType !== 'EPA' && materialType !== '' && materialType !== null) {
      return true;
    }
    
    return false;
  }

  // Helper method to check if any course has EPA material type selected
  hasEPAMaterial(): boolean {
    return this.courseDataSource.data.some(course => {
      const materialType = course.courseMaterialType;
      return materialType === 'EPA';
    });
  }

  // Helper method to check if any course has Other material type selected (custom text)
  hasOtherMaterial(): boolean {
    return this.courseDataSource.data.some(course => {
      const materialType = course.courseMaterialType;
      // Other includes 'other' selection or any custom text (not EPA, not null/empty)
      return materialType === 'other' || 
             (materialType && materialType !== 'EPA' && materialType !== '' && materialType !== null);
    });
  }

  // Helper method to check if any course has EPA test type selected
  hasEPATest(): boolean {
    return this.courseDataSource.data.some(course => {
      const testType = course.courseTest;
      return testType === 'EPA';
    });
  }

  // Helper method to check if any course has Other test type selected (custom text)
  hasOtherTest(): boolean {
    return this.courseDataSource.data.some(course => {
      const testType = course.courseTest;
      // Other includes 'other' selection or any custom text (not EPA, not null/empty)
      return testType === 'other' || 
             (testType && testType !== 'EPA' && testType !== '' && testType !== null);
    });
  }

  // Helper method to check if any course has non-English language
  hasNonEnglishCourse(): boolean {
    return this.courseDataSource.data.some(course => {
      return course.language && course.language !== 'english';
    });
  }

  // Helper method to check if any course is online format
  hasOnlineCourse(): boolean {
    return this.courseDataSource.data.some(course => {
      return course.format === 'online';
    });
  }

  updateCourseTest(courseIndex: number, value: string) {
    const courseControl = this.coursesFormArray.at(courseIndex);
    
    // If 'other' is selected, switch to text input mode
    if (value === 'other') {
      // Set a temporary marker to trigger text input display
      courseControl.get('courseTest')?.setValue('other');
      courseControl.get('courseTestCustom')?.setValue('');
    } else {
      // For 'EPA' or any other direct value, set it directly
      courseControl.get('courseTest')?.setValue(value);
      courseControl.get('courseTestCustom')?.setValue('');
    }
    
    this.updateCourseDataSource();
  }

  updateCourseTestCustom(courseIndex: number, value: string) {
    const courseControl = this.coursesFormArray.at(courseIndex);
    // Store the custom text in both fields for proper saving and display
    courseControl.get('courseTest')?.setValue(value || 'other');
    courseControl.get('courseTestCustom')?.setValue(value);
    // Don't call updateCourseDataSource() here - it causes input to lose focus
  }

  clearCourseTestCustom(courseIndex: number) {
    const courseControl = this.coursesFormArray.at(courseIndex);
    courseControl.get('courseTest')?.setValue(null);
    courseControl.get('courseTestCustom')?.setValue('');
    this.updateCourseDataSource();
  }

  // Helper method to determine if custom text input should be shown for course test
  shouldShowCustomTestInput(courseElement: any): boolean {
    const testType = courseElement.courseTest;
    
    // Show text input if:
    // 1. 'other' is selected (regardless of courseTestCustom value)
    // 2. Or if a custom value is loaded from DB (not EPA, not null/empty, not 'other')
    if (testType === 'other') {
      return true;
    }
    
    if (testType && testType !== 'EPA' && testType !== '' && testType !== null) {
      return true;
    }
    
    return false;
  }

  // Helper method to check if contact person is linked
  isContactPersonLinked(): boolean {
    const contactPerson = this.atpApplicationForm.get('contactPerson')?.value;
    return contactPerson && contactPerson.id && contactPerson.id !== 0;
  }

  // Remove linked contact person
  removeLinkedContactPerson(): void {
    this.atpApplicationForm.patchValue({
      contactPerson: null,
      atpContactFirstName: '',
      atpContactLastName: '',
      atpContactTitle: ''
    });
    
    // Enable the fields
    this.atpApplicationForm.get('atpContactFirstName')?.enable();
    this.atpApplicationForm.get('atpContactLastName')?.enable();
    this.atpApplicationForm.get('atpContactTitle')?.enable();
    
    // Clear filtered list
    this.filteredStaffMembers = [];
  }

  private openAutocompletePanel(trigger: MatAutocompleteTrigger | null | undefined): void {
    if (!trigger) {
      return;
    }

    setTimeout(() => {
      if (!trigger.panelOpen && trigger.autocomplete?.options?.length) {
        trigger.openPanel();
      }
    });
  }

  private filterAtpStaffMembers(searchValue: string): any[] {
    const normalizedSearchValue = (searchValue || '').toString().toLowerCase().trim();

    return (this.atpStaffMembers || []).filter(staff => {
      if (!staff?.person) {
        return false;
      }

      if (!normalizedSearchValue) {
        return true;
      }

      const firstName = (staff.person.firstName || '').toString().toLowerCase();
      const lastName = (staff.person.lastName || '').toString().toLowerCase();
      const fullName = `${firstName} ${lastName}`.trim();

      return firstName.includes(normalizedSearchValue)
        || lastName.includes(normalizedSearchValue)
        || fullName.includes(normalizedSearchValue);
    });
  }

  // Handle contact name input for autocomplete
  onContactNameInput(event: any): void {
    const searchValue = event?.target?.value || '';
    this.filteredStaffMembers = this.filterAtpStaffMembers(searchValue);
  }

  onContactNameFocus(event: any, trigger: MatAutocompleteTrigger): void {
    const searchValue = event?.target?.value || '';
    this.filteredStaffMembers = this.filterAtpStaffMembers(searchValue);
    this.openAutocompletePanel(trigger);
  }

  // Select staff member from autocomplete
  selectStaffMember(staff: any): void {
    if (staff && staff.person) {
      this.atpApplicationForm.patchValue({
        contactPerson: staff.person,
        atpContactFirstName: staff.person.firstName || '',
        atpContactLastName: staff.person.lastName || '',
        atpContactTitle: staff.person.title || ''
      });
      
      // Disable fields since person is linked
      if (staff.person.id && staff.person.id !== 0) {
        this.atpApplicationForm.get('atpContactFirstName')?.disable();
        this.atpApplicationForm.get('atpContactLastName')?.disable();
        this.atpApplicationForm.get('atpContactTitle')?.disable();
      }
      
      // Clear filtered list
      this.filteredStaffMembers = [];
    }
  }

  // Get display name for autocomplete
  getStaffDisplayName(staff: any): string {
    if (!staff || !staff.person) return '';
    const firstName = staff.person.firstName || '';
    const lastName = staff.person.lastName || '';
    const title = staff.person.title ? ` (${staff.person.title})` : '';
    return `${firstName} ${lastName}${title}`;
  }

  // Helper method to check if ATP is linked
  isAtpLinked(): boolean {
    const atpId = this.atpApplicationForm.get('atpId')?.value;
    return atpId && atpId !== 0 && atpId !== null;
  }

  // Remove linked ATP
  removeLinkedAtp(): void {
    this.atpApplicationForm.patchValue({
      atpId: null,
      atpName: '',
      atpOrId: '',
      atpPhysicalStreet: '',
      atpPhysicalCity: '',
      atpPhysicalState: '',
      atpPhysicalZip: '',
      atpPhysicalCounty: '',
      atpMailingStreet: '',
      atpMailingCity: '',
      atpMailingState: '',
      atpMailingZip: '',
      atpMailingCounty: '',
      mailingIsPhysical: false,
      atpContactFirstName: '',
      atpContactLastName: '',
      atpContactTitle: '',
      atpPhone: '',
      atpAlternatePhone: '',
      atpFaxPhone: '',
      atpEmail: '',
      atpConfirmEmail: '',
      atpWebsite: '',
      contactPerson: null
    });
    
    // Enable contact fields
    this.atpApplicationForm.get('atpContactFirstName')?.enable();
    this.atpApplicationForm.get('atpContactLastName')?.enable();
    this.atpApplicationForm.get('atpContactTitle')?.enable();
    
    // Clear staff members and filtered lists
    this.atpStaffMembers = [];
    this.atpContactContextLoaded = false;
    this.filteredStaffMembers = [];
    this.filteredAtpNames = [];
    this.clearAtpCourseAddressOptions();

    this.realignApplicationTypeForAtpSelectionChange();
    this.refreshLinkApprovalEmailStatus();
  }

  private realignApplicationTypeForAtpSelectionChange(): void {
    if (this.data?.atpApplicationId) {
      return;
    }

    this.applyContextDefaultApplicationType();
    this.refreshContextualTargetOptions();
  }

  // Handle ATP name input for autocomplete
  onAtpNameInput(event: any): void {
    const searchValue = event.target.value || '';

    if (this.selectedApplicationType === 'link') {
      this.linkAtpIdDisplayControl.setValue('', { emitEvent: false });
      this.atpApplicationForm.patchValue({
        atpId: null,
        atpNameAka: ''
      }, { emitEvent: false });
      this.refreshLinkApprovalEmailStatus();
    }

    this.atpLinkSearchInput$.next(searchValue);
  }

  onAtpIdInput(event: any): void {
    const searchValue = event.target.value || '';

    if (this.selectedApplicationType === 'link') {
      this.atpApplicationForm.patchValue({
        atpId: null,
        atpName: '',
        atpNameAka: ''
      }, { emitEvent: false });
      this.refreshLinkApprovalEmailStatus();
    }

    this.atpLinkSearchInput$.next(searchValue);
  }

  onLinkUserInput(event: any): void {
    const searchValue = event.target.value || '';
    this.applicationUserId = searchValue.trim();
    this.linkUserSearchInput$.next(searchValue);
  }

  // Select ATP from autocomplete
  selectAtpFromAutocomplete(atp: IAtp): void {
    if (atp && atp.id !== 0) {
      this.linkAtpIdDisplayControl.setValue(atp.id.toString(), { emitEvent: false });
      this.atpApplicationForm.patchValue({
        atpName: atp.name,
        atpId: atp.id,
        atpNameAka: atp.nameAKA || ''
      });

      this.realignApplicationTypeForAtpSelectionChange();
      
      // Clear the dropdown
      this.filteredAtpNames = [];
      
      // Load ATP data to pre-populate other fields
      this.loadAtpDataForApplication(atp.id);
      this.refreshLinkApprovalEmailStatus();
    }
  }

  // Get display name for ATP autocomplete
  getAtpDisplayName(atp: IAtp): string {
    if (!atp) return '';
    const name = atp.name || '';
    const aka = atp.nameAKA ? ` (AKA: ${atp.nameAKA})` : '';
    return `${name}${aka}`;
  }

  getLinkUserDisplay(user: Partial<IUser> | null, fallbackEmail: string = ''): string {
    if (!user) {
      return fallbackEmail || '';
    }

    const fullName = (user.fullName || '').trim();
    const email = (user.email || fallbackEmail || '').trim();

    if (fullName && email) {
      return `${fullName} (${email})`;
    }

    return fullName || email;
  }

  selectLinkUser(user: IUser): void {
    this.applicationUserId = user.email || '';
    this.linkUserDisplayControl.setValue(this.getLinkUserDisplay(user), { emitEvent: false });
    this.filteredLinkUsers = [];
  }

  private filterAtpLinkOptions(searchValue: string): void {
    const normalizedSearchValue = searchValue?.toLowerCase().trim() || '';
    if (normalizedSearchValue.length < 2) {
      this.filteredAtpNames = [];
      return;
    }

    this.filteredAtpNames = this.allAtpNames.filter(atp => {
      const idMatch = atp.id?.toString().includes(normalizedSearchValue);
      const nameMatch = atp.name?.toLowerCase().includes(normalizedSearchValue);
      const akaMatch = atp.nameAKA?.toLowerCase().includes(normalizedSearchValue);
      return (idMatch || nameMatch || akaMatch) && atp.id !== 0;
    });
  }

  private searchLinkUsers(searchValue: string): void {
    const normalizedSearchValue = searchValue?.trim() || '';
    if (normalizedSearchValue.length < 2) {
      this.filteredLinkUsers = [];
      return;
    }

    this.commonService.searchB2CUsers(normalizedSearchValue).subscribe({
      next: (users) => {
        this.filteredLinkUsers = users || [];
      },
      error: () => {
        this.filteredLinkUsers = [];
      }
    });
  }

  private hydrateLinkUserDisplay(email: string | null | undefined): void {
    const normalizedEmail = (email || '').trim();
    if (!normalizedEmail) {
      this.linkUserDisplayControl.setValue('', { emitEvent: false });
      return;
    }

    this.commonService.searchB2CUsers(normalizedEmail).subscribe({
      next: (users) => {
        const matchingUser = (users || []).find(user => (user.email || '').toLowerCase() === normalizedEmail.toLowerCase());
        this.linkUserDisplayControl.setValue(this.getLinkUserDisplay(matchingUser || null, normalizedEmail), { emitEvent: false });
      },
      error: () => {
        this.linkUserDisplayControl.setValue(normalizedEmail, { emitEvent: false });
      }
    });
  }

  // ===== Training Manager Staff Methods =====
  
  isTrainingManagerLinked(index: number): boolean {
    const manager = this.trainingManagersFormArray.at(index);
    const person = manager?.get('person')?.value;
    return person && person.id && person.id !== 0;
  }

  removeLinkedTrainingManager(index: number): void {
    const manager = this.trainingManagersFormArray.at(index);
    manager.patchValue({
      person: null,
      firstName: '',
      lastName: '',
      middleName: '',
      title: '',
      phone: '',
      email: ''
    });
    
    // Enable all fields
    manager.get('firstName')?.enable();
    manager.get('lastName')?.enable();
    manager.get('middleName')?.enable();
    manager.get('title')?.enable();
    manager.get('phone')?.enable();
    manager.get('email')?.enable();
    
    // Clear filtered list
    const managerId = manager.get('id')?.value;
    if (managerId) {
      this.filteredTrainingManagers[managerId] = [];
    }
  }

  onTrainingManagerNameInput(event: any, index: number): void {
    const searchValue = event?.target?.value || '';
    const manager = this.trainingManagersFormArray.at(index);
    const managerId = manager.get('id')?.value;
    
    if (!managerId) {
      return;
    }

    this.filteredTrainingManagers[managerId] = this.filterAtpStaffMembers(searchValue);
  }

  onTrainingManagerNameFocus(event: any, index: number, trigger: MatAutocompleteTrigger): void {
    const manager = this.trainingManagersFormArray.at(index);
    const managerId = manager?.get('id')?.value;
    if (!managerId) {
      return;
    }

    const searchValue = event?.target?.value || '';
    this.filteredTrainingManagers[managerId] = this.filterAtpStaffMembers(searchValue);
    this.openAutocompletePanel(trigger);
  }

  selectTrainingManagerStaff(staff: any, index: number): void {
    if (staff && staff.person) {
      const manager = this.trainingManagersFormArray.at(index);
      manager.patchValue({
        person: staff.person,
        firstName: staff.person.firstName || '',
        lastName: staff.person.lastName || '',
        middleName: staff.person.middleName || '',
        title: staff.person.title || '',
        phone: staff.person.phone || '',
        email: staff.person.email || ''
      });
      
      // Disable all editable fields
      if (staff.person.id && staff.person.id !== 0) {
        manager.get('firstName')?.disable();
        manager.get('lastName')?.disable();
        manager.get('middleName')?.disable();
        manager.get('title')?.disable();
        manager.get('phone')?.disable();
        manager.get('email')?.disable();
      }
      
      // Clear filtered list
      const managerId = manager.get('id')?.value;
      if (managerId) {
        this.filteredTrainingManagers[managerId] = [];
      }
    }
  }

  getFilteredTrainingManagers(index: number): any[] {
    const manager = this.trainingManagersFormArray.at(index);
    const managerId = manager?.get('id')?.value;
    return managerId ? (this.filteredTrainingManagers[managerId] || []) : [];
  }

  // ===== Primary Instructor Staff Methods =====
  
  isPrimaryInstructorLinked(index: number): boolean {
    const instructor = this.primaryInstructorsFormArray.at(index);
    const person = instructor?.get('person')?.value;
    return person && person.id && person.id !== 0;
  }

  removeLinkedPrimaryInstructor(index: number): void {
    const instructor = this.primaryInstructorsFormArray.at(index);
    instructor.patchValue({
      person: null,
      firstName: '',
      lastName: '',
      middleName: '',
      title: '',
      phone: '',
      email: ''
    });
    
    // Enable all fields
    instructor.get('firstName')?.enable();
    instructor.get('lastName')?.enable();
    instructor.get('middleName')?.enable();
    instructor.get('title')?.enable();
    instructor.get('phone')?.enable();
    instructor.get('email')?.enable();
    
    // Clear filtered list
    const instructorId = instructor.get('id')?.value;
    if (instructorId) {
      this.filteredPrimaryInstructors[instructorId] = [];
    }
  }

  onPrimaryInstructorNameInput(event: any, index: number): void {
    const searchValue = event?.target?.value || '';
    const instructor = this.primaryInstructorsFormArray.at(index);
    const instructorId = instructor.get('id')?.value;
    
    if (!instructorId) {
      return;
    }

    this.filteredPrimaryInstructors[instructorId] = this.filterAtpStaffMembers(searchValue);
  }

  onPrimaryInstructorNameFocus(event: any, index: number, trigger: MatAutocompleteTrigger): void {
    const instructor = this.primaryInstructorsFormArray.at(index);
    const instructorId = instructor?.get('id')?.value;
    if (!instructorId) {
      return;
    }

    const searchValue = event?.target?.value || '';
    this.filteredPrimaryInstructors[instructorId] = this.filterAtpStaffMembers(searchValue);
    this.openAutocompletePanel(trigger);
  }

  selectPrimaryInstructorStaff(staff: any, index: number): void {
    if (staff && staff.person) {
      const instructor = this.primaryInstructorsFormArray.at(index);
      instructor.patchValue({
        person: staff.person,
        firstName: staff.person.firstName || '',
        lastName: staff.person.lastName || '',
        middleName: staff.person.middleName || '',
        title: staff.person.title || '',
        phone: staff.person.phone || '',
        email: staff.person.email || ''
      });
      
      // Disable all editable fields
      if (staff.person.id && staff.person.id !== 0) {
        instructor.get('firstName')?.disable();
        instructor.get('lastName')?.disable();
        instructor.get('middleName')?.disable();
        instructor.get('title')?.disable();
        instructor.get('phone')?.disable();
        instructor.get('email')?.disable();
      }
      
      // Clear filtered list
      const instructorId = instructor.get('id')?.value;
      if (instructorId) {
        this.filteredPrimaryInstructors[instructorId] = [];
      }
    }
  }

  getFilteredPrimaryInstructors(index: number): any[] {
    const instructor = this.primaryInstructorsFormArray.at(index);
    const instructorId = instructor?.get('id')?.value;
    return instructorId ? (this.filteredPrimaryInstructors[instructorId] || []) : [];
  }

  // ===== Guest Instructor Staff Methods =====
  
  isGuestInstructorLinked(index: number): boolean {
    const instructor = this.guestInstructorsFormArray.at(index);
    const person = instructor?.get('person')?.value;
    return person && person.id && person.id !== 0;
  }

  removeLinkedGuestInstructor(index: number): void {
    const instructor = this.guestInstructorsFormArray.at(index);
    instructor.patchValue({
      person: null,
      name: ''
    });
    
    // Enable fields
    instructor.get('name')?.enable();
    
    // Clear filtered list
    const instructorId = instructor.get('id')?.value;
    if (instructorId) {
      this.filteredGuestInstructors[instructorId] = [];
    }
  }

  onGuestInstructorNameInput(event: any, index: number): void {
    const searchValue = event?.target?.value || '';
    const instructor = this.guestInstructorsFormArray.at(index);
    const instructorId = instructor.get('id')?.value;
    
    if (!instructorId) {
      return;
    }

    this.filteredGuestInstructors[instructorId] = this.filterAtpStaffMembers(searchValue);
  }

  onGuestInstructorNameFocus(event: any, index: number, trigger: MatAutocompleteTrigger): void {
    const instructor = this.guestInstructorsFormArray.at(index);
    const instructorId = instructor?.get('id')?.value;
    if (!instructorId) {
      return;
    }

    const searchValue = event?.target?.value || '';
    this.filteredGuestInstructors[instructorId] = this.filterAtpStaffMembers(searchValue);
    this.openAutocompletePanel(trigger);
  }

  selectGuestInstructorStaff(staff: any, index: number): void {
    if (staff && staff.person) {
      const instructor = this.guestInstructorsFormArray.at(index);
      const fullName = `${staff.person.firstName || ''} ${staff.person.lastName || ''}`.trim();
      
      instructor.patchValue({
        person: staff.person,
        name: fullName
      });
      
      // Disable fields
      if (staff.person.id && staff.person.id !== 0) {
        instructor.get('name')?.disable();
      }
      
      // Clear filtered list
      const instructorId = instructor.get('id')?.value;
      if (instructorId) {
        this.filteredGuestInstructors[instructorId] = [];
      }
    }
  }

  getFilteredGuestInstructors(index: number): any[] {
    const instructor = this.guestInstructorsFormArray.at(index);
    const instructorId = instructor?.get('id')?.value;
    return instructorId ? (this.filteredGuestInstructors[instructorId] || []) : [];
  }

  downloadCourseDocument(courseIndex: number, docIndex: number, documentType: string) {
    const courseControl = this.coursesFormArray.at(courseIndex);
    let documentFile: any = null;

    switch (documentType) {
      case 'material':
        const materialDocs = courseControl.get('courseMaterialDocuments')?.value || [];
        documentFile = materialDocs[docIndex];
        break;
      case 'test':
        const testDocs = courseControl.get('courseTestDocuments')?.value || [];
        documentFile = testDocs[docIndex];
        break;
      case 'assessment':
        const assessmentDocs = courseControl.get('courseAssessmentDocuments')?.value || [];
        documentFile = assessmentDocs[docIndex];
        break;
      case 'qualityControl':
        const qualityDocs = courseControl.get('courseQualityControlDocuments')?.value || [];
        documentFile = qualityDocs[docIndex];
        break;
      case 'certification':
        const certDocs = courseControl.get('courseCertificationDocuments')?.value || [];
        documentFile = certDocs[docIndex];
        break;
    }

    this.downloadDocument(documentFile);
  }

  downloadLanguageDocument(courseIndex: number, docIndex: number) {
    const courseControl = this.coursesFormArray.at(courseIndex);
    const documents = courseControl.get('languageDocuments')?.value || [];
    const documentFile = documents[docIndex];

    this.downloadDocument(documentFile);
  }

  updateStreet(index: number, value: string) {
    const facilityControl = this.facilityLocationsFormArray.at(index);
    facilityControl.get('street')?.setValue(value);
    this.updateFacilityDataSource();
  }

  updateCity(index: number, value: string) {
    const facilityControl = this.facilityLocationsFormArray.at(index);
    facilityControl.get('city')?.setValue(value);
    this.updateFacilityDataSource();
  }

  updateState(index: number, value: string) {
    const facilityControl = this.facilityLocationsFormArray.at(index);
    facilityControl.get('state')?.setValue(value);
    this.updateFacilityDataSource();
  }

  updateZip(index: number, value: string) {
    const facilityControl = this.facilityLocationsFormArray.at(index);
    facilityControl.get('zip')?.setValue(value);
    this.updateFacilityDataSource();
  }

  updateFacilityType(index: number, value: string) {
    const facilityControl = this.facilityLocationsFormArray.at(index);
    facilityControl.get('facilityType')?.setValue(value);
    this.updateFacilityDataSource();
  }

  async onFacilityDocumentSelected(event: any, facilityIndex: number) {
    const file = event.target.files[0];
    if (file) {
      const facilityControl = this.facilityLocationsFormArray.at(facilityIndex);
      const currentDocuments = facilityControl.get('facilityDocuments')?.value || [];
      
      await this.handleDocumentSelection(file, currentDocuments, DOCUMENT_CATEGORIES.courseFacilityDocument, (updatedDocuments) => {
        facilityControl.get('facilityDocuments')?.setValue([...updatedDocuments]);
        this.updateFacilityDataSource();
      });
    }
    // Clear the input
    event.target.value = '';
  }

  removeFacilityDocument(facilityIndex: number, docIndex: number) {
    const facilityControl = this.facilityLocationsFormArray.at(facilityIndex);
    const documents = facilityControl.get('facilityDocuments')?.value || [];
    const fileName = documents[docIndex]?.name || 'this document';
    
    const dialogRef = this.dialog.open(ConfirmationDialogueComponent, {
      width: '400px',
      data: {
        title: 'Remove Document?',
        message: `Are you sure you want to remove <strong>"${fileName}"</strong>?`
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result === true) {
        documents.splice(docIndex, 1);
        facilityControl.get('facilityDocuments')?.setValue([...documents]);
        this.updateFacilityDataSource();
      }
    });
  }

  downloadFacilityDocument(facilityIndex: number, docIndex: number) {
    const facilityControl = this.facilityLocationsFormArray.at(facilityIndex);
    const documents = facilityControl.get('facilityDocuments')?.value || [];
    const documentFile = documents[docIndex];

    this.downloadDocument(documentFile);
  }

  get otherAccreditationsFormArray(): UntypedFormArray {
    return this.atpApplicationForm.get('otherAccreditations') as UntypedFormArray;
  }

  // ---------------------------------------------------------------------------
  // Reaccreditation supporting documents
  // ---------------------------------------------------------------------------

  getReaccreditationDocuments(controlName: string): any[] {
    return this.atpApplicationForm.get(controlName)?.value || [];
  }

  async onReaccreditationDocumentSelected(event: any, controlName: string) {
    const file = event?.target?.files?.[0];
    if (file) {
      const control = this.atpApplicationForm.get(controlName);
      const currentDocuments = control?.value || [];
      const category = controlName === 'reaccreditationInstructorListDocuments'
        ? DOCUMENT_CATEGORIES.reaccreditationInstructorListDocument
        : DOCUMENT_CATEGORIES.reaccreditationCourseHistoryDocument;

      await this.handleDocumentSelection(file, currentDocuments, category, (updatedDocuments) => {
        control?.setValue([...updatedDocuments]);
        control?.markAsTouched();
        control?.markAsDirty();
        control?.updateValueAndValidity();
      });
    }

    if (event?.target) {
      event.target.value = '';
    }
  }

  removeReaccreditationDocument(controlName: string, docIndex: number) {
    const control = this.atpApplicationForm.get(controlName);
    const documents = control?.value || [];
    const fileName = documents[docIndex]?.name || 'this document';

    const dialogRef = this.dialog.open(ConfirmationDialogueComponent, {
      width: '400px',
      data: {
        title: 'Remove Document?',
        message: `Are you sure you want to remove <strong>"${fileName}"</strong>?`
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result === true) {
        documents.splice(docIndex, 1);
        control?.setValue([...documents]);
        control?.markAsTouched();
        control?.markAsDirty();
        control?.updateValueAndValidity();
      }
    });
  }

  downloadReaccreditationDocument(controlName: string, docIndex: number) {
    const documents = this.atpApplicationForm.get(controlName)?.value || [];
    this.downloadDocument(documents[docIndex]);
  }

  createOtherAccreditationGroup(): UntypedFormGroup {
    const id = 'oa_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
    
    // Check if otherJurisdictionAccreditation is true to determine if validators should be added
    const isAccreditationEnabled = this.atpApplicationForm.get('otherJurisdictionAccreditation')?.value === true;
    
    const group = new UntypedFormGroup({
      id: new UntypedFormControl(id),
      jurisdiction: new UntypedFormControl('', isAccreditationEnabled ? [Validators.required] : []),
      accreditationNumber: new UntypedFormControl('', isAccreditationEnabled ? [Validators.required] : []),
      courseDiscipline: new UntypedFormControl(null, isAccreditationEnabled ? [Validators.required] : []),
      courseType: new UntypedFormControl(null, isAccreditationEnabled ? [Validators.required] : []),
      approvalDate: new UntypedFormControl('', isAccreditationEnabled ? [Validators.required] : []),
      approvingAuthority: new UntypedFormControl('', isAccreditationEnabled ? [Validators.required] : []),
      documents: new UntypedFormControl([], isAccreditationEnabled ? [this.requiredDocumentValidator.bind(this)] : [])
    });
    
    return group;
  }

  addOtherAccreditation() {
    const newGroup = this.createOtherAccreditationGroup();
    this.otherAccreditationsFormArray.push(newGroup);
    this.updateOtherAccreditationsDataSource();
  }

  removeOtherAccreditation(index: number) {
    const accreditation = this.otherAccreditationsFormArray.at(index);
    const jurisdiction = accreditation.get('jurisdiction')?.value || '';
    const accreditationNumber = accreditation.get('accreditationNumber')?.value || '';
    const displayName = jurisdiction || accreditationNumber ? `${jurisdiction} - ${accreditationNumber}`.trim() : `Other Accreditation ${index + 1}`;
    
    const dialogRef = this.dialog.open(ConfirmationDialogueComponent, {
      width: '400px',
      data: {
        title: 'Delete?',
        message: `Are you sure you want to delete <strong>${displayName}</strong>?`
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result === true) {
        this.otherAccreditationsFormArray.removeAt(index);
        this.updateOtherAccreditationsDataSource();
      }
    });
  }

  updateOtherAccreditationsDataSource() {
    this.otherAccreditationsDataSource.data = this.otherAccreditationsFormArray.controls;
  }

  updateOtherAccreditation(index: number, field: string, value: any) {
    const accreditation = this.otherAccreditationsFormArray.at(index);
    accreditation.get(field)?.setValue(value);
  }

  async onOtherAccreditationDocumentSelected(event: any, index: number) {
    const file = event.target.files[0];
    if (file) {
      const accreditation = this.otherAccreditationsFormArray.at(index);
      const currentDocuments = accreditation.get('documents')?.value || [];
      
      await this.handleDocumentSelection(file, currentDocuments, DOCUMENT_CATEGORIES.otherAccreditationDocument, (updatedDocuments) => {
        accreditation.get('documents')?.setValue([...updatedDocuments]);
      });
      
      event.target.value = '';
    }
  }

  removeOtherAccreditationDocument(accreditationIndex: number, docIndex: number) {
    const accreditation = this.otherAccreditationsFormArray.at(accreditationIndex);
    const documents = accreditation.get('documents')?.value || [];
    const fileName = documents[docIndex]?.name || 'this document';
    
    const dialogRef = this.dialog.open(ConfirmationDialogueComponent, {
      width: '400px',
      data: {
        title: 'Remove Document?',
        message: `Are you sure you want to remove <strong>"${fileName}"</strong>?`
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result === true) {
        documents.splice(docIndex, 1);
        accreditation.get('documents')?.setValue([...documents]);
      }
    });
  }

  downloadOtherAccreditationDocument(accreditationIndex: number, docIndex: number) {
    const accreditation = this.otherAccreditationsFormArray.at(accreditationIndex);
    const documents = accreditation.get('documents')?.value || [];
    const documentFile = documents[docIndex];

    this.downloadDocument(documentFile);
  }

  getTotalFees(): number {
    if (!this.coursesFormArray) {
      return 0;
    }
    
    return this.coursesFormArray.controls.reduce((total, courseControl) => {      
      const fee = courseControl.get('fee')?.value || 0;
      return total + fee;
    }, 0);
  }

  getGrandTotalFees(): number {
    return this.getTotalFees();
  }

  getApplicationDateOrToday(): Date {
    const applicationDate = this.atpApplicationForm?.get('applicationDate')?.value;
    return applicationDate || new Date();
  }

  mailingIsSame(): void {        
    if(this.atpApplicationForm.get('mailingIsPhysical').value){
      this.atpApplicationForm.get('atpMailingStreet').clearValidators();
      this.atpApplicationForm.get('atpMailingCity').clearValidators();
      this.atpApplicationForm.get('atpMailingState').clearValidators();
      this.atpApplicationForm.get('atpMailingZip').clearValidators();
    }
    else{
      this.atpApplicationForm.get('atpMailingStreet').setValidators([Validators.required]);
      this.atpApplicationForm.get('atpMailingCity').setValidators([Validators.required]);
      this.atpApplicationForm.get('atpMailingState').setValidators([Validators.required]);
      this.atpApplicationForm.get('atpMailingZip').setValidators([Validators.required]);
    }
    this.atpApplicationForm.get('atpMailingStreet').updateValueAndValidity();
    this.atpApplicationForm.get('atpMailingCity').updateValueAndValidity();
    this.atpApplicationForm.get('atpMailingState').updateValueAndValidity();
    this.atpApplicationForm.get('atpMailingZip').updateValueAndValidity();    
  }

  validateFieldRequired(form: FormGroup, fieldName: string): boolean {
    if (form.hasError(`${fieldName}Required`) && form.get(fieldName).touched) {
      form.get(fieldName).setErrors(['required']);
      return true;
    }
    form.get(fieldName).clearValidators();
    form.get(fieldName).updateValueAndValidity();
    return false;
  }

  // Reaccreditation Instructor Table Functions
  get reaccredInstructorFormArray(): UntypedFormArray {
    return this.atpApplicationForm.get('reaccredInstructorTable') as UntypedFormArray;
  }

  createReaccredInstructorGroup(): UntypedFormGroup {
    const id = 'ri_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
    
    const group = new UntypedFormGroup({
      id: new UntypedFormControl(id),
      firstName: new UntypedFormControl('', [Validators.required]),
      lastName: new UntypedFormControl('', [Validators.required]),
      person: new UntypedFormControl(null) // IPerson reference
    });
    
    return group;
  }

  addReaccredInstructor() {
    const newGroup = this.createReaccredInstructorGroup();
    this.reaccredInstructorFormArray.push(newGroup);
    this.updateReaccredInstructorDataSource();
  }

  removeReaccredInstructor(index: number) {
    const instructor = this.reaccredInstructorFormArray.at(index);
    const firstName = instructor.get('firstName')?.value || '';
    const lastName = instructor.get('lastName')?.value || '';
    const displayName = firstName || lastName ? `${firstName} ${lastName}`.trim() : `Instructor ${index + 1}`;
    
    const dialogRef = this.dialog.open(ConfirmationDialogueComponent, {
      width: '400px',
      data: {
        title: 'Delete?',
        message: `Are you sure you want to delete <strong>${displayName}</strong>?`
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result === true) {
        this.reaccredInstructorFormArray.removeAt(index);
        this.updateReaccredInstructorDataSource();
      }
    });
  }

  updateReaccredInstructorDataSource() {
    this.reaccredInstructorDataSource.data = this.reaccredInstructorFormArray.controls;
  }

  updateReaccredInstructor(index: number, field: string, value: any) {
    const instructor = this.reaccredInstructorFormArray.at(index);
    instructor.get(field)?.setValue(value);
  }

  // Reaccreditation Course Table Functions
  get reaccredCourseFormArray(): UntypedFormArray {
    return this.atpApplicationForm.get('reaccredCourseTable') as UntypedFormArray;
  }

  createReaccredCourseGroup(): UntypedFormGroup {
    const id = 'rc_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
    
    const group = new UntypedFormGroup({
      id: new UntypedFormControl(id),
      discipline: new UntypedFormControl(null, [Validators.required]),
      date: new UntypedFormControl('', [Validators.required]),
      courseType: new UntypedFormControl(null, [Validators.required]),
      location: new UntypedFormControl('', [Validators.required]),
      preTrainingNotification: new UntypedFormControl('', [Validators.required]),
      postTrainingNotification: new UntypedFormControl('', [Validators.required])
    });
    
    return group;
  }

  addReaccredCourse() {
    const newGroup = this.createReaccredCourseGroup();
    this.reaccredCourseFormArray.push(newGroup);
    this.updateReaccredCourseDataSource();
  }

  removeReaccredCourse(index: number) {
    const course = this.reaccredCourseFormArray.at(index);
    const discipline = course.get('discipline')?.value || '';
    const courseType = course.get('courseType')?.value || '';
    const displayName = discipline || courseType ? `${discipline} ${courseType}`.trim() : `Course ${index + 1}`;
    
    const dialogRef = this.dialog.open(ConfirmationDialogueComponent, {
      width: '400px',
      data: {
        title: 'Delete?',
        message: `Are you sure you want to delete <strong>${displayName}</strong>?`
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result === true) {
        this.reaccredCourseFormArray.removeAt(index);
        this.updateReaccredCourseDataSource();
      }
    });
  }

  updateReaccredCourseDataSource() {
    this.reaccredCourseDataSource.data = this.reaccredCourseFormArray.controls;
  }

  updateReaccredCourse(index: number, field: string, value: any) {
    const course = this.reaccredCourseFormArray.at(index);
    course.get(field)?.setValue(value);
  }

  addReaccredInstructorRow() {
    this.addReaccredInstructor();
  }

  addReaccredCourseRow() {
    this.addReaccredCourse();
  }

  // Overview helper methods
  getApplicationTypeLabel(): string {
    const applicationType = this.atpApplicationForm.get('applicationType')?.value;
    if (!applicationType) return '';
    
    const option = this.applicationTypes.find(type => type.value === applicationType);
    return option ? option.label : applicationType;
  }

  getAmmendmentTypeLabel(): string {
    const ammendmentType = this.atpApplicationForm.get('ammendmentType')?.value;
    if (!ammendmentType) return '';
    
    const option = this.ammendmentType.find(type => type.value === ammendmentType);
    return option ? option.label : ammendmentType;
  }

  onAmmendmentTargetChange(accreditation: any): void {
    const targetOption = this.ammendmentTargetOptions.find(option => option.accreditation?.id === accreditation?.id);
    if (!targetOption) {
      return;
    }

    if (targetOption.sourceApplication) {
      this.populateFormWithData(targetOption.sourceApplication, true);
    }

    const latestStaffDataApplication = this.getLatestStaffDataApplication(this.atpTargetContextCache?.applications);
    if (latestStaffDataApplication) {
      this.populateStaffDataFromApplication(latestStaffDataApplication);
    }

    this.applyAtpContactPrefill();
    this.setupAmmendmentApplication(targetOption.accreditation, targetOption.sourceApplication);
  }

  onReaccreditationTargetChange(accreditation: any): void {
    const targetOption = this.reaccreditationTargetOptions.find(option => option.accreditation?.id === accreditation?.id);
    if (!targetOption) {
      return;
    }

    if (targetOption.sourceApplication) {
      this.populateFormWithData(targetOption.sourceApplication, true);
    }

    const latestStaffDataApplication = this.getLatestStaffDataApplication(this.atpTargetContextCache?.applications);
    if (latestStaffDataApplication) {
      this.populateStaffDataFromApplication(latestStaffDataApplication);
    }

    this.applyAtpContactPrefill();
    this.setupRenewalApplication(targetOption.accreditation, targetOption.sourceApplication, this.atpTargetContextCache);
  }

  getLanguageLabel(languageValue: string): string {
    if (!languageValue) return '';
    
    const option = this.courseLanguages.find(lang => lang.value === languageValue);
    return option ? option.label : languageValue;
  }

  getFormatLabel(formatValue: string): string {
    if (!formatValue) return '';
    
    const option = this.courseFormats.find(format => format.value === formatValue);
    return option ? option.label : formatValue;
  }

  getAccreditationLabel(accreditationValue: string): string {
    if (!accreditationValue) return '';
    
    const option = this.courseAccreditations.find(acc => acc.value === accreditationValue);
    return option ? option.label : accreditationValue;
  }

  getProgramTypeLabel(): string {
    const programType = this.atpApplicationForm.get('programType')?.value;
    if (!programType) return '';
    
    const programTypeOption = this.programTypes.find(pt => pt.value === programType);
    return programTypeOption ? programTypeOption.label : programType;
  }

  // Load ATP Application Data
  loadAtpApplication(id: number): void {
    this.loading = true;
    this.atpService.getAtpApplication(id).subscribe({
      next: (application: IAtpApplication) => {        
        this.atpApplication = application;
        this.populateFormWithData(application);
        
        // Disable admin fields if application is approved
        if (application.applicationStatus === APPLICATION_STATUS.approved) {
          this.atpApplicationForm.get('applicationStatus')?.disable();
          this.atpApplicationForm.get('applicationDate')?.disable();
          this.atpApplicationForm.get('applicationViableDate')?.disable();
          this.atpApplicationForm.get('approvalDate')?.disable();
          this.atpApplicationForm.get('applicationFeedback')?.disable();
        }
        
        // If application has an atpId, load the ATP staff for autocomplete
        if (application.atpId && application.atpId !== 0) {
          forkJoin({
            staff: this.atpService.getATPPeople(application.atpId).pipe(
              catchError(error => {
                console.error('Error loading ATP staff:', error);
                return of([]);
              })
            ),
            courseAddresses: this.atpService.getATPCourseAddressesByAtpId(application.atpId).pipe(
              catchError(error => {
                console.error('Error loading ATP course addresses for autocomplete:', error);
                return of([]);
              })
            )
          }).subscribe(({ staff, courseAddresses }) => {
            this.atpStaffMembers = staff || [];
            this.atpContactContextLoaded = false;
            this.setAtpCourseAddressOptions(courseAddresses || []);
          });
        } else {
          this.atpStaffMembers = [];
          this.atpContactContextLoaded = false;
          this.clearAtpCourseAddressOptions();
        }

        this.refreshLinkApprovalEmailStatus();
        
        this.loading = false;
      },
      error: (error) => {
        console.error('Error loading ATP Application:', error);        
        this.loading = false;
        // Initialize with default data if load fails
        this.initializeDefaultData();
      }
    });
  }

  // Load ATP Data to pre-populate application form when creating from embedded context
  loadAtpDataForApplication(atpId: number): void {
    this.loading = true;
    this.atpTargetContextCache = null;

    if (this.data?.targetContext?.atpId === atpId) {
      this.atpTargetContextCache = this.data.targetContext;
    }

    forkJoin({
      atpContext: this.loadAtpApplicationContext(atpId),
      targetContext: this.loadAtpTargetContext(atpId).pipe(
        catchError(error => {
          console.error('Error loading ATP target context for application:', error);
          return of(null);
        })
      )
    }).subscribe({
      next: ({ atpContext, targetContext }) => {
        const latestStaffDataApplication = this.getLatestStaffDataApplication(targetContext?.applications);
        this.applyAtpApplicationContext(atpContext, !latestStaffDataApplication);

        if (latestStaffDataApplication) {
          this.populateStaffDataFromApplication(latestStaffDataApplication);
        }

        this.applyAtpContactPrefill();

        this.loading = false;
      },
      error: (error) => {
        console.error('Error loading ATP data for application:', error);
        this.toastr.warning('Could not load all ATP data. Please verify the information.', 'Warning');
        this.clearAtpCourseAddressOptions();
        this.loading = false;
      }
    });
  }

  // Populate staff members from ATP staff data
  private populateStaffFromAtp(atpStaff: any[]): void {
    // Store all staff members for autocomplete
    this.atpStaffMembers = atpStaff;
    
    // Find contact person (first staff member flagged isContact)
    const contactPerson = this.getAtpContactPerson(atpStaff);
    if (contactPerson) {
      this.atpApplicationForm.patchValue({
        contactPerson: contactPerson.person,
        atpContactFirstName: contactPerson.person.firstName || '',
        atpContactLastName: contactPerson.person.lastName || '',
        atpContactTitle: contactPerson.person.title || ''
      });
      
      // Disable the contact fields since person reference is set
      if (contactPerson.person.id && contactPerson.person.id !== 0) {
        this.atpApplicationForm.get('atpContactFirstName')?.disable();
        this.atpApplicationForm.get('atpContactLastName')?.disable();
        this.atpApplicationForm.get('atpContactTitle')?.disable();
      }
    }
    
    // Clear existing arrays before populating
    while (this.trainingManagersFormArray.length !== 0) {
      this.trainingManagersFormArray.removeAt(0);
    }
    while (this.primaryInstructorsFormArray.length !== 0) {
      this.primaryInstructorsFormArray.removeAt(0);
    }
    while (this.guestInstructorsFormArray.length !== 0) {
      this.guestInstructorsFormArray.removeAt(0);
    }
    
    // Populate Training Managers (isTrainingManager = true)
    const trainingManagers = atpStaff.filter(staff => staff.isTrainingManager && staff.person);
    trainingManagers.forEach(staff => {
      const managerGroup = this.createTrainingManagerGroup();
      managerGroup.patchValue({
        person: staff.person,
        firstName: staff.person.firstName || '',
        lastName: staff.person.lastName || '',
        middleName: staff.person.middleName || '',
        title: staff.person.title || '',
        phone: '', // Leave empty for user to fill
        email: ''  // Leave empty for user to fill
      });
      
      // Disable fields if person reference is set
      if (staff.person.id && staff.person.id !== 0) {
        managerGroup.get('firstName')?.disable();
        managerGroup.get('lastName')?.disable();
        managerGroup.get('middleName')?.disable();
        managerGroup.get('title')?.disable();
        managerGroup.get('phone')?.disable();
        managerGroup.get('email')?.disable();
      }
      
      this.trainingManagersFormArray.push(managerGroup);
      this.trainingManagerIds.push(managerGroup.get('id')?.value);
    });
    
    // Populate Primary Instructors (isPrimaryInstructor = true)
    const primaryInstructors = atpStaff.filter(staff => staff.isPrimaryInstructor && staff.person);
    primaryInstructors.forEach(staff => {
      const instructorGroup = this.createPrimaryInstructorGroup();
      instructorGroup.patchValue({
        person: staff.person,
        firstName: staff.person.firstName || '',
        lastName: staff.person.lastName || '',
        middleName: staff.person.middleName || '',
        title: staff.person.title || '',
        phone: '', // Leave empty for user to fill
        email: ''  // Leave empty for user to fill
      });
      
      // Disable fields if person reference is set
      if (staff.person.id && staff.person.id !== 0) {
        instructorGroup.get('firstName')?.disable();
        instructorGroup.get('lastName')?.disable();
        instructorGroup.get('middleName')?.disable();
        instructorGroup.get('title')?.disable();
        instructorGroup.get('phone')?.disable();
        instructorGroup.get('email')?.disable();
      }
      
      this.primaryInstructorsFormArray.push(instructorGroup);
      this.primaryInstructorIds.push(instructorGroup.get('id')?.value);
    });
    
    // Populate Guest Instructors (isInstructor = true but not isPrimaryInstructor)
    const guestInstructors = atpStaff.filter(staff => staff.isInstructor && !staff.isPrimaryInstructor && staff.person);
    guestInstructors.forEach(staff => {
      const instructorGroup = this.createGuestInstructorGroup();
      const fullName = `${staff.person.firstName || ''} ${staff.person.lastName || ''}`.trim();
      instructorGroup.patchValue({
        person: staff.person,
        name: fullName,
        topics: '' // Leave empty for user to fill
      });
      
      // Disable name field if person reference is set
      if (staff.person.id && staff.person.id !== 0) {
        instructorGroup.get('name')?.disable();
      }
      
      this.guestInstructorsFormArray.push(instructorGroup);
      this.guestInstructorIds.push(instructorGroup.get('id')?.value);
    });
  }

  // Parse accreditation endorsement type to extract renewal application details
  private parseAccreditationEndorsementType(accreditationEndorsementType: string): {
    programType: string;
    discipline: string;
    courseType: string;
    language: string;
    format: string;
  } {
    const result = {
      programType: '',
      discipline: '',
      courseType: 'initial',
      language: 'english',
      format: 'inperson'
    };

    const normalizedEndorsementType = (accreditationEndorsementType || '').trim();
    const normalizedEndorsementTypeLower = normalizedEndorsementType.toLowerCase();
    if (!normalizedEndorsementType) {
      return result;
    }

    if (normalizedEndorsementTypeLower.includes('rrp')) {
      result.programType = 'rrp';
    } else if (normalizedEndorsementTypeLower.includes('lbpa')) {
      result.programType = 'lbpa';
    }

    const endorsementMatch = normalizedEndorsementType.match(/^[^-]+-(.+?)\s*\(([^)]+)\)/);
    const rawDiscipline = endorsementMatch?.[1]?.trim() || '';
    const qualifierTokens = endorsementMatch?.[2]
      ?.split('/')
      .map(token => token.trim().toLowerCase())
      .filter(token => token.length > 0) || [];

    const disciplineMap: Record<string, string> = {
      'dust sampling': 'Dust Sampling Technician'
    };

    result.discipline = disciplineMap[rawDiscipline.toLowerCase()] || rawDiscipline;

    if (qualifierTokens.includes('refresher')) {
      result.courseType = 'refresher';
    } else if (qualifierTokens.includes('initial')) {
      result.courseType = 'initial';
    }

    if (qualifierTokens.includes('spanish')) {
      result.language = 'spanish';
    }

    if (qualifierTokens.includes('online')) {
      result.format = 'online';
    }

    if (!result.discipline) {
      const parts = normalizedEndorsementType.split('-');
      if (parts.length > 1) {
        result.discipline = parts[1].split('(')[0].trim();
      }
    }

    return result;
  }

  // Setup application for renewal with accreditation data
  private setupRenewalApplication(
    accreditation: any,
    sourceApplication: IAtpApplication | null = null,
    targetContext: AtpApplicationTargetContext | null = this.atpTargetContextCache
  ): void {
    // Parse the accreditation endorsement type to extract course details
    const parsedData = this.parseAccreditationEndorsementType(accreditation.accreditationEndorsementType);
    const sourceReaccredCourse = sourceApplication?.reaccredCourseTable?.[0] || null;
    const form = this.atpApplicationForm as UntypedFormGroup | null;
    if (!form) {
      return;
    }
    
    // Set selectedApplicationType to 'new' to show the form, and applicationType to 'reaccreditation'
    this.selectedApplicationType = 'new';
    form.patchValue({
      id: 0,
      approvalDate: null,
      applicationDate: null,
      applicationViableDate: null,
      applicationStatus: 'In Progress',
      applicationFeedback: '',
      attestation: false,
      esignature: '',
      createdAtpAccreditation: null,
      ammendmentAtpAccreditation: null,
      applicationType: 'reaccreditation',
      submissionType: 'new',
      programType: parsedData.programType || sourceApplication?.programType || form.get('programType')?.value || '',
      ammendmentType: '',
      other: '',
      renewalAtpAccreditation: accreditation
    });
    
    this.loadReaccredInstructors(sourceApplication?.reaccredInstructorTable || []);
    this.loadReaccredCourses([{
      ...(sourceReaccredCourse || {}),
      discipline: parsedData.discipline || sourceReaccredCourse?.discipline || '',
      courseType: parsedData.courseType || sourceReaccredCourse?.courseType || ''
    }]);

    this.loadRenewalCoursesForAccreditation(accreditation, sourceApplication, parsedData, targetContext);
  }

  private setupAmmendmentApplication(accreditation: any, sourceApplication: IAtpApplication | null = null): void {
    const parsedData = this.parseAccreditationEndorsementType(accreditation.accreditationEndorsementType);
    const sourceCourse = this.findMatchingSourceCourse(sourceApplication, parsedData);
    const form = this.atpApplicationForm as UntypedFormGroup | null;
    if (!form) {
      return;
    }

    this.selectedApplicationType = 'new';
    form.patchValue({
      id: 0,
      approvalDate: null,
      applicationDate: null,
      applicationViableDate: null,
      applicationStatus: 'In Progress',
      applicationFeedback: '',
      attestation: false,
      esignature: '',
      createdAtpAccreditation: null,
      renewalAtpAccreditation: null,
      applicationType: 'ammendment',
      submissionType: 'new',
      programType: parsedData.programType || sourceApplication?.programType || form.get('programType')?.value || '',
      ammendmentAtpAccreditation: accreditation
    });

    if (!sourceApplication) {
      this.loadCourses([{
        ...(sourceCourse || {}),
        discipline: parsedData.discipline || sourceCourse?.discipline || '',
        type: parsedData.courseType || sourceCourse?.type || 'initial',
        language: parsedData.language || sourceCourse?.language || 'english',
        format: parsedData.format || sourceCourse?.format || 'inperson'
      }], false);
    }

    this.syncCourseAccreditations();
    this.recomputeCourseFees();
  }

  private populateStaffDataFromApplication(application: IAtpApplication): void {
    const documentSafeApplication = this.stripPrefillDocuments(application);
    const hasContactData = !!(
      documentSafeApplication.contactPerson ||
      this.hasText(documentSafeApplication.atpContactFirstName) ||
      this.hasText(documentSafeApplication.atpContactLastName) ||
      this.hasText(documentSafeApplication.atpContactTitle)
    );

    if (hasContactData) {
      this.atpApplicationForm.patchValue({
        contactPerson: documentSafeApplication.contactPerson || null,
        atpContactFirstName: documentSafeApplication.contactPerson?.firstName || documentSafeApplication.atpContactFirstName || '',
        atpContactLastName: documentSafeApplication.contactPerson?.lastName || documentSafeApplication.atpContactLastName || '',
        atpContactTitle: documentSafeApplication.contactPerson?.title || documentSafeApplication.atpContactTitle || ''
      });

      if (documentSafeApplication.contactPerson?.id && documentSafeApplication.contactPerson.id !== 0) {
        this.atpApplicationForm.get('atpContactFirstName')?.disable({ emitEvent: false });
        this.atpApplicationForm.get('atpContactLastName')?.disable({ emitEvent: false });
        this.atpApplicationForm.get('atpContactTitle')?.disable({ emitEvent: false });
      } else {
        this.atpApplicationForm.get('atpContactFirstName')?.enable({ emitEvent: false });
        this.atpApplicationForm.get('atpContactLastName')?.enable({ emitEvent: false });
        this.atpApplicationForm.get('atpContactTitle')?.enable({ emitEvent: false });
      }
    }

    this.loadTrainingManagers(documentSafeApplication.trainingManagers || []);
    this.loadPrimaryInstructors(documentSafeApplication.primaryInstructors || []);
    this.loadGuestInstructors(documentSafeApplication.guestInstructors || []);
  }

  private stripCourseDocuments(course: any): any {
    return {
      ...(course || {}),
      languageDocuments: [],
      courseMaterialDocuments: [],
      courseTestDocuments: [],
      courseAssessmentDocuments: [],
      courseQualityControlDocuments: [],
      courseCertificationDocuments: [],
      facilityDocuments: []
    };
  }

  private stripPrefillDocuments(application: IAtpApplication): IAtpApplication {
    return {
      ...application,
      courses: (application.courses || []).map(course => this.stripCourseDocuments(course)),
      trainingManagers: (application.trainingManagers || []).map((manager: any) => ({
        ...manager,
        trainingData: (manager.trainingData || []).map((training: any) => ({
          ...training,
          documents: []
        })),
        constructionData: (manager.constructionData || []).map((construction: any) => ({
          ...construction,
          documents: []
        }))
      })),
      primaryInstructors: (application.primaryInstructors || []).map((instructor: any) => ({
        ...instructor,
        principalInstructorDocuments: [],
        trainingData: (instructor.trainingData || []).map((training: any) => ({
          ...training,
          documents: []
        })),
        constructionData: (instructor.constructionData || []).map((construction: any) => ({
          ...construction,
          documents: []
        }))
      })),
      otherAccreditations: (application.otherAccreditations || []).map((accreditation: any) => ({
        ...accreditation,
        documents: []
      }))
    };
  }

  // Populate form with loaded data
  private populateFormWithData(application: IAtpApplication, suppressSubmittedNavigation: boolean = false): void {
    const applicationToLoad = suppressSubmittedNavigation
      ? this.stripPrefillDocuments(application)
      : application;

    this.applicationUserId = applicationToLoad.userID || '';
    this.hydrateLinkUserDisplay(this.applicationUserId);
    this.linkAtpIdDisplayControl.setValue(applicationToLoad.atpId?.toString() || '', { emitEvent: false });

    // Set basic form values
    this.selectedApplicationType = applicationToLoad.submissionType || '';
    this.atpApplicationForm.patchValue({
      id: applicationToLoad.id || 0, // Set the application ID - preserve database ID
      atpId: applicationToLoad.atpId || null, // Set the ATP ID if present
      approvalDate: this.formatDateForInput(applicationToLoad.approvalDate) || null,
      applicationDate: this.formatDateForInput(applicationToLoad.applicationDate) || null,
      applicationViableDate: this.formatDateForInput(applicationToLoad.applicationViableDate) || null,
      applicationStatus: applicationToLoad.applicationStatus || 'In Progress',
      applicationFeedback: applicationToLoad.applicationFeedback || '',
      contactPerson: applicationToLoad.contactPerson || null,
      createdAtpAccreditation: applicationToLoad.createdAtpAccreditation || null,
      renewalAtpAccreditation: applicationToLoad.renewalAtpAccreditation || null,
      ammendmentAtpAccreditation: (applicationToLoad as any).ammendmentAtpAccreditation || null,
      submissionType: applicationToLoad.submissionType || '',
      programType: applicationToLoad.programType || '',
      applicationType: applicationToLoad.applicationType || '',
      ammendmentType: (applicationToLoad as any).ammendmentType || (applicationToLoad as any).ammendmentType || '',
      other: applicationToLoad.other || '',
      atpName: applicationToLoad.atpName || '',
      atpOrId: applicationToLoad.atpOrId || '',
      mailingIsPhysical: applicationToLoad.mailingIsPhysical || false,
      atpPhysicalState: applicationToLoad.atpPhysicalState || '',
      atpPhysicalStreet: applicationToLoad.atpPhysicalStreet || '',
      atpPhysicalCity: applicationToLoad.atpPhysicalCity || '',
      atpPhysicalZip: applicationToLoad.atpPhysicalZip || '',
      atpPhysicalCounty: applicationToLoad.atpPhysicalCounty || '',
      atpMailingStreet: applicationToLoad.atpMailingStreet || '',
      atpMailingCity: applicationToLoad.atpMailingCity || '',
      atpMailingState: applicationToLoad.atpMailingState || '',
      atpMailingZip: applicationToLoad.atpMailingZip || '',
      atpMailingCounty: applicationToLoad.atpMailingCounty || '',
      atpContactFirstName: applicationToLoad.contactPerson?.firstName || applicationToLoad.atpContactFirstName || '',
      atpContactLastName: applicationToLoad.contactPerson?.lastName || applicationToLoad.atpContactLastName || '',
      atpContactTitle: applicationToLoad.contactPerson?.title || applicationToLoad.atpContactTitle || '',
      atpPhone: applicationToLoad.atpPhone || '',
      atpAlternatePhone: applicationToLoad.atpAlternatePhone || '',
      atpFaxPhone: applicationToLoad.atpFaxPhone || '',
      atpEmail: applicationToLoad.atpEmail || '',
      atpConfirmEmail: applicationToLoad.atpConfirmEmail || '',
      atpWebsite: applicationToLoad.atpWebsite || '',
      otherJurisdictionAccreditation: applicationToLoad.otherJurisdictionAccreditation || false,
      hasViolation: applicationToLoad.hasViolation || false,
      violationDescription: applicationToLoad.violationDescription || '',
      additionalInformation: applicationToLoad.additionalInformation || '',
      reaccreditationChangesSinceLastApproval: applicationToLoad.reaccreditationChangesSinceLastApproval || '',
      reaccreditationHasChanges: this.resolveReaccreditationHasChanges(applicationToLoad),
      reaccreditationMaterialsOrLocationChanged: (applicationToLoad as any).reaccreditationMaterialsOrLocationChanged === true,
      reaccreditationTrainingMaterialsChanged: (applicationToLoad as any).reaccreditationTrainingMaterialsChanged === true,
      reaccreditationTrainingLocationChanged: (applicationToLoad as any).reaccreditationTrainingLocationChanged === true,
      reaccreditationHasNewStaff: (applicationToLoad as any).reaccreditationHasNewStaff === true,
      reaccreditationInstructorListDocuments: suppressSubmittedNavigation
        ? []
        : ((applicationToLoad as any).reaccreditationInstructorListDocuments || []),
      reaccreditationCourseHistoryDocuments: suppressSubmittedNavigation
        ? []
        : ((applicationToLoad as any).reaccreditationCourseHistoryDocuments || []),
      attestation: applicationToLoad.attestation || false,
      esignature: applicationToLoad.esignature || ''
    });
    
    // Disable contact person fields if linked
    if (applicationToLoad.contactPerson && applicationToLoad.contactPerson.id && applicationToLoad.contactPerson.id !== 0) {
      this.atpApplicationForm.get('atpContactFirstName')?.disable();
      this.atpApplicationForm.get('atpContactLastName')?.disable();
      this.atpApplicationForm.get('atpContactTitle')?.disable();
    }

    // Load array data
    this.loadCourses(applicationToLoad.courses || [], !suppressSubmittedNavigation);
    this.loadTrainingManagers(applicationToLoad.trainingManagers || []);
    this.loadPrimaryInstructors(applicationToLoad.primaryInstructors || []);
    this.loadGuestInstructors(applicationToLoad.guestInstructors || []);
    this.loadOtherAccreditations(applicationToLoad.otherAccreditations || []);
    this.loadReaccredInstructors(applicationToLoad.reaccredInstructorTable || []);
    this.loadReaccredCourses(applicationToLoad.reaccredCourseTable || []);

    // Child arrays are populated above, so refresh every conditional validator now.
    this.applyReaccreditationValidators();
    
    // Navigate to confirmation step if application is submitted
    if (!suppressSubmittedNavigation && application.applicationStatus === APPLICATION_STATUS.submitted) {
      setTimeout(() => {
        this.navigateToConfirmationStep();
      }, 100); // Small delay to ensure form is fully loaded
    }
  }

  // Load courses data
  private loadCourses(courses: any[], includeDocuments: boolean = true): void {
    // Clear existing courses
    while (this.coursesFormArray.length !== 0) {
      this.coursesFormArray.removeAt(0);
    }

    const normalizedCourses = (courses || []).map(course =>
      includeDocuments ? course : this.stripCourseDocuments(course)
    );

    normalizedCourses.forEach(course => {
      const courseGroup = this.createCourseGroup();
      
      // Handle courseMaterialType loading:
      // If the value is 'EPA', keep it as is
      // If the value is 'other' or anything else (custom text), set both courseMaterialType and courseMaterialTypeCustom
      const materialType = course.courseMaterialType;
      
      // Handle courseTest loading in the same way:
      // If the value is 'EPA', keep it as is
      // If the value is 'other' or anything else (custom text), set both courseTest and courseTestCustom
      const testType = course.courseTest;
      
      if ((materialType && materialType !== 'EPA' && materialType !== null && materialType !== '') ||
          (testType && testType !== 'EPA' && testType !== null && testType !== '')) {
        // Has custom text - set both fields for material type and/or test type
        courseGroup.patchValue({
          ...course,
          courseMaterialType: materialType,
          courseMaterialTypeCustom: (materialType && materialType !== 'EPA') ? materialType : '',
          courseTest: testType,
          courseTestCustom: (testType && testType !== 'EPA') ? testType : ''
        });
      } else {
        // This is 'EPA' or null/empty - just patch normally
        courseGroup.patchValue(course);
      }
      
      this.coursesFormArray.push(courseGroup);
    });

    this.syncCourseAccreditations();
    this.recomputeCourseFees();
    this.updateCourseDataSource();
    this.updateFacilityCourseOptionsCache();
    this.loadFacilityLocationsFromCourses(normalizedCourses);
  }

  private loadFacilityLocationsFromCourses(courses: any[]): void {
    while (this.facilityLocationsFormArray.length !== 0) {
      this.facilityLocationsFormArray.removeAt(0);
    }

    const groupedLocations: {
      [key: string]: { street: string, city: string, state: string, zip: string, facilityType: string, facilityDocuments: any[], selectedCourseIds: string[] }
    } = {};

    courses.forEach((course: any) => {
      const hasFacilityData = !!(course.street || course.city || course.state || course.zip || course.facilityType || (course.facilityDocuments && course.facilityDocuments.length));
      if (!hasFacilityData) {
        return;
      }

      const courseId = String(course.id);
      const docsKey = JSON.stringify((course.facilityDocuments || []).map((doc: any) => `${doc.id || ''}|${doc.name || ''}`));
      const locationKey = `${course.street || ''}|${course.city || ''}|${course.state || ''}|${course.zip || ''}|${course.facilityType || ''}|${docsKey}`;

      if (!groupedLocations[locationKey]) {
        groupedLocations[locationKey] = {
          street: course.street || '',
          city: course.city || '',
          state: course.state || '',
          zip: course.zip || '',
          facilityType: course.facilityType || '',
          facilityDocuments: course.facilityDocuments || [],
          selectedCourseIds: []
        };
      }

      groupedLocations[locationKey].selectedCourseIds.push(courseId);
    });

    Object.values(groupedLocations).forEach(location => {
      const group = this.createFacilityLocationGroup();
      group.patchValue(location);
      this.facilityLocationsFormArray.push(group);
    });

    if (this.facilityLocationsFormArray.length === 0 && this.coursesFormArray.length > 0) {
      this.addFacilityLocation();
      return;
    }

    this.updateFacilityDataSource();
  }

  // Load training managers data
  private loadTrainingManagers(trainingManagers: any[]): void {
    // Clear existing training managers
    while (this.trainingManagersFormArray.length !== 0) {
      this.trainingManagersFormArray.removeAt(0);
    }

    trainingManagers.forEach(manager => {
      const managerGroup = this.createTrainingManagerGroup();
      
      // If person reference exists, use person data; otherwise use direct properties
      const firstName = manager.person?.firstName || manager.firstName || '';
      const lastName = manager.person?.lastName || manager.lastName || '';
      const middleName = manager.person?.middleName || manager.middleName || '';
      const title = manager.person?.title || manager.title || '';
      const phone = manager.person?.phone || manager.phone || '';
      const email = manager.person?.email || manager.email || '';
      
      managerGroup.patchValue({
        id: manager.id,
        firstName: firstName,
        lastName: lastName,
        middleName: middleName,
        title: title,
        phone: phone,
        email: email,
        person: manager.person || null
      });
      
      // Disable all editable fields if person is linked
      if (manager.person && manager.person.id && manager.person.id !== 0) {
        managerGroup.get('firstName')?.disable();
        managerGroup.get('lastName')?.disable();
        managerGroup.get('middleName')?.disable();
        managerGroup.get('title')?.disable();
        managerGroup.get('phone')?.disable();
        managerGroup.get('email')?.disable();
      }

      // Load training data
      const trainingArray = managerGroup.get('trainingData') as UntypedFormArray;
      while (trainingArray.length !== 0) {
        trainingArray.removeAt(0);
      }
      (manager.trainingData || []).forEach(training => {
        const trainingGroup = this.createTrainingManagerTrainingRow();
        trainingGroup.patchValue(training);
        trainingArray.push(trainingGroup);
      });

      // Load construction data
      const constructionArray = managerGroup.get('constructionData') as UntypedFormArray;
      while (constructionArray.length !== 0) {
        constructionArray.removeAt(0);
      }
      (manager.constructionData || []).forEach(construction => {
        const constructionGroup = this.createTrainingManagerConstructionRow();
        constructionGroup.patchValue(construction);
        constructionArray.push(constructionGroup);
      });

      this.trainingManagersFormArray.push(managerGroup);
      this.trainingManagerIds.push(manager.id);
    });
  }

  // Load primary instructors data
  private loadPrimaryInstructors(primaryInstructors: any[]): void {
    // Clear existing primary instructors
    while (this.primaryInstructorsFormArray.length !== 0) {
      this.primaryInstructorsFormArray.removeAt(0);
    }

    primaryInstructors.forEach(instructor => {
      const instructorGroup = this.createPrimaryInstructorGroup();
      
      // If person reference exists, use person data; otherwise use direct properties
      const firstName = instructor.person?.firstName || instructor.firstName || '';
      const lastName = instructor.person?.lastName || instructor.lastName || '';
      const middleName = instructor.person?.middleName || instructor.middleName || '';
      const title = instructor.person?.title || instructor.title || '';
      const phone = instructor.person?.phone || instructor.phone || '';
      const email = instructor.person?.email || instructor.email || '';
      
      // Format the instructor data with proper date formatting
      const formattedInstructor = {
        ...instructor,
        firstName: firstName,
        lastName: lastName,
        middleName: middleName,
        title: title,
        phone: phone,
        email: email,
        trainingCompleteDate: this.formatDateForInput(instructor.trainingCompleteDate),
        person: instructor.person || null
      };
      instructorGroup.patchValue(formattedInstructor);
      
      // Disable all editable fields if person is linked
      if (instructor.person && instructor.person.id && instructor.person.id !== 0) {
        instructorGroup.get('firstName')?.disable();
        instructorGroup.get('lastName')?.disable();
        instructorGroup.get('middleName')?.disable();
        instructorGroup.get('title')?.disable();
        instructorGroup.get('phone')?.disable();
        instructorGroup.get('email')?.disable();
      }

      // Load training data
      const trainingArray = instructorGroup.get('trainingData') as UntypedFormArray;
      while (trainingArray.length !== 0) {
        trainingArray.removeAt(0);
      }
      (instructor.trainingData || []).forEach(training => {
        const trainingGroup = this.createPrimaryInstructorTrainingRow();
        trainingGroup.patchValue(training);
        trainingArray.push(trainingGroup);
      });

      // Load construction data
      const constructionArray = instructorGroup.get('constructionData') as UntypedFormArray;
      while (constructionArray.length !== 0) {
        constructionArray.removeAt(0);
      }
      (instructor.constructionData || []).forEach(construction => {
        const constructionGroup = this.createPrimaryInstructorConstructionRow();
        constructionGroup.patchValue(construction);
        constructionArray.push(constructionGroup);
      });

      this.primaryInstructorsFormArray.push(instructorGroup);
      this.primaryInstructorIds.push(instructor.id);
    });
  }

  // Load guest instructors data
  private loadGuestInstructors(guestInstructors: any[]): void {
    // Clear existing guest instructors
    while (this.guestInstructorsFormArray.length !== 0) {
      this.guestInstructorsFormArray.removeAt(0);
    }

    guestInstructors.forEach(instructor => {
      const instructorGroup = this.createGuestInstructorGroup();
      
      // If person reference exists, construct name from person; otherwise use direct name property
      let name = instructor.name || '';
      if (instructor.person && instructor.person.id && instructor.person.id !== 0) {
        name = `${instructor.person.firstName || ''} ${instructor.person.lastName || ''}`.trim();
      }
      
      instructorGroup.patchValue({
        id: instructor.id,
        name: name,
        topics: instructor.topics || '',
        handsOn: !!instructor.handsOn,
        person: instructor.person || null
      });
      
      // Disable fields if person is linked
      if (instructor.person && instructor.person.id && instructor.person.id !== 0) {
        instructorGroup.get('name')?.disable();
      }
      
      this.guestInstructorsFormArray.push(instructorGroup);
      this.guestInstructorIds.push(instructor.id);
    });
  }

  // Helper function to format date for form inputs (remove time component)
  private formatDateForInput(dateString: string | null | undefined): string {
    if (!dateString) return '';
    
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return '';
      
      // Return date in YYYY-MM-DD format (ISO date format without time)
      return date.toISOString().split('T')[0];
    } catch (error) {
      console.warn('Invalid date format:', dateString);
      return '';
    }
  }

  // Helper function to describe a selected file without reading its contents
  private async convertFileToDocumentFile(file: File, category: string): Promise<any> {
    const fileExtension = file.name.split('.').pop()?.toLowerCase() || '';

    return {
      id: 0,
      name: file.name,
      size: file.size,
      type: fileExtension,
      fileType: file.type,
      lastModified: file.lastModified,
      documentCategory: category,
      // Held until the application has an id to upload against.
      _file: file
    };
  }

  // Unified document selection handler
  private async handleDocumentSelection(file: File, documentArray: any[], category: string, updateCallback?: (updatedDocuments: any[]) => void): Promise<void> {
    try {
      const extension = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();

      if (!ACCEPTED_FILE_TYPES.extensions.includes(extension)) {
        this.toastr.error(`Only the following file types are accepted: ${ACCEPTED_FILE_TYPES.displayText}`, 'File Error');
        return;
      }

      const documentFile = await this.convertFileToDocumentFile(file, category);
      this.pendingDocuments.push(documentFile);
      documentArray.push(documentFile);

      if (updateCallback) {
        updateCallback(documentArray);
      }
    } catch (error) {
      console.error('Error handling document selection:', error);
    }
  }

  /** Uploads every file picked since the last save, attaching it to the application. */
  private async flushPendingDocuments(applicationId: number): Promise<boolean> {
    const pending = this.pendingDocuments.filter(doc => doc?._file && !doc.id);

    if (!applicationId || pending.length === 0) {
      return false;
    }

    for (const doc of pending) {
      const saved = await this.documentService
        .uploadDocument(doc._file, { ownerType: 'AtpApplication', ownerId: applicationId }, doc.documentCategory)
        .toPromise();

      doc.id = saved.id;
      delete doc._file;
    }

    this.pendingDocuments = this.pendingDocuments.filter(doc => doc?._file && !doc.id);

    return true;
  }

  // Unified download handler
  private downloadDocument(documentFile: any): void {
    if (!documentFile) return;

    // Not uploaded yet, so hand back the file the user just picked
    const file = documentFile._file;
    if (file && file instanceof File) {
      const url = window.URL.createObjectURL(file);
      const link = document.createElement('a');
      link.href = url;
      link.download = documentFile.name || file.name;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } else if (documentFile.id) {
      this.documentService.downloadDocument({ id: documentFile.id, fileName: documentFile.name } as any).subscribe({
        error: () => this.toastr.error('Unable to download the document.', 'Download Error')
      });
    } else {
      this.toastr.error('Document not available for download.', 'Download Error');
    }
  }

  // Load other accreditations data
  private loadOtherAccreditations(otherAccreditations: any[]): void {
    // Clear existing other accreditations
    while (this.otherAccreditationsFormArray.length !== 0) {
      this.otherAccreditationsFormArray.removeAt(0);
    }

    otherAccreditations.forEach(accreditation => {
      const accreditationGroup = this.createOtherAccreditationGroup();
      // Format the accreditation data with proper date formatting
      const formattedAccreditation = {
        ...accreditation,
        approvalDate: this.formatDateForInput(accreditation.approvalDate)
      };
      accreditationGroup.patchValue(formattedAccreditation);
      this.otherAccreditationsFormArray.push(accreditationGroup);
    });

    this.updateOtherAccreditationsDataSource();
  }

  // Load reaccreditation instructors data
  private loadReaccredInstructors(reaccredInstructors: any[]): void {
    // Clear existing reaccreditation instructors
    while (this.reaccredInstructorFormArray.length !== 0) {
      this.reaccredInstructorFormArray.removeAt(0);
    }

    reaccredInstructors.forEach(instructor => {
      const instructorGroup = this.createReaccredInstructorGroup();
      instructorGroup.patchValue(instructor);
      this.reaccredInstructorFormArray.push(instructorGroup);
    });

    this.updateReaccredInstructorDataSource();
  }

  // Load reaccreditation courses data
  private loadReaccredCourses(reaccredCourses: any[]): void {
    // Clear existing reaccreditation courses
    while (this.reaccredCourseFormArray.length !== 0) {
      this.reaccredCourseFormArray.removeAt(0);
    }

    reaccredCourses.forEach(course => {
      const courseGroup = this.createReaccredCourseGroup();
      // Format the course data with proper date formatting
      const formattedCourse = {
        ...course,
        date: this.formatDateForInput(course.date)
      };
      courseGroup.patchValue(formattedCourse);
      this.reaccredCourseFormArray.push(courseGroup);
    });

    this.updateReaccredCourseDataSource();
  }

  // Save ATP Application
  saveAtpApplication(): void {    
    const submissionType = this.selectedApplicationType || this.atpApplicationForm.get('submissionType')?.value;
    var dialogRef;
    if (submissionType === 'link') {
      dialogRef = this.dialog.open(ConfirmationDialogueComponent, {
      width: '400px',
      data: {
        title: 'Save Link Request?',
        message: 'Are you sure you want to save this link request?'
      }
      });
    }
    else{
      dialogRef = this.dialog.open(ConfirmationDialogueComponent, {
      width: '400px',
      data: {
        title: 'Save Application?',
        message: 'Are you sure you want to save this application?'
      }
      });
    }

    dialogRef.afterClosed().subscribe(result => {
      if (result === true) {
        if (!this.validateFacilityAssignments(false)) {
          return;
        }

        this.loading = true;
        
        // Prepare the application data
        const applicationData = this.prepareApplicationData();    
        this.atpService.saveAtpApplication(applicationData).subscribe({
          next: async (response) => {        
            // Record the id before the follow-up save so it updates this application instead of creating a duplicate.
            if (response?.id) {
              this.atpApplicationForm.patchValue({ id: response.id });
            }

            try {
              // Files can only be uploaded once the application has an id, so attach them now
              // and save again so they land on the right course, instructor or accreditation.
              if (await this.flushPendingDocuments(response?.id)) {
                await this.atpService.saveAtpApplication(this.prepareApplicationData()).toPromise();
              }
            } catch {
              this.toastr.error('The application saved but one or more documents did not upload.', 'Error');
            }

            this.toastr.success('Saved successfully.', 'Success');
            this.wasSaved = true;
            this.refreshLinkApprovalEmailStatus();
            this.loading = false;
            // Refresh the course data source to ensure display reflects current form values
            this.updateCourseDataSource();
          },
          error: (error) => {
            console.error('Error saving:', error);
            this.toastr.error('Error saving. Please try again.', 'Error');
            this.loading = false;        
          }
        });
      }
    });
  }

  // Prepare application data for saving
  private prepareApplicationData(): IAtpApplication {
    const formValue = this.atpApplicationForm.getRawValue();
    
    // Helper function to check if an ID is frontend-generated (should be null) or database-assigned (preserve)
    const shouldNullifyId = (id: any): boolean => {
      return typeof id === 'string' && (
        id.startsWith('course_') || 
        id.startsWith('tm_') || 
        id.startsWith('pi_') || 
        id.startsWith('gi_') || 
        id.startsWith('oa_') || 
        id.startsWith('ri_') || 
        id.startsWith('rc_')
      );
    };

    // Helper function to process document arrays for saving (remove _file property, keep only metadata)
    const processDocumentsForSave = (documents: any[]): any[] => {
      if (!Array.isArray(documents)) return [];
      
      return documents.map(doc => {
        if (doc && typeof doc === 'object') {
          // Remove _file property and return only document metadata
          const { _file, ...documentMetadata } = doc;
          return documentMetadata;
        }
        return doc;
      });
    };
    
    // Helper function to process arrays and handle IDs appropriately
    const processArrayForSave = (array: any[]) => {
      return array.map(item => {
        const processedItem = { ...item };
        
        // Handle main item ID - null if frontend-generated, preserve if database-assigned
        if (processedItem.id && shouldNullifyId(processedItem.id)) {
          processedItem.id = 0;
        }
        

        
        // Fix trainingCompleteDate - should be null if empty string
        if (processedItem.hasOwnProperty('trainingCompleteDate')) {
          processedItem.trainingCompleteDate = processedItem.trainingCompleteDate && processedItem.trainingCompleteDate.trim() !== '' 
            ? processedItem.trainingCompleteDate 
            : null;
        }
        
        // Fix approvalDate in otherAccreditations - should be null if empty string
        if (processedItem.hasOwnProperty('approvalDate')) {
          processedItem.approvalDate = processedItem.approvalDate && processedItem.approvalDate.trim() !== '' 
            ? processedItem.approvalDate 
            : null;
        }
        
        // Fix date in reaccredCourseTable - should be null if empty string
        if (processedItem.hasOwnProperty('date')) {
          processedItem.date = processedItem.date && processedItem.date.trim() !== '' 
            ? processedItem.date 
            : null;
        }

        // Process document arrays - remove _file property from document objects
        const documentFields = [
          'documents', 'languageDocuments', 'courseMaterialDocuments', 'courseTestDocuments',
          'courseAssessmentDocuments', 'courseQualityControlDocuments', 'courseCertificationDocuments',
          'facilityDocuments', 'principalInstructorDocuments'
        ];
        
        documentFields.forEach(field => {
          if (processedItem.hasOwnProperty(field)) {
            processedItem[field] = processDocumentsForSave(processedItem[field]);
          }
        });

        // Process nested training and construction data documents
        if (processedItem.trainingData) {
          processedItem.trainingData = processedItem.trainingData.map(nestedItem => {
            const processedNested = { ...nestedItem };
            if (processedNested.id && shouldNullifyId(processedNested.id)) {
              processedNested.id = 0;
            }
            if (processedNested.documents) {
              processedNested.documents = processDocumentsForSave(processedNested.documents);
            }
            return processedNested;
          });
        }
        
        if (processedItem.constructionData) {
          processedItem.constructionData = processedItem.constructionData.map(nestedItem => {
            const processedNested = { ...nestedItem };
            if (processedNested.id && shouldNullifyId(processedNested.id)) {
              processedNested.id = 0;
            }
            if (processedNested.documents) {
              processedNested.documents = processDocumentsForSave(processedNested.documents);
            }
            return processedNested;
          });
        }
        
        return processedItem;
      });
    };
    
    // Convert dates properly and handle null/empty values
    const rawCourses = (this.coursesFormArray.getRawValue() || []).map((course: any) => ({
      ...course,
      clientCourseId: String(course.id)
    }));
    const processedCourses = processArrayForSave(rawCourses);
    const processedFacilityLocations = processArrayForSave(this.facilityLocationsFormArray.getRawValue() || []);

    // Ignore facility rows with no meaningful address/facility data.
    const meaningfulFacilityLocations = processedFacilityLocations.filter((location: any) => {
      const hasAddressOrType = !!(
        (location?.street && String(location.street).trim() !== '') ||
        (location?.city && String(location.city).trim() !== '') ||
        (location?.state && String(location.state).trim() !== '') ||
        (location?.zip && String(location.zip).trim() !== '') ||
        (location?.facilityType && String(location.facilityType).trim() !== '')
      );
      const hasDocuments = Array.isArray(location?.facilityDocuments) && location.facilityDocuments.length > 0;
      return hasAddressOrType || hasDocuments;
    });

    const facilityByCourseId: { [courseId: string]: any } = {};
    meaningfulFacilityLocations.forEach((location: any) => {
      const selectedCourseIds = Array.isArray(location.selectedCourseIds) ? location.selectedCourseIds : [];
      selectedCourseIds.forEach((courseId: any) => {
        facilityByCourseId[String(courseId)] = location;
      });
    });

    processedCourses.forEach((course: any) => {
      const mappedFacility = facilityByCourseId[String(course.clientCourseId || course.id)];
      course.street = mappedFacility?.street || '';
      course.city = mappedFacility?.city || '';
      course.state = mappedFacility?.state || '';
      course.zip = mappedFacility?.zip || '';
      course.facilityType = mappedFacility?.facilityType || '';
      course.facilityDocuments = mappedFacility?.facilityDocuments || [];
      delete course.clientCourseId;
    });

    const applicationData = {
      ...formValue,
      // Preserve the main application ID - 0 for new applications, database ID for existing
      id: formValue.id || 0,
      userID: this.applicationUserId || this.atpApplication?.userID || '',
      // Include atpId if provided (from embedded context or form value)
      atpId: formValue.atpId || this.data?.atpId || null,
      // Include person and accreditation references
      contactPerson: formValue.contactPerson || null,
      createdAtpAccreditation: formValue.createdAtpAccreditation || null,
      renewalAtpAccreditation: formValue.renewalAtpAccreditation || null,
      ammendmentAtpAccreditation: formValue.ammendmentAtpAccreditation || null,
      // Convert date strings to proper format or null
      approvalDate: formValue.approvalDate ? new Date(formValue.approvalDate).toISOString() : null,
      applicationDate: formValue.applicationDate ? new Date(formValue.applicationDate).toISOString() : null,
      applicationViableDate: formValue.applicationViableDate ? new Date(formValue.applicationViableDate).toISOString() : null,
      submissionType: this.selectedApplicationType || formValue.submissionType || '',
      // Ensure required fields have default values
      esignature: formValue.esignature || '',
      // Process all arrays to handle IDs appropriately
      trainingManagers: processArrayForSave(this.trainingManagersFormArray.getRawValue() || []),
      primaryInstructors: processArrayForSave(this.primaryInstructorsFormArray.getRawValue() || []),
      guestInstructors: processArrayForSave(this.guestInstructorsFormArray.getRawValue() || []),
      courses: processedCourses,
      otherAccreditations: processArrayForSave(this.otherAccreditationsFormArray.getRawValue() || []),
      reaccredInstructorTable: processArrayForSave(this.reaccredInstructorFormArray.getRawValue() || []),
      reaccredCourseTable: processArrayForSave(this.reaccredCourseFormArray.getRawValue() || []),
      reaccreditationInstructorListDocuments: processDocumentsForSave(formValue.reaccreditationInstructorListDocuments || []),
      reaccreditationCourseHistoryDocuments: processDocumentsForSave(formValue.reaccreditationCourseHistoryDocuments || [])
    };
    
    return applicationData;
  }

  /**
   * Staff requirements: a standard application needs a Training Manager and a Principal Instructor.
   * A reaccreditation only needs staff when the applicant reported new staff, and then any one of a
   * Training Manager, Principal Instructor or Guest Instructor satisfies the requirement.
   */
  private collectStaffValidationErrors(): { field: string; label: string; step: string }[] {
    const errors: { field: string; label: string; step: string }[] = [];

    if (this.selectedApplicationType !== 'new' || !this.showStaffStep()) {
      return errors;
    }

    if (!this.validationSummaryRequested) {
      return errors;
    }

    if (this.isReaccreditationApplication()) {
      const staffCount = this.trainingManagersFormArray.length
        + this.primaryInstructorsFormArray.length
        + this.guestInstructorsFormArray.length;

      if (staffCount === 0) {
        errors.push({
          field: 'trainingManagers',
          label: 'Add at least one Training Manager, Principal Instructor or Guest Instructor',
          step: 'Staff'
        });
      }

      return errors;
    }

    if (this.trainingManagersFormArray.length === 0) {
      errors.push({ field: 'trainingManagers', label: 'At least one Training Manager', step: 'Staff' });
    }

    if (this.primaryInstructorsFormArray.length === 0) {
      errors.push({ field: 'primaryInstructors', label: 'At least one Principal Instructor', step: 'Staff' });
    }

    return errors;
  }

  // Helper method to mark all form fields as touched
  private markFormGroupTouched(formGroup: FormGroup): void {
    Object.keys(formGroup.controls).forEach(key => {
      const control = formGroup.get(key);
      if (control instanceof FormGroup) {
        this.markFormGroupTouched(control);
      } else if (control instanceof UntypedFormArray) {
        control.controls.forEach(arrayControl => {
          if (arrayControl instanceof FormGroup) {
            this.markFormGroupTouched(arrayControl);
          }
        });
      } else {
        control?.markAsTouched();
      }
    });
  }

  private scrollDeficiencyCardIntoView(): void {
    setTimeout(() => {
      this.deficiencyCard?.nativeElement?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    });
  }

  private hasControlValidationInteraction(control: AbstractControl | null): boolean {
    if (!control) {
      return false;
    }

    if (this.validationSummaryRequested || control.touched || control.dirty) {
      return true;
    }

    if (control instanceof FormGroup || control instanceof UntypedFormGroup) {
      return Object.keys(control.controls).some(key => this.hasControlValidationInteraction(control.get(key)));
    }

    if (control instanceof UntypedFormArray) {
      return control.controls.some(arrayControl => this.hasControlValidationInteraction(arrayControl));
    }

    return false;
  }

  private collectFacilityValidationErrors(): { field: string; label: string; step: string }[] {
    const errors: { field: string; label: string; step: string }[] = [];
    if (this.coursesFormArray.length === 0 || !this.showFacilitySection()) {
      return errors;
    }

    const shouldShowFacilityErrors = this.validationSummaryRequested ||
      this.facilityLocationsFormArray.controls.some(control => this.hasControlValidationInteraction(control));

    if (!shouldShowFacilityErrors) {
      return errors;
    }

    if (this.facilityLocationsFormArray.length === 0) {
      errors.push({
        field: 'facilityLocations',
        label: 'Add at least one facility location',
        step: 'Course Details - Facility Information'
      });
      return errors;
    }

    this.facilityLocationsFormArray.controls.forEach((control, index) => {
      const facilityGroup = control as UntypedFormGroup;
      const facilityLabel = `Facility Location ${index + 1}`;
      const fieldConfig = [
        { key: 'selectedCourseIds', label: 'Assigned Courses' },
        { key: 'street', label: 'Street Address' },
        { key: 'city', label: 'City' },
        { key: 'state', label: 'State' },
        { key: 'zip', label: 'ZIP Code' },
        { key: 'facilityType', label: 'Facility Type' },
        { key: 'facilityDocuments', label: 'Facility Documents' }
      ];

      fieldConfig.forEach(field => {
        const facilityField = facilityGroup.get(field.key);
        if (facilityField?.invalid && this.hasControlValidationInteraction(facilityField)) {
          errors.push({
            field: `facility${index}${field.key}`,
            label: `${facilityLabel} - ${field.label}`,
            step: 'Course Details - Facility Information'
          });
        }
      });
    });

    const allCourseIds = this.getAllCourseIds();
    const assignmentCounts: { [courseId: string]: number } = {};

    this.facilityLocationsFormArray.controls.forEach(control => {
      const selectedCourseIds = (control.get('selectedCourseIds')?.value || []).map((id: any) => String(id));
      selectedCourseIds.forEach((courseId: string) => {
        assignmentCounts[courseId] = (assignmentCounts[courseId] || 0) + 1;
      });
    });

    const duplicateAssignments = Object.keys(assignmentCounts).filter(courseId => assignmentCounts[courseId] > 1);
    if (duplicateAssignments.length > 0) {
      errors.push({
        field: 'facilityDuplicateAssignments',
        label: 'Each course can only be assigned to one facility location',
        step: 'Course Details - Facility Information'
      });
    }

    const unassignedCourses = allCourseIds.filter(courseId => !assignmentCounts[courseId]);
    if (unassignedCourses.length > 0) {
      errors.push({
        field: 'facilityUnassignedCourses',
        label: 'Every course must be assigned to a facility location',
        step: 'Course Details - Facility Information'
      });
    }

    return errors;
  }

  showNextButton(): boolean {
    // Don't show next button if no application type is selected
    if (!this.selectedApplicationType) {
      return false;
    }
    
    const applicationStatus = this.atpApplicationForm.get('applicationStatus')?.value;
    const currentStep = this.step;
    
    // If stepper is not available yet, use fallback logic based on application type and step
    if (!this.atpStepper || !this.atpStepper.steps) {
      // For Link applications, we know the structure: step 0 and 1 should show next button
      if (this.selectedApplicationType === 'link') {
        if (applicationStatus === APPLICATION_STATUS.submitted) {
          return currentStep < 2; // Steps 0,1 show next, step 2 (confirmation) doesn't
        } else {
          return currentStep < 1; // Step 0 shows next, step 1 (final before submit) doesn't
        }
      }
      
      // For New applications, use dynamic calculation
      if (this.selectedApplicationType === 'new') {
        const lastStepIndex = this.getLastStepIndex();
        if (applicationStatus === APPLICATION_STATUS.submitted) {
          return currentStep < lastStepIndex; // All steps before confirmation show next
        } else {
          return currentStep < lastStepIndex - 1; // All steps before final (submit) show next
        }
      }
      
      // Default fallback - don't show if stepper not available
      return false;
    }
    
    // Normal logic when stepper is available
    const totalSteps = this.atpStepper.steps.length;
    const isLastStep = currentStep >= totalSteps - 1;
    
    // For submitted applications, if we're on the last step (confirmation), don't show next button
    if (applicationStatus === APPLICATION_STATUS.submitted && isLastStep) {
      return false;
    }
    
    // For non-submitted applications, don't show next button only on the actual last step (where submit button shows)
    if (applicationStatus !== APPLICATION_STATUS.submitted && isLastStep) {
      return false;
    }
    
    // Show next button if there's a next step available
    return !isLastStep;
  }

  submitApplication(): void {
    const submissionType = this.selectedApplicationType || this.atpApplicationForm.get('submissionType')?.value;

    if (submissionType === 'link') {
      this.linkUserDisplayControl.markAsTouched();
      this.linkAtpIdDisplayControl.markAsTouched();
      this.atpApplicationForm.get('atpName')?.markAsTouched();
      this.atpApplicationForm.get('atpId')?.markAsTouched();
      this.atpApplicationForm.get('attestation')?.markAsTouched();
      this.atpApplicationForm.get('esignature')?.markAsTouched();
    } else {
      this.markFormGroupTouched(this.atpApplicationForm);
    }

    this.validationSummaryRequested = true;
    const validationErrors = this.getStepValidationErrors();

    if (validationErrors.length > 0) {
      this.scrollDeficiencyCardIntoView();
      this.toastr.error(
        `Please complete all required fields. ${validationErrors.length} field(s) need attention.`,
        'Form Validation Error',
        { timeOut: 5000 }
      );
      return;
    }

    const dialogRef = this.dialog.open(ConfirmationDialogueComponent, {
      width: '400px',
      data: {
        title: 'Submit Application?',
        message: 'Are you sure you want to submit this application?'
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result === true) {
        this.loading = true;
        
        // Only set application date if it's currently empty or null
        const currentApplicationDate = this.atpApplicationForm.get('applicationDate')?.value;
        const patchValues: any = {
          applicationStatus: APPLICATION_STATUS.submitted
        };
        
        // Set application date to today's date only if not already set
        if (!currentApplicationDate || currentApplicationDate === '') {
          patchValues.applicationDate = new Date().toISOString().split('T')[0]; // Today's date in YYYY-MM-DD format
        }
        
        // If submissionType is 'link', set programType and applicationType to 'link'
        if (this.selectedApplicationType === 'link') {
          patchValues.programType = 'link';
          patchValues.applicationType = 'link';
        }
        
        this.atpApplicationForm.patchValue(patchValues);
        
        // Prepare the application data
        const applicationData = this.prepareApplicationData();    
        this.atpService.saveAtpApplication(applicationData).subscribe({
          next: (response) => {        
            this.toastr.success('Application submitted successfully.', 'Success');
            this.wasSaved = true;
            this.loading = false;
            
            // Navigate to confirmation step after successful submission
            this.navigateToConfirmationStep();
          },
          error: (error) => {
            console.error('Error submitting ATP Application:', error);
            this.toastr.error('Error submitting application. Please try again.', 'Error');
            this.loading = false;        
          }
        });
      }
    });
  }

  private validateFacilityAssignments(requireCompleteAssignments: boolean = true): boolean {
    if (this.coursesFormArray.length === 0 || !this.showFacilitySection()) {
      return true;
    }

    if (this.coursesFormArray.length > 0 && this.facilityLocationsFormArray.length === 0) {
      if (!requireCompleteAssignments) {
        return true;
      }
      this.toastr.error('Add at least one facility location before saving.', 'Facility Information Required');
      return false;
    }

    if (!requireCompleteAssignments) {
      return true;
    }

    this.facilityLocationsFormArray.controls.forEach(control => {
      control.markAllAsTouched();
      control.updateValueAndValidity({ emitEvent: false });
    });

    if (this.facilityLocationsFormArray.invalid) {
      this.toastr.error('Complete all required facility fields and course assignments.', 'Facility Information Required');
      return false;
    }

    const allCourseIds = this.getAllCourseIds();
    const assignmentCounts: { [courseId: string]: number } = {};

    this.facilityLocationsFormArray.controls.forEach(control => {
      const selectedCourseIds = (control.get('selectedCourseIds')?.value || []).map((id: any) => String(id));
      selectedCourseIds.forEach((courseId: string) => {
        assignmentCounts[courseId] = (assignmentCounts[courseId] || 0) + 1;
      });
    });

    const duplicateAssignments = Object.keys(assignmentCounts).filter(courseId => assignmentCounts[courseId] > 1);
    if (duplicateAssignments.length > 0) {
      this.toastr.error('Each course can only be assigned to one facility location.', 'Facility Assignment Conflict');
      return false;
    }

    const unassignedCourses = allCourseIds.filter(courseId => !assignmentCounts[courseId]);
    if (unassignedCourses.length > 0) {
      this.toastr.error('Every course must be assigned to a facility location.', 'Facility Assignment Required');
      return false;
    }

    return true;
  }

  approveAtpApplication(): void {
    const submissionType = this.selectedApplicationType || this.atpApplicationForm.get('submissionType')?.value;
    const applicationType = this.atpApplicationForm.get('applicationType')?.value;
    
    // Mark relevant fields as touched BEFORE checking validation
    if (submissionType === 'link') {
      // For link submissions, only mark the 4 required fields as touched
      this.linkUserDisplayControl.markAsTouched();
      this.linkAtpIdDisplayControl.markAsTouched();
      this.atpApplicationForm.get('atpName')?.markAsTouched();
      this.atpApplicationForm.get('atpId')?.markAsTouched();
      this.atpApplicationForm.get('attestation')?.markAsTouched();
      this.atpApplicationForm.get('esignature')?.markAsTouched();
    } else {
      // For non-link submissions, mark all fields as touched
      this.markFormGroupTouched(this.atpApplicationForm);
    }
    
    // Check validation for both link and non-link submission types
    // getStepValidationErrors() now handles link-specific validation internally
    const validationErrors = this.getStepValidationErrors();
    
    if (validationErrors.length > 0) {
      // Show toastr error with field count
      this.toastr.error(
        `Please complete all required fields. ${validationErrors.length} field(s) need attention.`,
        'Form Validation Error',
        { timeOut: 5000 }
      );
      return;
    }

    const programType: string = this.atpApplicationForm.get('programType')?.value;

    const continueApprovalFlow = (): void => {
      // Set dates to today if they're null or empty
      const today = new Date().toISOString().split('T')[0]; // Today's date in YYYY-MM-DD format

      const applicationDate = this.atpApplicationForm.get('applicationDate')?.value;
      const applicationViableDate = this.atpApplicationForm.get('applicationViableDate')?.value;
      const approvalDate = this.atpApplicationForm.get('approvalDate')?.value;

      const patchValues: any = {};

      if (!applicationDate || applicationDate === '') {
        patchValues.applicationDate = today;
      }

      if (!applicationViableDate || applicationViableDate === '') {
        patchValues.applicationViableDate = today;
      }

      if (!approvalDate || approvalDate === '') {
        patchValues.approvalDate = today;
      }

      // Apply the patch if any dates need to be set
      if (Object.keys(patchValues).length > 0) {
        this.atpApplicationForm.patchValue(patchValues);
      }

      // Get the final date values (either from form or newly set)
      const finalApplicationDate = patchValues.applicationDate || applicationDate;
      const finalApplicationViableDate = patchValues.applicationViableDate || applicationViableDate;
      const finalApprovalDate = patchValues.approvalDate || approvalDate;

      const dialogRef = this.dialog.open(AtpApplicationApprovalDialogComponent, {
        width: '500px',
        data: {
          title: submissionType === 'link' ? 'Approve Link Request?' : 'Approve Application?',
          submissionType,
          applicationDate: finalApplicationDate,
          applicationViableDate: finalApplicationViableDate,
          approvalDate: finalApprovalDate,
          atpOrId: this.atpApplicationForm.get('atpOrId')?.value
        }
      });

      dialogRef.afterClosed().subscribe((result: false | AtpApplicationApprovalDialogResult) => {
        if (result && result.confirmed) {
          this.atpApplicationForm.patchValue({
            applicationDate: result.applicationDate,
            applicationViableDate: result.applicationViableDate,
            approvalDate: result.approvalDate,
            atpOrId: result.atpOrId
          });

          this.loading = true;

          // Set application status to approved after user confirms
          this.atpApplicationForm.patchValue({
            applicationStatus: APPLICATION_STATUS.approved
          });

          // Prepare the application data
          const applicationData = this.prepareApplicationData();

          this.atpService.approveAtpApplication(applicationData).subscribe({
            next: (response) => {
              this.toastr.success('Approved successfully.', 'Success');
              this.wasSaved = true;
              this.loading = false;

              // Disable admin fields after approval
              this.atpApplicationForm.get('applicationStatus')?.disable();
              this.atpApplicationForm.get('applicationDate')?.disable();
              this.atpApplicationForm.get('applicationViableDate')?.disable();
              this.atpApplicationForm.get('approvalDate')?.disable();
              this.atpApplicationForm.get('applicationFeedback')?.disable();

              // Optionally reload or update the form
              if (response && response.id) {
                this.atpApplicationForm.patchValue({ id: response.id });
              }
            },
            error: (error) => {
              console.error('Error approving:', error);
              this.toastr.error('Error approving. Please try again.', 'Error');
              this.loading = false;
            }
          });
        }
      });
    };

    const currentAtpOrIdRaw: string = this.atpApplicationForm.get('atpOrId')?.value;
    const currentAtpOrId = typeof currentAtpOrIdRaw === 'string' ? currentAtpOrIdRaw.trim() : '';

    // If atpOrId exists, normalize 41A/41R -> 41AR when programType requires both.
    if (currentAtpOrId && this.atpApplicationForm.get('atpId')?.value !== null) {
      const upper = currentAtpOrId.toUpperCase();

      // Keep as-is if already 41AR or if it doesn't start with 41A/41R.
      if (upper.startsWith('41A') && !upper.startsWith('41AR') && programType === 'rrp') {
        this.atpApplicationForm.patchValue({ atpOrId: '41AR' + currentAtpOrId.substring(3) });
      } else if (upper.startsWith('41R') && !upper.startsWith('41AR') && programType === 'lbpa') {
        this.atpApplicationForm.patchValue({ atpOrId: '41AR' + currentAtpOrId.substring(3) });
      }

      continueApprovalFlow();
      return;
    }

    // If atpOrId is null/empty, generate it based on programType.
    const generateType = programType === 'lbpa' ? 'A' : programType === 'rrp' ? 'R' : null;
    if (!generateType) {
      continueApprovalFlow();
      return;
    }

    this.atpService.generateAtpORId(generateType).subscribe({
      next: (generated) => {
        this.atpApplicationForm.patchValue({ atpOrId: generated.atporId });
        continueApprovalFlow();
      },
      error: (error) => {
        console.error('Error generating ATP OR ID:', error);
        this.toastr.error('Error generating suggested ATP OR ID.', 'Error');
      }
    });
  }

  // Get validation errors for the current step
  getStepValidationErrors(): { field: string; label: string; step: string }[] {
    const errors: { field: string; label: string; step: string }[] = [];
    
    if (!this.atpApplicationForm) {
      return errors;
    }

    const submissionType = this.selectedApplicationType || this.atpApplicationForm.get('submissionType')?.value;
    const applicationType = this.atpApplicationForm.get('applicationType')?.value;
    
    // For 'link' submission type, only validate the 4 required fields
    if (submissionType === 'link') {
      const userIdControl = this.linkUserDisplayControl;
      const atpLinkIdControl = this.linkAtpIdDisplayControl;
      const atpNameControl = this.atpApplicationForm.get('atpName');
      const atpIdControl = this.atpApplicationForm.get('atpId');
      const attestationControl = this.atpApplicationForm.get('attestation');
      const esignatureControl = this.atpApplicationForm.get('esignature');
      
      // Only check for errors if at least one field has been touched or is dirty
      const anyFieldTouched = (userIdControl?.touched || userIdControl?.dirty) ||
                              (atpLinkIdControl?.touched || atpLinkIdControl?.dirty) ||
                              (atpNameControl?.touched || atpNameControl?.dirty) ||
                              (atpIdControl?.touched || atpIdControl?.dirty) ||
                              (attestationControl?.touched || attestationControl?.dirty) ||
                              (esignatureControl?.touched || esignatureControl?.dirty);
      
      if (!anyFieldTouched) {
        return errors; // Return empty array if no fields have been touched yet
      }
      
      // Check each field only if it has been touched or is dirty
      if (!this.applicationUserId && (userIdControl?.touched || userIdControl?.dirty)) {
        errors.push({ field: 'userID', label: 'User ID', step: 'Admin' });
      }
      if (!atpNameControl?.value && (atpNameControl?.touched || atpNameControl?.dirty)) {
        errors.push({ field: 'atpName', label: 'ATP Name', step: 'Admin' });
      }
      if (!atpIdControl?.value && (atpIdControl?.touched || atpIdControl?.dirty)) {
        errors.push({ field: 'atpId', label: 'ATP ID', step: 'Admin' });
      }
      if (!attestationControl?.value && (attestationControl?.touched || attestationControl?.dirty)) {
        errors.push({ field: 'attestation', label: 'Attestation', step: 'Admin' });
      }
      if (!esignatureControl?.value && (esignatureControl?.touched || esignatureControl?.dirty)) {
        errors.push({ field: 'esignature', label: 'Electronic Signature', step: 'Admin' });
      }
      
      return errors;
    }

    // Define field labels with step information for non-link submission types
    const fieldConfig: { [key: string]: { label: string; step: string } } = {
      // Step 1: App Info
      'applicationType': { label: 'Application Type', step: 'App Info' },
      'programType': { label: 'Program Type', step: 'App Info' },
      'ammendmentType': { label: 'Amendment Type', step: 'App Info' },
      'ammendmentAtpAccreditation': { label: 'Accreditation or Approved Application to Amend', step: 'App Info' },
      'renewalAtpAccreditation': { label: 'Accreditation to Reaccredit', step: 'App Info' },
      'other': { label: 'Other Details', step: 'App Info' },
      
      // Step 2: Organization Info
      'atpName': { label: 'Company/Organization Name', step: 'Contact Info' },
      'atpPhysicalStreet': { label: 'Physical Street Address', step: 'Contact Info' },
      'atpPhysicalCity': { label: 'Physical City', step: 'Contact Info' },
      'atpPhysicalState': { label: 'Physical State', step: 'Contact Info' },
      'atpPhysicalZip': { label: 'Physical ZIP Code', step: 'Contact Info' },
      'atpMailingStreet': { label: 'Mailing Street Address', step: 'Contact Info' },
      'atpMailingCity': { label: 'Mailing City', step: 'Contact Info' },
      'atpMailingState': { label: 'Mailing State', step: 'Contact Info' },
      'atpMailingZip': { label: 'Mailing ZIP Code', step: 'Contact Info' },
      
      // Step 3: Courses
      'courses': { label: 'At least one course is required', step: 'Courses' },
      'facilityLocations': { label: 'Facility Locations', step: 'Course Details' },
      
      // Step 4: Contact Info
      'atpContactFirstName': { label: 'Contact First Name', step: 'Contact Info' },
      'atpContactLastName': { label: 'Contact Last Name', step: 'Contact Info' },
      'atpPhone': { label: 'Phone Number', step: 'Contact Info' },
      'atpEmail': { label: 'Email Address', step: 'Contact Info' },
      'atpConfirmEmail': { label: 'Confirm Email Address', step: 'Contact Info' },
      
      // Step 5: Staff
      'trainingManagers': { label: 'At least one Training Manager', step: 'Staff' },
      'primaryInstructors': { label: 'At least one Primary Instructor', step: 'Staff' },
      
      // Step 6: Background
      'violationDescription': { label: 'Violation Description', step: 'Background' },

      // ReAccredit
      'reaccreditationChangesSinceLastApproval': { label: 'Changes Since Last Approval', step: 'ReAccredit' },
      'reaccreditationInstructorListDocuments': { label: 'List of all current instructors document', step: 'ReAccredit' },
      'reaccreditationCourseHistoryDocuments': { label: 'List of last 25 courses held document', step: 'ReAccredit' },
      'reaccreditationMaterialsOrLocationChanged': { label: 'Select Training Materials and/or Training Location', step: 'ReAccredit' },

      // Step 8: Overview (Attestation)
      'attestation': { label: 'Attestation checkbox must be checked', step: 'Overview' },
      'esignature': { label: 'Electronic Signature', step: 'Overview' }
    };

    const reaccreditationOnlyKeys = [
      'reaccreditationChangesSinceLastApproval',
      'reaccreditationInstructorListDocuments',
      'reaccreditationCourseHistoryDocuments',
      'reaccreditationHasChanges',
      'reaccreditationMaterialsOrLocationChanged',
      'reaccreditationTrainingMaterialsChanged',
      'reaccreditationTrainingLocationChanged',
      'reaccreditationHasNewStaff'
    ];

    // Check all form controls
    Object.keys(this.atpApplicationForm.controls).forEach(key => {
      const control = this.atpApplicationForm.get(key);

      // Reaccreditation instructor/course tables are hidden, so they should never block submission.
      if (['reaccredInstructorTable', 'reaccredCourseTable'].includes(key)) {
        return;
      }

      // Only validate the ReAccredit step controls for reaccreditation applications.
      if (applicationType !== 'reaccreditation' && reaccreditationOnlyKeys.includes(key)) {
        return;
      }

      if (applicationType !== 'ammendment' && key === 'ammendmentAtpAccreditation') {
        return;
      }

      if (applicationType !== 'reaccreditation' && key === 'renewalAtpAccreditation') {
        return;
      }
      
      if (control && control.invalid && this.hasControlValidationInteraction(control)) {
        const config = fieldConfig[key] || { label: this.formatFieldName(key), step: 'ReAccredit' };
        errors.push({ field: key, label: config.label, step: config.step });
      }
    });

    errors.push(...this.collectStaffValidationErrors());
    
    // Check email matching
    const email = this.atpApplicationForm.get('atpEmail')?.value;
    const confirmEmail = this.atpApplicationForm.get('atpConfirmEmail')?.value;
    const confirmEmailControl = this.atpApplicationForm.get('atpConfirmEmail');
    if (email && confirmEmail && email !== confirmEmail && (confirmEmailControl?.touched || confirmEmailControl?.dirty)) {
      errors.push({ field: 'atpConfirmEmail', label: 'Email addresses must match', step: 'Contact Info' });
    }

    // Check array fields
    if (this.coursesFormArray.length === 0 && this.selectedApplicationType === 'new') {
      errors.push({ field: 'courses', label: 'At least one course is required', step: 'Courses' });
    }

    // Check each course for validation errors with specific section feedback
    this.coursesFormArray.controls.forEach((courseControl, index) => {
      const courseGroup = courseControl as UntypedFormGroup;
      const courseNumber = index + 1;
      
      // Get course discipline for better identification
      const discipline = courseGroup.get('discipline')?.value || 'Course ' + courseNumber;
      
      // Check Courses step fields (only if touched or dirty)
      const disciplineField = courseGroup.get('discipline');
      if (disciplineField?.invalid && (disciplineField.touched || disciplineField.dirty)) {
        errors.push({ field: `course${index}discipline`, label: `${discipline} - Discipline`, step: 'Courses' });
      }
      
      // Check Course Details step fields (Course Training Materials) - only if touched or dirty
      const materialTypeField = courseGroup.get('courseMaterialType');
      if (materialTypeField?.invalid && (materialTypeField.touched || materialTypeField.dirty)) {
        errors.push({ field: `course${index}materialType`, label: `${discipline} - Material Type`, step: 'Course Details - Course Training Materials' });
      }
      
      const materialDocsField = courseGroup.get('courseMaterialDocuments');
      if (materialDocsField?.invalid && (materialDocsField.touched || materialDocsField.dirty)) {
        errors.push({ field: `course${index}materialDocs`, label: `${discipline} - Material Documents`, step: 'Course Details - Course Training Materials' });
      }
      
      const testField = courseGroup.get('courseTest');
      if (testField?.invalid && (testField.touched || testField.dirty)) {
        errors.push({ field: `course${index}testType`, label: `${discipline} - Test Type`, step: 'Course Details - Course Training Materials' });
      }
      
      const testDocsField = courseGroup.get('courseTestDocuments');
      if (testDocsField?.invalid && (testDocsField.touched || testDocsField.dirty)) {
        errors.push({ field: `course${index}testDocs`, label: `${discipline} - Test Documents`, step: 'Course Details - Course Training Materials' });
      }
      
      const assessmentDocsField = courseGroup.get('courseAssessmentDocuments');
      if (assessmentDocsField?.invalid && (assessmentDocsField.touched || assessmentDocsField.dirty)) {
        errors.push({ field: `course${index}assessmentDocs`, label: `${discipline} - Assessment Documents`, step: 'Course Details - Course Training Materials' });
      }
      
      const qualityDocsField = courseGroup.get('courseQualityControlDocuments');
      if (qualityDocsField?.invalid && (qualityDocsField.touched || qualityDocsField.dirty)) {
        errors.push({ field: `course${index}qualityDocs`, label: `${discipline} - Quality Control Documents`, step: 'Course Details - Course Training Materials' });
      }
      
      const certDocsField = courseGroup.get('courseCertificationDocuments');
      if (certDocsField?.invalid && (certDocsField.touched || certDocsField.dirty)) {
        errors.push({ field: `course${index}certDocs`, label: `${discipline} - Certification Documents`, step: 'Course Details - Course Training Materials' });
      }
      
      // Check Course Additional Documents fields - conditional based on language and format
      const additionalDocumentsVisible = this.showCourseMaterialsSection();
      const language = courseGroup.get('language')?.value;
      const format = courseGroup.get('format')?.value;
      
      // Language Documents required if language is not English
      if (additionalDocumentsVisible && language !== 'english') {
        const languageDocsField = courseGroup.get('languageDocuments');
        const languageDocs = languageDocsField?.value || [];
        if (languageDocs.length === 0 && (languageDocsField?.touched || languageDocsField?.dirty)) {
          errors.push({ field: `course${index}languageDocs`, label: `${discipline} - Language Documents`, step: 'Course Details - Course Additional Documents' });
        }
      }
      
      // Course Website and Website Instructions required if format is online
      if (additionalDocumentsVisible && format === 'online') {
        const websiteField = courseGroup.get('courseWebsite');
        if ((!websiteField?.value || websiteField.value.trim() === '') && (websiteField?.touched || websiteField?.dirty)) {
          errors.push({ field: `course${index}website`, label: `${discipline} - Course Website`, step: 'Course Details - Course Additional Documents' });
        }
        
        const websiteInstructionsField = courseGroup.get('websiteAccessDescription');
        if ((!websiteInstructionsField?.value || websiteInstructionsField.value.trim() === '') && (websiteInstructionsField?.touched || websiteInstructionsField?.dirty)) {
          errors.push({ field: `course${index}websiteInstructions`, label: `${discipline} - Website Instructions`, step: 'Course Details - Course Additional Documents' });
        }
      }
    });

    errors.push(...this.collectFacilityValidationErrors());

    // Check otherAccreditations array - only if otherJurisdictionAccreditation is true
    if (this.atpApplicationForm.get('otherJurisdictionAccreditation')?.value === true) {
      this.otherAccreditationsFormArray.controls.forEach((accreditationControl, index) => {
        const accreditationGroup = accreditationControl as UntypedFormGroup;
        const accreditationNumber = index + 1;
        
        // Get jurisdiction for better identification
        const jurisdiction = accreditationGroup.get('jurisdiction')?.value || `Other Accreditation ${accreditationNumber}`;
        
        const jurisdictionField = accreditationGroup.get('jurisdiction');
        if (jurisdictionField?.invalid && (jurisdictionField.touched || jurisdictionField.dirty)) {
          errors.push({ field: `otherAccred${index}jurisdiction`, label: `${jurisdiction} - Jurisdiction`, step: 'Background' });
        }
        
        const accredNumField = accreditationGroup.get('accreditationNumber');
        if (accredNumField?.invalid && (accredNumField.touched || accredNumField.dirty)) {
          errors.push({ field: `otherAccred${index}accredNumber`, label: `${jurisdiction} - Accreditation Number`, step: 'Background' });
        }
        
        const courseDisciplineField = accreditationGroup.get('courseDiscipline');
        if (courseDisciplineField?.invalid && (courseDisciplineField.touched || courseDisciplineField.dirty)) {
          errors.push({ field: `otherAccred${index}discipline`, label: `${jurisdiction} - Course Discipline`, step: 'Background' });
        }
        
        const courseTypeField = accreditationGroup.get('courseType');
        if (courseTypeField?.invalid && (courseTypeField.touched || courseTypeField.dirty)) {
          errors.push({ field: `otherAccred${index}courseType`, label: `${jurisdiction} - Course Type`, step: 'Background' });
        }
        
        const approvalDateField = accreditationGroup.get('approvalDate');
        if (approvalDateField?.invalid && (approvalDateField.touched || approvalDateField.dirty)) {
          errors.push({ field: `otherAccred${index}approvalDate`, label: `${jurisdiction} - Approval Date`, step: 'Background' });
        }
        
        const approvingAuthorityField = accreditationGroup.get('approvingAuthority');
        if (approvingAuthorityField?.invalid && (approvingAuthorityField.touched || approvingAuthorityField.dirty)) {
          errors.push({ field: `otherAccred${index}authority`, label: `${jurisdiction} - Approving Authority`, step: 'Background' });
        }
        
        const documentsField = accreditationGroup.get('documents');
        if (documentsField?.invalid && (documentsField.touched || documentsField.dirty)) {
          errors.push({ field: `otherAccred${index}documents`, label: `${jurisdiction} - Documents`, step: 'Background' });
        }
      });
    }

    return errors;
  }

  // Format field name for display (camelCase to Title Case)
  private formatFieldName(fieldName: string): string {
    return fieldName
      .replace(/([A-Z])/g, ' $1')
      .replace(/^./, str => str.toUpperCase())
      .trim();
  }

  // Check if there are any validation errors
  hasValidationErrors(): boolean {
    return this.getStepValidationErrors().length > 0;
  }

  private formatLinkFeedbackValue(value: any, emptyValue: string = 'Not Provided'): string {
    const normalized = (value ?? '').toString().trim();
    return normalized.length > 0 ? normalized : emptyValue;
  }

  // Combine admin feedback and validation errors into a single string
  getCombinedFeedback(): string {
    let combined = '';
    
    // Add admin feedback first
    const adminFeedback = this.atpApplicationForm?.get('applicationFeedback')?.value;
    if (adminFeedback) {
      combined += adminFeedback;
    }

    if (this.selectedApplicationType === 'link') {
      const linkDetails = [
        `• User ID - ${this.formatLinkFeedbackValue(this.applicationUserId, 'Not available')}`,
        `• ATP ID - ${this.formatLinkFeedbackValue(this.atpApplicationForm?.get('atpId')?.value, 'Not linked')}`,
        `• ATP Name - ${this.formatLinkFeedbackValue(this.atpApplicationForm?.get('atpName')?.value, 'Not linked')}`,
        `• Attestation - ${this.atpApplicationForm?.get('attestation')?.value === true ? 'Yes' : 'No'}`,
        `• ESignature - ${this.formatLinkFeedbackValue(this.atpApplicationForm?.get('esignature')?.value)}`,
      ];

      if (combined) {
        combined += '\n\n';
      }

      combined += '--- Link Request Details ---\n' + linkDetails.join('\n');
    }
    
    // Add validation errors grouped by step
    const errors = this.getStepValidationErrors();
    if (errors.length > 0) {
      if (combined) {
        combined += '\n\n'; // Add spacing if there's admin feedback
      }
      combined += '--- Missing Required Fields ---\n';
      
      // Define the step order to match mat-stepper order
      const stepOrder = [
        'Admin',  // For link submission type
        'App Info',        
        'Courses',
        'ReAccredit',
        'Course Details - Course Training Materials',
        'Course Details - Facility Information',
        'Course Details - Course Additional Documents',
        'Contact Info',
        'Staff',
        'Background',
        'Overview'
      ];
      
      // Group errors by step
      const errorsByStep = new Map<string, string[]>();
      errors.forEach(error => {
        const step = error.step || 'General';
        if (!errorsByStep.has(step)) {
          errorsByStep.set(step, []);
        }
        errorsByStep.get(step)!.push(error.label);
      });
      
      // Sort and format each step's errors in the defined order
      stepOrder.forEach(step => {
        if (errorsByStep.has(step)) {
          const labels = errorsByStep.get(step)!;
          combined += `• ${step} - ${labels.join(', ')}\n`;
        }
      });
      
      // Add any remaining steps not in the defined order (fallback)
      errorsByStep.forEach((labels, step) => {
        if (!stepOrder.includes(step)) {
          combined += `• ${step} - ${labels.join(', ')}\n`;
        }
      });
    }
    
    return combined;
  }
}